import { TRANSCRIPT_OPTION } from './export/exportText';
import { buildTranscriptText } from './export/transcriptText';
import { buildExportClips, isSyntheticId } from './export/exportClips';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MovieIcon from "@mui/icons-material/Movie";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useTheme } from "@mui/material/styles";
import { reaction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TableVirtuoso } from "react-virtuoso";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { viewItems } from "../../components/constants";
import { filterContext } from "../../stores/filters/filterContext";
import { messageContext } from "../../stores/messages/messageContext";
import { userContext } from "../../stores/users/userContext";
import {
  CURRENT_VIEW_CONFIG,
  DRAG_TOOLBAR_HEIGHT,
  getUsageLimitsUrl,
  makeVideoUrl,
  shareMakeVideoUrl,
  creationStatusSharedUrl,
  creationDownloadSharedUrl,
  mapViewToModel,
  maxLines,
  optionToKind,
  clipDirectDownloadUrl,
  edlExportUrl,
  createShareUrl,
} from "../constants";
import { canShareFiles, shareVideoFile, shareLink, clipShareText, clipFileName } from "./socialShare";
import { useLabs, LABS, setLab, resetLabs, labsOnCount } from "./etLabs";
import { ProgressCard, ShareBox, ShareIcon } from "./ShareFlow";
import { targetByKey, targetAction } from "./shareTargets";
import { getCookie } from "../../utils/cookie";
import { getEmbedParentOrigin, getEmbedPostTarget, getRemoteDriverToken, isTrustedEmbedCommand, postToEmbedParent } from "../../utils/embedBridge";
import apiRequest from "../../utils/apiClient";
import { handleLimitError, formatDurationShort } from "../../utils/limitErrorHandler";
import Header from "../header/header";
import { useEtBreakpoints, setEtViewMode, setEtModeIndex, useEtViewMode, useEtMode, ET_MODES } from "../responsive/useEtView";
import EtViewToggle from "../responsive/EtViewToggle";
import {
  AIBHeader,
  autoHighlightHeader,
  categoriesHeader,
  contentModerationHeader,
  entitiesHeader,
  sentencesHeader,
} from "../header/headerConstants";
import { ItemContent } from "../Items/ItemContent";
import Loading from "../loading/loading";
import ChartsGrid from "../views/ChartsGrid";
import CherryPickGrid from "../views/CherryPickGrid";
import CherryClassicGrid from "../views/CherryClassicGrid";
import MosaicView from "../views/MosaicView";
import ChessboardView from "../views/ChessboardView";
import CubeView from "../views/CubeView";
import SuperTranscriptView from "./SuperTranscriptView";
import { regroupSentences, regroupByInterval, splitIntoWords } from "./regroupSentences";
import { getContentStyle } from "../views/getContentStyle";
import DraggableToolbar, { AI_GOAL_CHIPS } from "./draggableToolbar";
import EtTimelineDock from "../timeline/EtTimelineDock";
// ARCHIVE mode (several uploads as ONE sheet) — pure, unit-tested helpers. Design:
// docs/superpowers/specs/2026-09-06-archive-et-design.md. Every archive branch below is gated
// on the `archive` prop; without it the ET behaves exactly as before.
import { parseSheetId, sameSource } from "./multiSheet";
import { applySpeakerIdentities } from "./speakerIdentity";
import { buildArchiveSegments, pickToArchiveSegment, archiveExportTitle, ARCHIVE_UNSUPPORTED_EXPORTS } from "./archiveExport";
import { buildArchiveClips, archivePreambleLines, archiveExampleIds } from "./archiveAiPack";
import ccLogoRed from "../../assets/brand/logo_site.svg";
import ccLogoPurple from "../../assets/brand/logo_site_purple.svg";
import { reGex } from "./regex";
import CreateVideoLogo from "./CreateVideoLogo";
import { createAiLinkPoller, AI_LINK_STATUS } from "../../utils/aiLink";
import { planAiPicks, splitRowIntoPieces, toHMS } from "../../utils/aiPieces";
import {
  getSentencesStreamUrl,
  clipThumbnailsUrl,
  livePicksSetUrl,
  livePicksArmUrl,
  livePicksGetUrl,
  CREATE_VIDEO,
  CHART,
  CHERRY_PICK,
  urlViews,
} from "../constants";
import { useNavigate } from "react-router-dom";
const INTERVAL = 75;

// Audio uploads produce a transcript ET with NO per-clip video posters (ever). Used to skip
// the background-thumbnail poll for audio ETs (mirrors the worker's AUDIO_FORMATS).
const AUDIO_ET_EXTS = new Set(['mp3', 'm4a', 'wav', 'aac', 'flac', 'ogg', 'oga', 'aiff', 'aif', 'wma']);

// Context used to pass the rows ref into the SortableTableRow component
// (TableVirtuoso's TableRow override only receives data-index, not the row id)
const RowsRefContext = React.createContext(null);

const SortableTableRow = React.forwardRef(function SortableTableRow(
  { "data-index": dataIndex, style: rowStyle, className: rowClassName, ...props },
  _ref,
) {
  const rowsRef = useContext(RowsRefContext);
  const id = rowsRef?.current?.[dataIndex]?.id ?? `row-${dataIndex}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <tr
      ref={setNodeRef}
      data-index={dataIndex}
      {...props}
      {...attributes}
      {...listeners}
      style={{
        // Must preserve TableVirtuoso's injected height/positioning styles
        ...rowStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: isDragging ? "grabbing" : "default",
        ...(isDragging && { zIndex: 9999, position: "relative" }),
      }}
      className={`${rowClassName || ""} responsive-table-row`}
    />
  );
});

const CreateVideo = ({ archive = null } = {}) => {
  const userStore = useContext(userContext);
  const filterStore = useContext(filterContext);
  const messageStore = useContext(messageContext);
  // ARCHIVE: several uploads open as ONE sheet (ArchiveView hands in the merged, source-tagged
  // rows). There is no single transcript, so transcriptId is null — every per-transcript
  // network effect no-ops through its existing `transcriptId == null` guard — and per-sheet
  // persistence (translations, virality, filler, names) is keyed by the archive key instead.
  const transcriptId = archive ? null : userStore.getUserTranscriptId();
  const persistKey = archive ? `a${archive.key}` : transcriptId;
  const archiveRows = archive ? archive.rows : null;   // stable array ref = the ingest trigger
  const hiddenSourcesRef = useRef(new Set());          // archive sources currently hidden
  const archiveIngestedRef = useRef(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const [originalRows, setOriginalRows] = useState([]);
  // The loader rebuilds the list twice after `loading` flips: its final filterAll() when the
  // stream promise settles, and the deferred column enrichment's row swap. Each is counted per
  // load; the URL's ?word= / ?speaker= selection runs when a load has finished both (counts
  // equal), or a rebuild would undo its pin-to-top. Counts, not flags: React's dev mode runs two
  // overlapping loads, and the second one's rebuild must re-apply the selection too.
  const [streamsDone, setStreamsDone] = useState(0);
  const [enrichesDone, setEnrichesDone] = useState(0);
  // True right after load if any visible clip is still missing its poster (thumbnails are
  // generated in the background now) — gates the progressive-thumbnail poll below.
  const thumbnailsPendingRef = useRef(false);
  // Posters generated in the BACKGROUND after the ET opened, keyed by clip id. Kept as a
  // separate map (like `translations`) rather than patched into rows/originalRows, so filling
  // them in does NOT re-run the filter pipeline (which resets Seconds/Length filters and can
  // stop a playing clip). ItemContent reads clipThumbnails[id] ?? data.thumbnail.
  const [clipThumbnails, setClipThumbnails] = useState({});
  // True while the background poster-generation poll is running — drives the "creating
  // thumbnails…" banner + the per-clip placeholder spinner so the user knows it's in progress.
  const [thumbnailsGenerating, setThumbnailsGenerating] = useState(false);
  // Show the "creating thumbnails…" pill whenever posters are actively LANDING — from the cloud
  // poll OR the Premiere panel's local push — so the live count stays visible even when the poll
  // has gone quiet between the panel's generation bursts (that gap is why "is it still making
  // more?" was unclear). Auto-hides ~8s after they stop arriving, so there's never a stuck
  // spinner. Pure client state, no extra requests.
  const [postersFlowing, setPostersFlowing] = useState(false);
  const lastPosterCountRef = useRef(0);
  const posterIdleTimerRef = useRef(null);
  useEffect(() => {
    const n = Object.keys(clipThumbnails).length;
    if (n > lastPosterCountRef.current) {
      lastPosterCountRef.current = n;
      setPostersFlowing(true);
      if (posterIdleTimerRef.current) clearTimeout(posterIdleTimerRef.current);
      posterIdleTimerRef.current = setTimeout(() => setPostersFlowing(false), 8000);
    } else if (n < lastPosterCountRef.current) {
      lastPosterCountRef.current = n;   // transcript switched / posters cleared — re-baseline
    }
  }, [clipThumbnails]);
  useEffect(() => () => { if (posterIdleTimerRef.current) clearTimeout(posterIdleTimerRef.current); }, []);
  // Lazy-thumbnail mode (big ETs, >1,500 clips): the backend streams sentence rows WITHOUT
  // signed posters to avoid a ~28s presign-CPU stall at load. Instead we sign only the clips
  // in the viewport, on scroll, via clip-thumbnails?ids= — merged into clipThumbnails above.
  const lazyThumbnailsRef = useRef(false);
  // Audio-only ET: normally these never get posters (no video on the server), BUT the Premiere
  // plugin can push local posters up (cloud-thumbnail sync), so we still poll — just cautiously
  // (no "creating…" banner unless posters actually start arriving, and a quick stop if none do).
  const isAudioEtRef = useRef(false);
  const clipThumbInFlightRef = useRef(new Set()); // clip ids currently being viewport-signed
  const [originalEntities, setOriginalEntities] = useState([]);
  const [originalCategories, setOriginalCategories] = useState([]);
  const [originalAIB, setOriginalAIB] = useState([]);
  const [originalAutoHighlight, setOriginalAutoHighlight] = useState([]);
  const [originalContentModeration, setOriginalContentModeration] = useState(
    [],
  );
  const [rows, setRows] = useState([]);
  // Ref that always points at the current rows — used by SortableTableRow to look up row IDs
  // without recreating the component on every render.
  const rowsRef = useRef([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  // Tracks the transcript currently loaded — lets the deferred Phase-B enrichment (progressive
  // render) bail if the user opens a different transcript before it finishes.
  const transcriptIdRef = useRef(transcriptId);
  useEffect(() => { transcriptIdRef.current = transcriptId; }, [transcriptId]);

  // Persistent store for user +/- adjustments, keyed by sentence ID.
  // Survives filter/sort operations that re-populate `rows` from originalRows.
  const adjustmentsRef = useRef({});
  // Undo/redo history stacks — each entry is a snapshot of adjustmentsRef.current
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [splitCursor, setSplitCursor] = useState(null);
  // Last row index clicked for Shift+click range selection
  const lastCheckedIndexRef = useRef(null);
  // Suppresses per-row undo pushes during batch selection operations
  const suppressUndoPushRef = useRef(false);
  const [quickFilters, setQuickFilters] = useState({ selectedOnly: false, hasQuestion: false, firstLetter: null, durationSec: null, containsNumbers: false, entityTypes: [], contentFlags: [], keyPhrases: [], filterVerbs: [], filterNouns: [], filterAdjectives: [] });
  const [columnSet, setColumnSet] = useState('standard');
  const [spacesMode, setSpacesMode] = useState('hide');
  // FILLER — an AI pass flags throwaway clips (ums, run-up, rambling, dead air). It then
  // behaves like the Spaces filter: 'dim' fades filler so the keepers shine, 'hide' drops
  // it, 'only' shows ONLY the filler (a filler montage). Ids live in a ref so they survive
  // re-filtering; rows are tagged on the way through filterAll().
  const [fillerMode, setFillerMode] = useState('off'); // 'off' | 'dim' | 'hide' | 'only' (drives the button UI)
  const [fillerDone, setFillerDone] = useState(false);
  const fillerIdsRef = useRef(new Set());
  // fillerMode (state) can't be trusted INSIDE filterAll right after a click — React hasn't
  // committed it yet, so the filter would run with the previous mode (off-by-one). This ref
  // is the synchronous source of truth the filter reads; the state is only for the buttons.
  const fillerModeRef = useRef('off');
  // VIRALITY — an AI pass scores each clip 0–100; the score is injected onto the visible
  // rows (from this ref, by id) and a new "Viral" column appears, replacing Q?. Auto-sorts
  // high→low so the bangers float to the top.
  const viralityScoresRef = useRef(new Map());
  const [viralityDone, setViralityDone] = useState(false);
  // Virality range filter (0–100), the twin of the Time/Length sliders. Ref is the
  // synchronous source of truth for the filter; the state drives the slider UI.
  const [viralityRange, setViralityRange] = useState([0, 100]);
  const viralityRangeRef = useRef([0, 100]);
  // sortOrder.column = the header field the active-sort indicator attaches to;
  // sortOrder.label = the human text shown beside that column ("longest first").
  const [sortOrder, setSortOrder] = useState({ field: null, direction: 'asc', column: null, label: null });
  const [showExtraMobileColumns, setShowExtraMobileColumns] = useState(false);
  const [penMode, setPenMode] = useState(false);
  // Full (unfiltered) min/max for the Seconds and Length dimensions of the current
  // clips — the toolbar compares the applied range against these to know whether the
  // Time / Length filters are actually narrowing anything.
  const [secondsBounds, setSecondsBounds] = useState([0, 0]);
  const [textLenBounds, setTextLenBounds] = useState([0, 0]);
  // Search context: show N clips before/after each search hit (read-only-ish context, but
  // still selectable). 0 = off. Ref mirrors state so filterAll() sees the new value at once.
  const [searchContext, setSearchContext] = useState(0);
  const searchContextRef = useRef(0);
  const onSearchContextChange = (val) => {
    const n = Math.max(0, Math.min(5, Math.round(val)));
    searchContextRef.current = n;
    setSearchContext(n);
    filterAll();
  };

  const [header, setHeader] = useState(sentencesHeader);
  const [headerEntities, setHeaderEntities] = useState(entitiesHeader);
  const [headerAutoHighlight, setHeaderAutoHighlight] =
    useState(autoHighlightHeader);
  const [headerAIB, setHeaderAIB] = useState(AIBHeader);
  const [headerCategories, setHeaderCategories] = useState(categoriesHeader);
  const [headerContentModeration, setHeaderContentModeration] = useState(
    contentModerationHeader,
  );

  const [precision, setPrecision] = useState(
    viewItems.reduce((acc, item) => {
      acc[item] = false;
      return acc;
    }, {}),
  );

  const [order, setOrder] = useState(
    viewItems.reduce((acc, item) => {
      acc[item] = 1;
      return acc;
    }, {}),
  );
  const [checked, setChecked] = useState(
    viewItems.reduce((acc, item) => {
      acc[item] = [false, false]; // [checked, indeterminate] for each view
      return acc;
    }, {}),
  );
  const [selectedCount, setSelectedCount] = useState(
    viewItems.reduce((acc, item) => {
      acc[item] = 0;
      return acc;
    }, {}),
  );

  const [videoFile, setVideoFile] = useState("");
  const [videoExt, setVideoExt] = useState("");
  const [currentView, setCurrentView] = useState("Create a Video");
  const [viewConfig, setViewConfig] = useState(
    CURRENT_VIEW_CONFIG[currentView],
  );
  const [regroupParams, setRegroupParams] = useState(null);
  const [transcriptStyle, setTranscriptStyle] = useState({ size: 22, fontKey: 'sans', fontCSS: "system-ui, -apple-system, sans-serif", bold: true, italic: false });
  // Translation: cache results per language so switching back is instant
  const [translationsCache, setTranslationsCache] = useState({});

  // Speaker names (v1): a cosmetic label layer, letter -> name (e.g. {A: "Bob"}). Browser-local
  // per transcript, like translations/virality. The underlying clip `speaker` letter is NEVER
  // rewritten, so the filter pipeline / AI scoring / export are untouched. Declared here (above
  // its load/save effects below) so it's initialized before those effects reference it. See
  // docs/superpowers/specs/2026-08-06-speaker-naming-design.md.
  const [speakerNames, setSpeakerNames] = useState({});
  // Set (or clear, when name is blank) the name for a speaker LETTER — updates every row for
  // that speaker at once (the column reads through the map).
  const onRenameSpeaker = useCallback((letter, name) => {
    if (letter == null || letter === '') return;
    // ARCHIVE: a rename LINKS people across videos (same name = same speaker). The archive
    // controller owns the identity map; the row's speaker value itself becomes the name.
    if (archive && archive.onRenameSpeaker) { archive.onRenameSpeaker(letter, name); return; }
    setSpeakerNames((prev) => {
      const next = { ...prev };
      const v = (name || '').trim();
      if (v) next[letter] = v; else delete next[letter];
      return next;
    });
  }, [archive]);
  const [translationLang, setTranslationLang] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const translateAbortRef = useRef(false);
  // Fresh mirror of the cache + a set of in-flight `${lang}:${id}` keys, so the
  // background fill and on-scroll prioritisation never fetch the same clip twice.
  const translationsCacheRef = useRef({});
  const inFlightRef = useRef(new Set());
  useEffect(() => { translationsCacheRef.current = translationsCache; }, [translationsCache]);
  const translations = translationLang ? (translationsCache[translationLang] || {}) : {};
  // Remember the last chosen language so the on/off toggle can restore it
  const [lastTranslationLang, setLastTranslationLang] = useState(null);
  useEffect(() => { if (translationLang) setLastTranslationLang(translationLang); }, [translationLang]);

  // ── Persist translations per transcript in localStorage ────────────────
  // Load saved translations + active language when a transcript opens.
  useEffect(() => {
    if (persistKey == null) return;
    try {
      const raw = localStorage.getItem(`cc_translations_${persistKey}`);
      const cache = raw ? JSON.parse(raw) : {};
      translationsCacheRef.current = cache;
      setTranslationsCache(cache);
      const savedLang = localStorage.getItem(`cc_translation_lang_${persistKey}`);
      if (savedLang) { setTranslationLang(savedLang); setLastTranslationLang(savedLang); }
      else {
        // restore the remembered language even if display was left off
        const remembered = localStorage.getItem(`cc_translation_last_${persistKey}`);
        if (remembered) setLastTranslationLang(remembered);
      }
    } catch { /* corrupt or full storage — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistKey]);

  // Save the translation cache (debounced — it can grow large on long videos).
  useEffect(() => {
    if (persistKey == null) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(`cc_translations_${persistKey}`, JSON.stringify(translationsCache)); }
      catch { /* quota exceeded — translations just won't persist */ }
    }, 800);
    return () => clearTimeout(t);
  }, [translationsCache, persistKey]);

  // Load this transcript's speaker names on open; persist on change (browser-local label layer).
  useEffect(() => {
    if (persistKey == null) { setSpeakerNames({}); return; }
    try {
      const raw = localStorage.getItem(`cc_speaker_names_${persistKey}`);
      const obj = raw ? JSON.parse(raw) : null;
      setSpeakerNames(obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {});
    } catch (e) { setSpeakerNames({}); }
  }, [persistKey]);
  useEffect(() => {
    if (persistKey == null) return;
    try { localStorage.setItem(`cc_speaker_names_${persistKey}`, JSON.stringify(speakerNames)); }
    catch { /* quota — names just won't persist */ }
  }, [speakerNames, persistKey]);

  // Save the active + remembered language so the toggle state survives reload.
  useEffect(() => {
    if (persistKey == null) return;
    try {
      if (translationLang) localStorage.setItem(`cc_translation_lang_${persistKey}`, translationLang);
      else localStorage.removeItem(`cc_translation_lang_${persistKey}`);
    } catch {}
  }, [translationLang, persistKey]);
  useEffect(() => {
    if (persistKey == null || !lastTranslationLang) return;
    try { localStorage.setItem(`cc_translation_last_${persistKey}`, lastTranslationLang); } catch {}
  }, [lastTranslationLang, persistKey]);

  // Restore saved virality scores + filler tags for THIS transcript, so a refresh or glitch
  // never costs a re-score again. We only set the refs/flags here; the fetch effect calls
  // filterAll() once the rows load, which injects the scores onto the visible list.
  useEffect(() => {
    if (persistKey == null) return;
    try {
      const raw = localStorage.getItem(`cc_virality_${persistKey}`);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.length) {
        viralityScoresRef.current = new Map(arr);
        setViralityDone(true);
        const revealViral = (prev) => prev.map((col) =>
          col.field === 'virality_score' ? { ...col, visible: true }
            : col.field === 'question' ? { ...col, visible: false } : col);
        setHeader(revealViral);
        setHeaderCategories(revealViral);
      } else {
        viralityScoresRef.current = new Map();
        setViralityDone(false);
      }
    } catch (e) { /* ignore */ }
    try {
      const raw = localStorage.getItem(`cc_filler_${persistKey}`);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.length) {
        fillerIdsRef.current = new Set(arr);
        setFillerDone(true);
      } else {
        fillerIdsRef.current = new Set();
        setFillerDone(false);
      }
    } catch (e) { /* ignore */ }
  }, [persistKey]);

  // Toggle translated text on/off without losing the cached results.
  const toggleTranslation = () => {
    if (translationLang) setTranslationLang(null);
    else if (lastTranslationLang) setTranslationLang(lastTranslationLang);
  };
  const canToggleTranslation = !!(
    (translationLang && Object.keys(translationsCache[translationLang] || {}).length) ||
    (lastTranslationLang && Object.keys(translationsCache[lastTranslationLang] || {}).length)
  );

  // Text editing: non-destructive map of id → edited text
  const [editMode, setEditMode] = useState(false);
  const [playNext, setPlayNext] = useState(false);
  const playNextRef = useRef(false);
  useEffect(() => { playNextRef.current = playNext; }, [playNext]);
  // When on, playback uses each clip's pen-highlighted range (if any) instead of the
  // whole clip — so Play Next previews just the trimmed moments.
  const [playHighlightsOnly, setPlayHighlightsOnly] = useState(false);
  const playHighlightsRef = useRef(false);
  useEffect(() => { playHighlightsRef.current = playHighlightsOnly; }, [playHighlightsOnly]);

  // ── Play Selected ─────────────────────────────────────────────────────────
  // Auto-plays the SELECTED clips, in the order they were selected, one after another,
  // stopping when the last one ends. Distinct from Play Next (which advances by table
  // order and ignores selection). Selection order is free from the store
  // (filterStore.getSelectedSentences(view) is an ordered array of ids). Everything the
  // advance path reads lives in refs so a stale playFired closure still advances correctly.
  const [playingSelected, setPlayingSelected] = useState(false);   // in a Play-Selected sequence
  const [playSelectedPaused, setPlaySelectedPaused] = useState(false);
  const playingSelectedRef = useRef(false);
  const playSelectedPausedRef = useRef(false);
  const playSelectedQueueRef = useRef([]);   // ordered clip ids to play
  const playSelectedCursorRef = useRef(0);   // index into the queue
  useEffect(() => { playingSelectedRef.current = playingSelected; }, [playingSelected]);
  useEffect(() => { playSelectedPausedRef.current = playSelectedPaused; }, [playSelectedPaused]);
  // Supercut mode (Premiere panel): when on AND a search is active, each ticked
  // clip contributes ONLY the searched word(s) to the timeline — "border border
  // border" from many clips — using the word timings already in each row. Same
  // idea as the "Super Cut (searched words only)" export, computed client-side.
  const supercutRef = useRef(false);
  const [editedSentences, setEditedSentences] = useState({});
  const editedSentencesRef = useRef({});
  useEffect(() => { editedSentencesRef.current = editedSentences; }, [editedSentences]);
  const [editingRow, setEditingRow] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [closeSearchModalRef, setCloseSearchModalRef] = useState(null);
  const [openExportModalRef, setOpenExportModalRef] = useState(null); // toolbar registers its export-modal opener here (the mobile floating Export button calls it)
  const [openBlackCherryRef, setOpenBlackCherryRef] = useState(null);

  // ── FLOATING TOOL RAIL ───────────────────────────────────────────────────────
  // The toolbar carries 16 equal-weight sections and a newcomer has no idea what to press
  // first. The rail groups them into a few bubbles (Search / Filters / Ask AI / Settings);
  // each bubble opens a box of the SAME buttons and pressing one calls back into the
  // toolbar's own control via openTool(), so there is never a second implementation.
  const [openTool, setToolbarOpeners] = useState(null);   // toolbar registers openTool(name)
  const [openBubble, setOpenBubble] = useState(null);     // which bubble's box is showing
  const [showRailPrefs, setShowRailPrefs] = useState(false); // collapse the layout knobs — the real settings come first
  const [aiAsk, setAiAsk] = useState("");                    // prompt typed straight into the Ask AI bubble
  const [showAiMore, setShowAiMore] = useState(false);       // the chat is the point; tools fold away
  const [rawVideoOpen, setRawVideoOpen] = useState(false);   // watch the untouched upload
  const [rawVideoSrc, setRawVideoSrc] = useState("");        // ARCHIVE: which source to watch
  const [nowShowing, setNowShowing] = useState("");          // transient "here is what changed" line
  const nowShowingTimer = useRef(null);
  const sayShowing = useCallback((msg) => {
    setNowShowing(msg);
    if (nowShowingTimer.current) clearTimeout(nowShowingTimer.current);
    nowShowingTimer.current = setTimeout(() => setNowShowing(""), 2600);
  }, []);
  useEffect(() => () => { if (nowShowingTimer.current) clearTimeout(nowShowingTimer.current); }, []);
  const [aiPersona, setAiPersona] = useState("");            // prefixed onto the prompt, not a separate mode
  const [askHint, setAskHint] = useState("");                // typewriter placeholder

  // Same idea as BlackCherryLoop on the flagship page: type a prompt out, hold, delete,
  // move to the next. It runs only while the Ask bubble is open and the box is empty, so
  // it never fights with what you are typing.
  useEffect(() => {
    if (openBubble !== "ai" || aiAsk) { setAskHint(""); return undefined; }
    const PROMPTS = [
      "make me a trailer",
      "give me the best lines",
      "find the funniest moments",
      "cut a 30-second teaser",
      "every time she mentions her mother",
    ];
    let i = 0, c = 0, phase = "type", t = null;
    const tick = () => {
      const full = PROMPTS[i];
      let delay = 70;
      if (phase === "type") {
        c += 1; setAskHint(full.slice(0, c));
        if (c >= full.length) { phase = "hold"; delay = 1800; }
      } else if (phase === "hold") {
        phase = "delete"; delay = 400;
      } else {
        c -= 1; setAskHint(full.slice(0, Math.max(0, c))); delay = 34;
        if (c <= 0) { phase = "type"; i = (i + 1) % PROMPTS.length; delay = 380; }
      }
      t = setTimeout(tick, delay);
    };
    t = setTimeout(tick, 400);
    return () => { if (t) clearTimeout(t); };
  }, [openBubble, aiAsk]);
  const [railOut, setRailOut] = useState(false);          // tab mode: is the rail slid in?
  const readPref = (k, d) => { try { return localStorage.getItem(k) || d; } catch (e) { return d; } };
  const [railPos, setRailPos]     = useState(() => readPref("cc_rail_pos2", "play"));    // 'right' | 'play' | 'free'
  const [railShape, setRailShape] = useState(() => readPref("cc_rail_shape", "flower")); // 'row' | 'flower' | 'arc' — used when free
  const [railXY, setRailXY]       = useState(() => {                                      // where you dragged the cluster
    try { return JSON.parse(localStorage.getItem("cc_rail_xy") || "null") || null; }
    catch (e) { return null; }
  });
  const [toolbarHidden, setToolbarHidden] = useState(() => readPref("cc_toolbar_hidden", "0") === "1");
  const [speakerHeadings, setSpeakerHeadings] = useState(() => readPref("cc_speaker_headings", "0") === "1");
  const [etZoom, setEtZoom] = useState(() => Number(readPref("cc_et_zoom", "100")) || 100);
  const [timelineOpen, setTimelineOpen] = useState(false);       // the dock is opt-in, always
  const [timelineEnabled, setTimelineEnabled] = useState(() => readPref("cc_timeline_dock", "0") === "1");
  // The floating Phone/Full + Compact control has no corner left to live in on a phone —
  // every one of them has content or a bubble in it. Its controls are all in Settings now,
  // so it defaults OFF there and you switch it back on if you want it.
  const [viewToggleShown, setViewToggleShown] = useState(() => readPref("cc_view_toggle", ""));
  const [railShow, setRailShow]   = useState(() => readPref("cc_rail_show", "auto"));    // 'auto' | 'always' | 'tab'
  const [railSpace, setRailSpace] = useState(() => readPref("cc_rail_space", "float"));  // 'float' | 'push'
  const [railHint, setRailHint]   = useState(() => readPref("cc_rail_hint", "auto"));    // 'auto' | 'label' | 'peek' | 'none'
  useEffect(() => { try { localStorage.setItem("cc_rail_pos2", railPos); } catch (e) {} }, [railPos]);
  useEffect(() => { try { localStorage.setItem("cc_rail_shape", railShape); } catch (e) {} }, [railShape]);
  useEffect(() => { try { localStorage.setItem("cc_rail_xy", JSON.stringify(railXY)); } catch (e) {} }, [railXY]);
  useEffect(() => { try { localStorage.setItem("cc_toolbar_hidden", toolbarHidden ? "1" : "0"); } catch (e) {} }, [toolbarHidden]);
  useEffect(() => { try { localStorage.setItem("cc_speaker_headings", speakerHeadings ? "1" : "0"); } catch (e) {} }, [speakerHeadings]);
  useEffect(() => { try { localStorage.setItem("cc_et_zoom", String(etZoom)); } catch (e) {} }, [etZoom]);
  useEffect(() => { try { localStorage.setItem("cc_timeline_dock", timelineEnabled ? "1" : "0"); } catch (e) {} }, [timelineEnabled]);
  useEffect(() => { try { if (viewToggleShown) localStorage.setItem("cc_view_toggle", viewToggleShown); } catch (e) {} }, [viewToggleShown]);
  useEffect(() => { if (!timelineEnabled) setTimelineOpen(false); }, [timelineEnabled]);

  // One button puts every layout experiment back to how it shipped.
  const resetLayout = () => {
    setRailPos("play"); setRailShape("flower"); setRailXY(null);
    setRailShow("auto"); setRailSpace("float"); setRailHint("auto");
    setToolbarHidden(false); setSpeakerHeadings(false); setEtZoom(100);
    setTimelineEnabled(false); setTimelineOpen(false); setViewToggleShown("");
  };
  useEffect(() => { try { localStorage.setItem("cc_rail_show", railShow); } catch (e) {} }, [railShow]);
  useEffect(() => { try { localStorage.setItem("cc_rail_space", railSpace); } catch (e) {} }, [railSpace]);
  useEffect(() => { try { localStorage.setItem("cc_rail_hint", railHint); } catch (e) {} }, [railHint]); // toolbar registers its Black Cherry opener here (an embed's cc-et-cmd "blackcherry" calls it)
  const openBlackCherryLatest = useRef(null);
  openBlackCherryLatest.current = openBlackCherryRef; // reassigned every render so the embed command never reads a stale opener
  const openExportLatest = useRef(null);
  openExportLatest.current = openExportModalRef; // "?export=1" opens the exporter once the picks are in
  // "?blackcherry=1" (the library's quick look → Ask your AI → Fast): open Black Cherry once the
  // toolbar has registered its opener, then drop the flag so a reload doesn't reopen it.
  const blackCherryOnLoad = useRef(new URLSearchParams(window.location.search).get("blackcherry") === "1");
  useEffect(() => {
    if (!blackCherryOnLoad.current || !openBlackCherryRef) return;
    blackCherryOnLoad.current = false;
    openBlackCherryRef();
    try { window.history.replaceState(null, "", window.location.pathname); } catch (e) { /* ignore */ }
  }, [openBlackCherryRef]);
  const [exportModalOpen, setExportModalOpen] = useState(false); // toolbar reports the Create modal's open state — floating overlays (view toggle, export bar) hide while it's up
  const [exportWarning, setExportWarning] = useState(null); // { selectedOptions, customTitle, orientation, burnCaptions, captionMode, captionSize, captionWordsPerGroup } when >5min
  const [sharedExportBuilding, setSharedExportBuilding] = useState(0); // in-flight guest stitched-export builds (shows a persistent "stitching…" indicator)
  // A trimmed clip waiting for the user to say where it goes: { blob, filename, text }.
  // It exists because a share must start on a FRESH tap — see socialShare.js, rule 2.
  const [pendingShare, setPendingShare] = useState(null);
  const [sheetLink, setSheetLink] = useState("");       // this transcript's /s/ link, once asked for
  const [sheetLinkBusy, setSheetLinkBusy] = useState(false);
  const [shareBoxFor, setShareBoxFor] = useState(null);  // { row, title, timecode } while the share box is up
  const [working, setWorking] = useState(null);          // { stage, sub } while a clip is being cut
  const [boxXY, setBoxXY] = useState(null);              // panel dragged to {x,y}px; null = docked
  const boxDrag = useRef(null);
  const [boxSize, setBoxSize] = useState(null);          // panel resized to {w,h}px; null = default
  const boxResize = useRef(null);

  const sentClasses = { 0: "positive", 1: "negative", 2: "neutral" };
  const [toolbarPosition, setToolbarPosition] = useState("right");

  const [firstVisibleIndex, setFirstVisibleIndex] = useState(0);
  const [scrollTimeDisplay, setScrollTimeDisplay] = useState({
    show: false,
    time: "",
    position: { x: 0, y: 0 },
  });
  const scrollTimeoutRef = useRef(null);
  const scrollerRef = useRef(null); // Ref for the scroll container
  const virtuosoRef = useRef(null); // Ref for TableVirtuoso navigation

  const theme = useTheme();
  const mode = theme.palette.mode;
  const { isXSmallScreen } = useEtBreakpoints(); // phone layout — gates the floating Export/Download button; honors the view toggle
  const [etViewMode] = useEtViewMode();       // 'auto' | 'phone' | 'full'
  const [etModeIdx, etMode] = useEtMode();    // Compact / Simple / Media
  const labs = useLabs();                     // "What's new" switches — every one defaults OFF
  // Resolved rail geometry. 'auto' parks the rail behind an edge tab on a phone and inside
  // the Premiere panel (both are short on room) and leaves it open everywhere else.
  const rail = useMemo(() => {
    const compact = isXSmallScreen;
    const inPanel = getEmbedParentOrigin() === "file://";
    // The home page runs the real ET inside a small frame. At full size the bubbles sat on
    // top of the transcript in that little window, so an embed gets a smaller set pinned to
    // the edge — the frame's bottom is already busy with Play and Select clips.
    let embedded = false;
    try { embedded = window.self !== window.top; } catch (e) { embedded = true; }
    const show = railShow !== "auto" ? railShow : ((compact || inPanel) ? "tab" : "always");
    const hint = railHint !== "auto" ? railHint : (compact ? "peek" : "label");
    const free = !embedded && railPos === "free";      // draggable cluster, any shape
    const byPlay = embedded || railPos === "play";     // sit above Play selected instead
    const out = free || byPlay || show === "always" || railOut;
    // A right-docked toolbar already owns the 60px gutter, so sit inboard of it.
    const right = toolbarPosition === "right" ? 72 : (embedded ? 6 : 14);
    const gutter = compact ? 58 : 74;
    return {
      compact, show, hint, out, right, gutter, byPlay, free, shape: railShape, embedded,
      size: embedded ? 34 : (compact ? 48 : 56),
      // Only "Push sheet" reserves room. Floating (the default) leaves the sheet alone —
      // a permanent gutter made the spreadsheet look shunted across.
      pad: !byPlay && !free && railSpace === "push" && out ? gutter : 0,
      parked: !byPlay && !free && show === "tab" && !railOut,
    };
  }, [isXSmallScreen, railShow, railHint, railSpace, railPos, railShape, railOut, toolbarPosition]);
  const subtitleColorHighlight = mode === "dark" ? "orange" : "red";

  // STABLE TableVirtuoso components. These MUST keep the same identity across renders.
  // Previously they were created inline inside renderCurrentView, so every parent re-render
  // (e.g. selecting a checkbox) produced new component types — which makes Virtuoso unmount
  // and remount the entire table body. Every clip <video> then remounted and reloaded,
  // making all the clips "jump a frame". Memoizing them keeps the table mounted.
  const virtuosoComponents = useMemo(() => ({
    Table: React.forwardRef((props, ref) => (
      <table
        {...props}
        ref={ref}
        style={{
          ...props.style,
          tableLayout: "auto",
          width: "100%",
          borderCollapse: "separate",
          borderSpacing: 0,
          minWidth: "fit-content",
        }}
        className="responsive-table"
      />
    )),
    TableRow: SortableTableRow,
    TableHead: React.forwardRef((props, ref) => (
      <thead
        {...props}
        ref={ref}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      />
    )),
  }), [theme]);

  // Regroup: merge originalRows according to slider params (pure frontend transform)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rebasedRows = useMemo(
    () => {
      if (archive || !regroupParams) return originalRows;                       // Original — sentence by sentence (ARCHIVE: no regroup across videos)
      if (regroupParams.mode === 'word') return splitIntoWords(originalRows);   // Original — word by word
      if (regroupParams.mode === 'interval') return regroupByInterval(originalRows, regroupParams.interval);
      return regroupSentences(originalRows, regroupParams);                     // Custom
    },
    [originalRows, regroupParams]
  );

  // Posters for the NON-Sentence views (Entities / Highlights / Categories(=AIB) / Content
  // Moderation). Those rows carry their OWN model id (an Entity/AIB/... pk), but the poster
  // pipeline — the clip-thumbnails poll, the plugin `thumbs` push, and the cloud upload — is
  // keyed ONLY by Sentence id. So `clipThumbnails[row.id]` always misses for these views and
  // (for audio-only / plugin sources, where the worker never generated per-model posters) the
  // streamed `row.thumbnail` is empty too → blank thumbnails. Fix: every such row overlaps a
  // Sentence in time (rows expose start_in/end_in in seconds, same axis as Sentence rows), so
  // resolve each row → the Sentence covering its start → reuse that Sentence's poster. Sentence
  // views pass `clipThumbnails` through untouched (zero extra work on the big list).
  // Sorted Sentence start-times (id + start seconds) for binary-search lookup. Only rebuilds
  // when the Sentence set changes — NOT on every background-poll thumbnail tick.
  const sentencePosterIndex = useMemo(() => {
    // ARCHIVE: one sorted list PER SOURCE — a time overlap only means anything inside one video.
    // Single ET: a single list under the "" key.
    const bySrc = new Map();
    for (const r of originalRows) {
      if (!r || r.space) continue;
      const s = Number(r.start_in);
      if (!isFinite(s)) continue;
      const k = r.source ? String(r.source.transcriptId) : "";
      if (!bySrc.has(k)) bySrc.set(k, []);
      bySrc.get(k).push({ id: r.id, start: s });
    }
    bySrc.forEach((list) => list.sort((a, b) => a.start - b.start));
    return bySrc;
  }, [originalRows]);
  const viewClipThumbnails = useMemo(() => {
    const NON_SENTENCE = new Set(['AIB', 'Entities', 'Categories', 'Auto Highlight', 'Content Moderation']);
    if (!NON_SENTENCE.has(currentView)) return clipThumbnails;
    const sentenceIdAt = (t, srcKey) => {
      const sents = sentencePosterIndex.get(srcKey) || [];
      if (!sents.length || !isFinite(t)) return null;
      let lo = 0, hi = sents.length - 1, best = 0;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (sents[mid].start <= t) { best = mid; lo = mid + 1; } else hi = mid - 1;
      }
      return sents[best].id; // greatest start <= t = the covering (or nearest preceding) Sentence
    };
    const out = {};
    for (const r of rows) {
      if (!r || r.space) continue;
      const direct = clipThumbnails[r.id];
      if (direct) { out[r.id] = direct; continue; }   // a Sentence-id poster that happens to match
      if (r.thumbnail) continue;                       // row has its own streamed poster → let ItemContent use it
      const sid = sentenceIdAt(Number(r.start_in), r.source ? String(r.source.transcriptId) : "");
      if (sid != null && clipThumbnails[sid]) out[r.id] = clipThumbnails[sid];
    }
    return out;
  }, [currentView, rows, clipThumbnails, sentencePosterIndex]);

  // Re-filter whenever the regrouped base changes (mode switch or slider moved).
  // Re-fit the Seconds/Length filter ranges to the new clips — otherwise a re-cut
  // (e.g. 1-word clips below the length min, or 28s interval clips above the
  // seconds max) falls outside the old range and every clip gets filtered out.
  useEffect(() => {
    if (rebasedRows.length > 0) {
      const sb = getDimensionMinMax("sec", rebasedRows);
      const lb = getDimensionMinMax("length", rebasedRows);
      filterStore.setSeconds(sb, currentView);
      filterStore.setTextLen(lb, currentView);
      setSecondsBounds(sb);
      setTextLenBounds(lb);
      filterAll();
    }
    // filterAll reads rebasedRows through closure — intentionally omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebasedRows]);

  // Fetch one sentence from the (free, unofficial) Google Translate endpoint
  const fetchTranslation = async (row, lang) => {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${lang}&dt=t&q=${encodeURIComponent((row.sentence || '').trim())}`;
    const res = await fetch(url);
    const data = await res.json();
    return (data[0] || []).map(c => c[0]).join('');
  };

  // Translate a subset of rows, skipping anything already cached or in flight.
  // Used for both the immediate visible batch and the background fill, so the
  // same clip is never fetched twice.
  const ensureTranslated = async (subset, lang, { concurrency = 6, delayMs = 120, onProgress } = {}) => {
    const done = translationsCacheRef.current[lang] || {};
    const queue = [];
    const localSeen = new Set();
    for (const r of subset) {
      if (!r || r.space || !r.sentence) continue;
      if (done[r.id] != null) continue;                       // already translated
      const key = `${lang}:${r.id}`;
      if (inFlightRef.current.has(key)) continue;             // currently fetching
      if (localSeen.has(r.id)) continue;                      // de-dupe this batch
      localSeen.add(r.id);
      inFlightRef.current.add(key);
      queue.push(r);
    }
    if (!queue.length) { onProgress && onProgress(100); return; }

    for (let i = 0; i < queue.length; i += concurrency) {
      if (translateAbortRef.current) break;
      const batch = queue.slice(i, i + concurrency);
      const results = await Promise.allSettled(
        batch.map(async r => ({ id: r.id, text: await fetchTranslation(r, lang) }))
      );
      const upd = {};
      results.forEach((res, idx) => {
        inFlightRef.current.delete(`${lang}:${batch[idx].id}`);
        if (res.status === 'fulfilled') upd[res.value.id] = res.value.text;
      });
      if (Object.keys(upd).length) {
        setTranslationsCache(prev => ({ ...prev, [lang]: { ...(prev[lang] || {}), ...upd } }));
      }
      onProgress && onProgress(Math.min(99, Math.round(((i + concurrency) / queue.length) * 100)));
      if (i + concurrency < queue.length) await new Promise(res => setTimeout(res, delayMs));
    }
    // release any still-marked (aborted) keys
    queue.forEach(r => inFlightRef.current.delete(`${lang}:${r.id}`));
  };

  // Lazy-thumbnail viewport fetch: given a slice of visible rows, sign posters for the clips
  // that don't have one yet (clip-thumbnails?ids=) and merge them into clipThumbnails. Mirrors
  // ensureTranslated — de-dupes via clipThumbInFlightRef, skips clips already signed. This is
  // what keeps a big ET's load cost independent of size: only the ~20 rows on screen get signed
  // at a time, on scroll, instead of all N at load. No-op unless lazy mode is active.
  const ensureClipThumbnails = async (subset) => {
    if (!lazyThumbnailsRef.current || !subset || !subset.length) return;
    const have = clipThumbnails;
    const inFlight = clipThumbInFlightRef.current;
    const ids = [];
    const seen = new Set();
    for (const r of subset) {
      if (!r || r.id == null) continue;
      const id = String(r.id);
      if (have[id] || r.thumbnail) continue;        // already signed (in the map or on the row)
      if (inFlight.has(id) || seen.has(id)) continue;
      seen.add(id);
      ids.push(r.id);
    }
    if (!ids.length) return;
    ids.forEach((id) => inFlight.add(String(id)));
    try {
      // ARCHIVE: ids are namespaced ("6_456") — sign per source transcript, re-key the result.
      // Single ET: one group keyed by this transcript, same request as before.
      const groups = new Map();
      ids.forEach((id) => {
        const p = archive ? parseSheetId(id) : null;
        const tid = p ? p.transcriptId : transcriptId;
        if (tid == null) return;
        if (!groups.has(tid)) groups.set(tid, []);
        groups.get(tid).push(p ? { raw: p.rawId, ns: id } : { raw: id, ns: id });
      });
      for (const [tid, entries] of groups) {
        const res = await apiRequest(
          `${clipThumbnailsUrl}${tid}/?ids=${entries.map((e) => e.raw).join(",")}`,
          { method: "GET" },
        );
        const data = await res.json();
        const got = (data && data.thumbnails) || {};
        const map = {};
        entries.forEach((e) => { const url = got[e.raw]; if (url) map[e.ns] = url; });
        if (Object.keys(map).length) {
          setClipThumbnails((prev) => ({ ...prev, ...map }));
        }
      }
    } catch (e) {
      // transient — the row keeps its cherry placeholder; a later scroll retries it
    } finally {
      ids.forEach((id) => inFlight.delete(String(id)));
    }
  };

  const translateAll = async (lang) => {
    if (!lang) return;
    translateAbortRef.current = false;
    const view = rowsRef.current.filter(r => !r.space && r.sentence);

    // 1) Immediate: translate the first 30 of the current view so results
    //    show up right away.
    setTranslating(true);
    setTranslationProgress(0);
    await ensureTranslated(view.slice(0, 30), lang, {
      concurrency: 8, delayMs: 80, onProgress: setTranslationProgress,
    });
    setTranslating(false);
    setTranslationProgress(100);

    // 2) Background: quietly fill in the rest of the view (gentler on the
    //    endpoint so it doesn't throttle). Not awaited.
    ensureTranslated(view.slice(30), lang, { concurrency: 5, delayMs: 220 });
  };

  const getOriginalDataForCurrentView = (view = currentView) => {
    if (filterStore.getSpaces(view)) {
      return rebasedRows.map((obj) => ({ ...obj, order: maxLines }));
    }
    return rebasedRows
      .filter((obj) => !obj["space"])
      .map((obj) => ({ ...obj, order: maxLines }));
  };

  const getDataForCurrentView = (view = currentView) => {
    // get a fresh copy of the data for the current view
    // and add the order field to the data
    // filter out the spaces if the spaces are off
    switch (view) {
      case "AIB":
        return originalAIB.map((obj) => ({ ...obj, order: maxLines }));
      case "Auto Highlight":
        return originalAutoHighlight.map((obj) => ({
          ...obj,
          order: maxLines,
        }));
      case "Entities":
        return originalEntities.map((obj) => ({ ...obj, order: maxLines }));
      case "Categories":
        return originalCategories.map((obj) => ({ ...obj, order: maxLines }));
      case "Content Moderation":
        return originalContentModeration.map((obj) => ({
          ...obj,
          order: maxLines,
        }));
      case "Charts":
      case "Cherry Pick":
      case "Create a Video":
        return getOriginalDataForCurrentView(view);
      default:
        console.log("default should not happen");
        return getOriginalDataForCurrentView(view);
    }
  };

  const getHeaderCurrentView = () => {
    switch (currentView) {
      case "AIB":
        return headerAIB;
      case "Auto Highlight":
        return headerAutoHighlight;
      case "Categories":
        return headerCategories;
      case "Content Moderation":
        return headerContentModeration;
      case "Entities":
        return headerEntities;
      case "Create a Video":
      default:
        return header;
    }
  };

  const updateHeaderDataView = (newHeader) => {
    switch (currentView) {
      case "AIB":
        return setHeaderAIB(newHeader);
      case "Auto Highlight":
        return setHeaderAutoHighlight(newHeader);
      case "Categories":
        return setHeaderCategories(newHeader);
      case "Content Moderation":
        return setHeaderContentModeration(newHeader);
      case "Entities":
        return setHeaderEntities(newHeader);
      case "Create a Video":
      default:
        return setHeader(newHeader);
    }
  };

  // ── Embed pick bridge (v2: two-way) ──────────────────────────────────────
  // When the ET runs inside an iframe (home-page embeds, Letter P's timeline,
  // and later the Premiere-panel):
  //   OUT: broadcast the ordered pick list — with EFFECTIVE (trim-adjusted)
  //        times and the source video URL — on every selection change and
  //        after every trim.
  //   IN:  accept a small command set from the parent page (trim ±0.1s,
  //        reorder picks, deselect) so an external timeline can drive the
  //        same non-destructive edits as the ET's own steppers.
  // Locked down both directions:
  //  1. active ONLY on public /s/ share pages (never the authenticated ET), and
  //  2. posts to / accepts from an explicit allowlisted parent origin — never
  //     "*" — so a hostile page framing us can't harvest data or drive edits.
  const embedAdjustRef = useRef(null);      // latest onAdjustTimes (defined later in this file)
  const embedSplitRef = useRef(null);       // latest split-at-time handler (defined later)
  const embedBroadcastRef = useRef(null);   // latest broadcast closure
  // Remote-driver (phone → Premiere): on a /s/…#cc_remote=<token> link, every pick change is
  // POSTed (debounced) to the live-picks relay so the desktop plugin drives Premiere. The token
  // is the capability; computed once from the hash. remoteDriverStatus drives the "🔴 Live" banner.
  const remoteDriverTokenRef = useRef(getRemoteDriverToken());
  const remotePostTimerRef = useRef(null);
  const remotePendingPicksRef = useRef(null);
  const [remoteDriverStatus, setRemoteDriverStatus] = useState(
    getRemoteDriverToken() ? "connecting" : null
  );
  // 🔗 AI link: this ET follows picks an AI host (ChatGPT / Claude via the CherryClipz MCP
  // connector) writes into the live-picks relay — the same relay, same "apply when version
  // rises" rule as the Premiere panel's 📱 phone control. Owner-only (never on /s/ shares,
  // never in Archive mode). The poller lives in utils/aiLink.js; this is just the wiring.
  const [aiLink, setAiLink] = useState({ on: false, status: AI_LINK_STATUS.OFF, count: 0 });
  const aiLinkPollerRef = useRef(null);
  // Freshest selectClipsByIds (defined further down). AI-link picks go through the SAME path
  // as Paste AI Picks: clear filters, select in pick order, pen-trim "keep" phrases, pad
  // trailing silence, pin the picks to the top numbered 1, 2, 3… — not just a tick.
  const selectByIdsRef = useRef(null);
  // On a /s/ share page the viewer has no account, so they can't arm the relay — but they CAN
  // follow it (the backend accepts a valid share token for reads). So the link is offered
  // everywhere except Archive, in follow-only mode on shares.
  const aiLinkShareFollow = typeof window !== "undefined" && window.location.pathname.startsWith("/s/");
  const aiLinkAvailable = !archive && !!transcriptId;
  const toggleAiLink = async () => {
    if (aiLinkPollerRef.current) {
      aiLinkPollerRef.current.stop();
      aiLinkPollerRef.current = null;
      return;
    }
    if (!aiLinkAvailable) return;
    const relayJson = async (url, init) => {
      const r = await apiRequest(url, init);
      if (!r.ok) { const e = new Error("relay " + r.status); e.status = r.status; throw e; }
      return r.json();
    };
    const poller = createAiLinkPoller({
      transcriptId,
      arm: (armed) => relayJson(livePicksArmUrl, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ transcript_id: transcriptId, armed, keep_picks: true }),
      }),
      fetchPicks: () => relayJson(`${livePicksGetUrl}${transcriptId}/`, { method: "GET" }),
      followOnly: aiLinkShareFollow,
      onPicks: (ids, picks) => {
        if (selectByIdsRef.current) selectByIdsRef.current(JSON.stringify(picks), true);
      },
      onStatus: (status, extra = {}) => setAiLink((prev) => ({
        on: status !== AI_LINK_STATUS.OFF,
        status,
        count: status === AI_LINK_STATUS.LIVE ? (extra.count || 0) : (status === AI_LINK_STATUS.OFF ? 0 : prev.count),
      })),
    });
    aiLinkPollerRef.current = poller;
    try {
      await poller.start();
    } catch (e) {
      aiLinkPollerRef.current = null;
      setAiLink({ on: false, status: AI_LINK_STATUS.OFF, count: 0 });
    }
  };
  useEffect(() => () => { if (aiLinkPollerRef.current) aiLinkPollerRef.current.stop(); }, []);
  // Reassigned every render so it always closes over fresh state. Effective times come
  // from adjustmentsRef (the same source the export uses), falling back to the originals.
  // The ordered cut, exactly as the plugin bridge and the export see it. Built once here so
  // the in-app timeline can never drift into a second interpretation of the same picks.
  const buildPicks = () => {
    try {
      const ids = (filterStore.selectedSentences[currentView] || []).slice();
      // Build the id→row map from the CURRENT view's rows, not always the
      // sentence view. Ticks in Categories / Entities / Auto Highlight /
      // Content Moderation select ids that don't exist in originalRows, so the
      // old sentence-only map resolved them all to null and nothing reached the
      // timeline. getDataForCurrentView() is the same switch the rest of the
      // component uses to pick a view's rows.
      const viewRows = getDataForCurrentView(currentView) || [];
      const byId = new Map(viewRows.filter((r) => !r.space).map((r) => [r.id, r]));
      // Overlay live rows: split pieces (string ids like "123-a") and freshly adjusted
      // times only exist there — originals stay as the fallback for filtered-out picks.
      rows.forEach((r) => { if (!r.space) byId.set(r.id, r); });
      // Supercut: when the toggle is on AND a search is active, each ticked clip
      // becomes one pick PER occurrence of the searched word, not the whole line.
      const searchTerm = (filterStore.getSearch(currentView) || "").trim().toLowerCase();
      const supercut = supercutRef.current && !!searchTerm;
      const picks = [];
      ids.forEach((id) => {
        const r = byId.get(id);
        // A row with no usable time (Charts / Cherry Pick / a malformed
        // specialty row) can't become a clip — skip it rather than place a
        // zero-length or wrong cut.
        if (!r || r.start_in == null || r.end_in == null) return;
        if (supercut) {
          // Only the searched word(s) from this clip, in spoken order. A clip
          // with no match contributes nothing (never the whole line).
          const occ = supercutOccurrences(r, searchTerm);
          occ.forEach((o, k) => {
            picks.push({
              id: `${r.id}-sc${k}`, order: picks.length + 1,
              start: o.start, end: o.end,
              sec: parseFloat(Math.max(0, o.end - o.start).toFixed(2)),
              trimmed: true, sentence: r.sentence, speaker: r.speaker,
              sentiment: r.sentiment, thumb: r.thumbnail || "",
              video: (r.source && r.source.video) || "",
              srcLabel: (r.source && r.source.label) || "",
            });
          });
          return;
        }
        // Effective clip bounds, matching the export EXACTLY (see makeMidia):
        // a pen-tool highlight (a phrase kept from inside the clip, with any
        // +/- breathing room) WINS over a manual ± adjustment, which wins over
        // the row's own times. Without the pen step here, highlighting part of
        // a clip trimmed the export but not the timeline.
        const pen = getPenTimecodes(id, r.utterance, r);
        const adj = adjustmentsRef.current[id];
        const eff = pen || adj || null;
        const start = eff ? eff.start_in : r.start_in;
        const end = eff ? eff.end_in : r.end_in;
        picks.push({
          id: r.id,
          order: picks.length + 1,
          start,
          end,
          sec: parseFloat(Math.max(0, end - start).toFixed(2)),
          trimmed: !!eff,
          sentence: r.sentence,
          speaker: r.speaker,
          sentiment: r.sentiment,
          thumb: r.thumbnail || "",
          // ARCHIVE: each pick carries ITS video so the cut player / export cut from the right file,
          // and its source's name so a stitched SRT can say which video each line came from.
          video: (r.source && r.source.video) || "",
          srcLabel: (r.source && r.source.label) || "",
        });
      });
      return picks;
    } catch (e) { return []; }
  };

  embedBroadcastRef.current = () => {
    // "*" in Premiere-panel mode (file:// parent — still reaches only the parent window),
    // the exact allowlisted origin otherwise. See utils/embedBridge.js.
    const postTarget = getEmbedPostTarget();
    const remoteToken = remoteDriverTokenRef.current;
    // Panel/embed mode broadcasts via postMessage; remote-driver (phone) mode POSTs to the relay.
    // If we're neither, there's nothing to do.
    if (!postTarget && !remoteToken) return;
    try {
      const picks = buildPicks();
      if (postTarget) {
        window.parent.postMessage(
          { type: "cc-et-picks", view: currentView, video: videoFile || "", picks },
          postTarget
        );
      }
      // Remote-driver (phone): push the desired pick-set to the relay, debounced so a fast
      // multi-tick coalesces into one POST. The desktop plugin polls this and drives Premiere.
      // Fire-and-forget; a failed push just leaves the timeline on the last accepted set.
      if (remoteToken && transcriptId) {
        remotePendingPicksRef.current = picks;
        if (remotePostTimerRef.current) clearTimeout(remotePostTimerRef.current);
        remotePostTimerRef.current = setTimeout(() => {
          apiRequest(livePicksSetUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              transcript_id: transcriptId,
              session_token: remoteToken,
              picks: remotePendingPicksRef.current || [],
            }),
          })
            .then((r) => setRemoteDriverStatus(r.ok ? "live" : "disconnected"))
            .catch(() => setRemoteDriverStatus("disconnected"));
        }, 300);
      }
    } catch (e) {
      /* ignore — the bridge must never break the ET */
    }
  };
  // OUT: rebroadcast on every selection change AND every trim of a selected clip.
  // Watching the selection alone missed pen-tool highlights and ± pad nudges made
  // to an already-ticked clip (and Black Cherry's precise trims, which highlight
  // in place) — the export honoured them but the timeline never heard. Serialise
  // the selection plus each selected clip's highlight state so the reaction re-
  // fires when either changes. (Manual ± on a non-highlighted clip already
  // rebroadcasts directly from onAdjustTimes; a fresh highlight fires here.)
  useEffect(() => {
    // Fire on selection change when EMBEDDED (panel/web parent) OR in remote-driver mode
    // (a standalone /s/…#cc_remote phone page, which has no embed parent but still needs to
    // POST its pick-set to the relay so the desktop plugin can drive Premiere).
    if (!getEmbedParentOrigin() && !getRemoteDriverToken()) return undefined;
    const dispose = reaction(
      () => {
        const ids = (filterStore.selectedSentences[currentView] || []);
        const hl = ids.map((id) => {
          const h = filterStore.highlights[id];
          return h
            ? `${id}:${h.start}-${h.end}:${h.padStart || 0}/${h.padEnd || 0}:${h.incBefore ? 1 : 0}${h.incAfter ? 1 : 0}`
            : "";
        }).join("|");
        // Include the search term so supercut re-expands as the word is refined.
        return ids.slice().join(",") + "#" + hl + "#" + (filterStore.getSearch(currentView) || "");
      },
      () => { if (embedBroadcastRef.current) embedBroadcastRef.current(); },
      { fireImmediately: true }
    );
    return dispose;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, originalRows, videoFile, filterStore]);
  // CEF (the Premiere panel) doesn't translate Magic Mouse / trackpad wheel
  // gestures into scrolling on overflow containers — the clip list wouldn't
  // scroll at all (programmatic scroll works; wheel events are ignored). Drive
  // it ourselves: walk up from the hovered element to the nearest scrollable
  // ancestor and move it manually. Panel mode only, both axes.
  useEffect(() => {
    if (getEmbedParentOrigin() !== "file://") return undefined;
    const onWheel = (e) => {
      const vertical = Math.abs(e.deltaY) >= Math.abs(e.deltaX);
      const delta = vertical ? e.deltaY : e.deltaX;
      if (!delta) return;
      let el = e.target;
      while (el && el !== document.body) {
        if (el.nodeType === 1) {
          const cs = window.getComputedStyle(el);
          const oy = cs.overflowY, ox = cs.overflowX;
          const canY = (oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 2;
          const canX = (ox === "auto" || ox === "scroll") && el.scrollWidth > el.clientWidth + 2;
          if (vertical && canY) { el.scrollTop += delta; e.preventDefault(); return; }
          if (!vertical && canX) { el.scrollLeft += delta; e.preventDefault(); return; }
          // Horizontal-only container (e.g. the toolbar): let ANY wheel gesture
          // — including a plain vertical mouse wheel — scroll it sideways.
          if (canX && !canY) { el.scrollLeft += (e.deltaX || e.deltaY); e.preventDefault(); return; }
        }
        el = el.parentElement;
      }
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);
  // CEF (the Premiere panel) has flaky pointer-capture, which breaks MUI
  // slider drags (e.g. Video Width). Wrap the calls so a capture failure
  // can't kill the drag gesture. Panel mode only.
  useEffect(() => {
    if (getEmbedParentOrigin() !== "file://") return;
    const proto = window.Element && Element.prototype;
    if (!proto || proto.__ccPointerShim) return;
    proto.__ccPointerShim = true;
    const origSet = proto.setPointerCapture;
    const origRel = proto.releasePointerCapture;
    proto.setPointerCapture = function (id) {
      try { if (origSet) origSet.call(this, id); } catch (e) { /* keep dragging */ }
    };
    proto.releasePointerCapture = function (id) {
      try { if (origRel) origRel.call(this, id); } catch (e) { /* keep dragging */ }
    };
  }, []);
  // OUT: one row-map broadcast per load, so the Premiere panel can generate
  // posters LOCALLY (the audio-only upload path has no server thumbnails —
  // the panel extracts frames from the local video file and sends them back
  // via the "thumbs" command below).
  useEffect(() => {
    const postTarget = getEmbedPostTarget();
    if (!postTarget || !originalRows.length) return;
    try {
      const rows = originalRows
        .filter((r) => !r.space)
        .map((r) => ({ id: r.id, start: r.start_in, end: r.end_in }));
      window.parent.postMessage({ type: "cc-et-rows", count: rows.length, rows }, postTarget);
    } catch (e) {
      /* the bridge must never break the ET */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalRows]);
  // IN: the command listener. Only the trusted parent window is obeyed
  // (allowlisted origin on the web; file:// + token echo in the Premiere panel).
  useEffect(() => {
    if (!getEmbedParentOrigin()) return undefined;
    const onCmd = (e) => {
      if (!isTrustedEmbedCommand(e)) return;
      const d = e.data;
      if (!d || d.type !== "cc-et-cmd") return;
      try {
        const validId = typeof d.id === "number" || typeof d.id === "string"; // split pieces have string ids ("123-a")
        if (d.cmd === "adjust" && validId) {
          // integer steps of 0.1s. A drag-trim on the parent timeline batches the whole
          // gesture into ONE command (one undo entry), |n| ≤ 600 steps = 60s of trim.
          const clampSteps = (n) => (Number.isFinite(n) ? Math.max(-600, Math.min(600, Math.trunc(n))) : 0);
          const dS = clampSteps(Number(d.deltaStart));
          const dE = clampSteps(Number(d.deltaEnd));
          if ((dS || dE) && embedAdjustRef.current) {
            embedAdjustRef.current({ id: d.id }, dS, dE);
            // adjustmentsRef is written inside the setRows updater — rebroadcast after it lands
            window.setTimeout(() => { if (embedBroadcastRef.current) embedBroadcastRef.current(); }, 90);
          }
        } else if (d.cmd === "reorder" && Array.isArray(d.ids)) {
          const current = new Set(filterStore.selectedSentences[currentView] || []);
          const ids = d.ids.filter((id) => current.has(id));
          if (ids.length === current.size) { // a pure permutation of the existing picks
            filterStore.resetSelectedSentences(currentView);
            ids.forEach((id) => filterStore.setSelectedSentences(id, currentView));
          }
        } else if (d.cmd === "select" && Array.isArray(d.ids)) {
          // External selection MIRROR (phone → relay → panel → here). Make THIS ET (the
          // one docked in Premiere) reflect the phone's ticks so there is a SINGLE source
          // of truth: set the selection to d.ids in order. The selection reaction (see
          // above) then rebroadcasts cc-et-picks, so placement flows through the normal
          // path AND the plugin panel visibly ticks. Before this, phone picks and local
          // picks were two competing sources and a local tick wiped the phone's clips.
          // NOTE: do NOT pre-validate ids against getDataForCurrentView() here — this
          // listener is registered with deps [currentView, filterStore] and would capture
          // a STALE rows closure (empty at first mount), filtering every id out. The
          // broadcast closure (reassigned every render) already skips ids with no row, so
          // setting the raw ids is safe and always fresh.
          // De-duped: an AI that cut one clip into pieces repeats its id in the relay, and the
          // panel (which places whole clips) must not tick — or place — it twice.
          const ids = [...new Set(d.ids)];
          filterStore.resetSelectedSentences(currentView);
          ids.forEach((id) => filterStore.setSelectedSentences(id, currentView));
          setSelectedCount((p) => ({ ...p, [currentView]: ids.length }));
        } else if (d.cmd === "deselect" && validId) {
          filterStore.removeSelectedSentence(d.id, currentView);
        } else if (d.cmd === "split" && validId && typeof d.t === "number") {
          if (embedSplitRef.current) embedSplitRef.current(d.id, d.t);
          window.setTimeout(() => { if (embedBroadcastRef.current) embedBroadcastRef.current(); }, 120);
        } else if (d.cmd === "thumbs" && d.map && typeof d.map === "object") {
          // Locally-generated posters from the Premiere panel. data:image URLs
          // only; merged the same way the clip-thumbnails poll merges — into
          // the clipThumbnails map, NEVER into rows/originalRows (that would
          // re-run the filter pipeline; see the background-thumbnails gotcha).
          const clean = {};
          Object.keys(d.map).forEach((k) => {
            const v = d.map[k];
            if (
              typeof v === "string" &&
              v.length < 500000 &&
              /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(v)
            ) {
              clean[k] = v;
            }
          });
          if (Object.keys(clean).length) {
            setClipThumbnails((prev) => ({ ...prev, ...clean }));
          }
        } else if (d.cmd === "view" && ["auto", "phone", "full"].indexOf(d.mode) !== -1) {
          // Panel-controlled layout: same as the on-screen Phone/Full toggle.
          setEtViewMode(d.mode);
        } else if (d.cmd === "etmode" && typeof d.index === "number") {
          // Phone-layout preset (Compact / Simple / Media).
          setEtModeIndex(d.index);
        } else if (d.cmd === "zoom" && typeof d.factor === "number") {
          // Panel-controlled zoom: lets the Premiere panel act like a view
          // dial — zoom out approximates the Full layout, zoom in the phone
          // layout (the responsive breakpoints see the zoomed width).
          const f = Math.max(0.5, Math.min(1.5, d.factor));
          document.documentElement.style.zoom = String(f);
        } else if (d.cmd === "supercut") {
          // Panel ✂ toggle: place only the searched word from each ticked clip.
          supercutRef.current = !!d.on;
          if (embedBroadcastRef.current) embedBroadcastRef.current();
        } else if (d.cmd === "supercutpad" && typeof d.ms === "number") {
          // Panel ✂ buffer: breathing room (ms) added to each supercut word.
          supercutPadRef.current = Math.max(0, d.ms) / 1000;
          if (embedBroadcastRef.current) embedBroadcastRef.current();
        } else if (d.cmd === "blackcherry") {
          // Embed (the marketing plugin page) asks us to open Black Cherry — the AI co-editor
          // panel. The toolbar registered its opener via setOpenBlackCherryRef; call the freshest
          // one through the ref so we never hit a stale closure.
          const openBC = openBlackCherryLatest.current;
          if (openBC) openBC();
        }
      } catch (err) {
        /* ignore — the bridge must never break the ET */
      }
    };
    window.addEventListener("message", onCmd);
    return () => window.removeEventListener("message", onCmd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, filterStore]);

  const AdjaceSpaces = (rows) => {
    let stack = [];
    for (let i = 0; i < rows.length; i++) {
      if (rows[i]["space"]) {
        stack.push(i); // Store array index instead of ID
      } else {
        rows[i]["related"] = stack;
        stack = [];
      }
    }
    return rows;
  };

  const availableFirstLetters = React.useMemo(() => {
    const letters = new Set();
    originalRows.forEach(r => {
      if (!r.space && r.sentence) {
        const first = r.sentence.trim()[0]?.toUpperCase();
        if (first && /[A-Z]/.test(first)) letters.add(first);
      }
    });
    return [...letters].sort();
  }, [originalRows]);

  const availableDurations = React.useMemo(() => {
    const secs = new Set();
    originalRows.forEach(r => {
      if (!r.space && r.sec_int !== undefined) {
        secs.add(r.sec_int <= 9 ? r.sec_int : 10);
      }
    });
    return [...secs].sort((a, b) => a - b);
  }, [originalRows]);

  const defaultExportTitle = React.useMemo(() => {
    const selectedIds = new Set(filterStore.getSelectedSentences(currentView));
    const text = rows
      .filter(r => selectedIds.has(r.id))
      .map(r => r.sentence || '')
      .join(' ')
      .trim();
    return text.length > 80 ? text.substring(0, 80) + '…' : text;
  }, [rows, currentView, selectedCount[currentView]]);

  const pickWordStarts = (r, edit) => {
    const utt = Array.isArray(r.utterance) ? r.utterance : [];
    const words = String(edit ?? r.sentence ?? '').split(/\s+/).filter(Boolean);
    if (!words.length || utt.length !== words.length) return null;
    const start = Number(r.start_in) || 0;
    const times = utt.map((w) => Number(Array.isArray(w) ? w[0] : NaN) - start);
    return times.every(Number.isFinite) ? times : null;
  };

  // The export window's live preview + its "Your cherry picks" strip: the ticked clips in the
  // SAME order the export cuts them (getSelectedSentences order — the worker keeps it).
  const exportPreview = React.useMemo(() => {
    const ids = filterStore.getSelectedSentences(currentView) || [];
    const byId = new Map(rows.filter(r => r && !r.space).map(r => [r.id, r]));
    const picks = ids
      .map((id) => byId.get(id))
      .filter(Boolean)
      .map((r) => ({
        id: r.id,
        thumbnail: viewClipThumbnails[r.id] || r.thumbnail || '',
        // The ET's ✏ edit wins (that is what the export burns); `edited` offers "back to what was said".
        text: editedSentences[r.id] ?? r.sentence ?? '',
        // The clip's nouns: Story Stack's key words (the worker reads the same Sentence.nouns)
        nouns: r.nouns || '',
        edited: editedSentences[r.id] !== undefined,
        speaker: r.speaker,
        start: Number(r.start_in) || 0,
        seconds: Math.max(0, (Number(r.end_in) || 0) - (Number(r.start_in) || 0)),
        // The clip's own video, and — for a split piece, whose poster is its parent's first
        // frame — where the window grabs a frame of its own.
        video: (r.source && r.source.video) || videoFile || '',
        frameAt: r._splitId !== undefined ? Number(r.start_in) || 0 : null,
        // When each word is said (seconds from the clip's start) — the preview's captions play
        // to them; null when the words on show aren't the heard ones word for word (an edit).
        wordStarts: pickWordStarts(r, editedSentences[r.id]),
      }));
    const first = picks[0];
    return {
      thumbnail: first ? first.thumbnail : '',
      text: first ? first.text : '',
      speaker: first ? first.speaker : '',
      start: first ? first.start : 0,
      count: picks.length,
      picks,
    };
  }, [rows, currentView, selectedCount[currentView], viewClipThumbnails, editedSentences, videoFile]);

  // The export window's ✏ Edit words: the SAME edit as the ET's ✏ (undoable), so the clip's text
  // changes in both places. null / blank / the heard words again → back to what was said.
  const editPickText = useCallback((id, text) => {
    undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)], editedSentences: { ...editedSentencesRef.current } });
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
    setEditedSentences((prev) => {
      const next = { ...prev };
      const row = (rows || []).find((r) => r && r.id === id);
      const clean = text === null || text === undefined ? '' : String(text).replace(/\s+/g, ' ').trim();
      if (!clean || (row && clean === String(row.sentence || '').trim())) delete next[id];
      else next[id] = clean;
      return next;
    });
  }, [rows, currentView]);

  useEffect(() => {
    if (!archive && transcriptId === null) {
      setLoading(false);
      navigate("/");
      return;
    }
    // New video: drop any background posters carried over from the previous transcript.
    setClipThumbnails({});
    setThumbnailsGenerating(false);
    thumbnailsPendingRef.current = false;

    // Everything that happens AFTER a transcript's rows are in memory — enrichment, spacer rows,
    // the progressive entity/highlight/moderation columns, filter bounds, speakers. Shared by the
    // single-transcript stream below AND by archive mode (which hands in several transcripts'
    // rows already merged), so the two paths can never drift apart. (`sentences` is reassigned
    // inside, exactly as the original inline code did.) The cross-referencing filters are
    // source-scoped: an entity from video 2 must never land on a sentence of video 1 just
    // because their timecodes overlap. sameSource() is a no-op for single-transcript rows.
    const ingestLoaded = ({ sentences, entity, category, aib, auto_highlight, content_moderation, metadata, video_file, video_duration }) => {
        if (sentences.length > 0) {
          // PROGRESSIVE RENDER (2026-07-21): the per-sentence enrichment used to cross-reference
          // every sentence against the FULL entity / auto_highlight / content_moderation arrays
          // in one synchronous .map() — O(sentences × others). On a big ET (thousands of clips)
          // that blocked the main thread for seconds with nothing painted: the "stuck at 90%"
          // freeze. Split it in two:
          //   Phase A — a cheap O(n) pass (order/sentiment/sec/contains_numbers) that has
          //             everything the main "by sentences" view needs. We paint the table from
          //             this immediately, so the ET opens in ~a second regardless of length.
          //   Phase B — the expensive entity/highlight/moderation columns, computed AFTER the
          //             table is on screen, in chunks that yield to the browser between batches
          //             so it never freezes. Those columns fill in "in front of" the user.
          // All rows still fully load — search/sort/filter are unaffected; only the paint order
          // changes. The enriched columns are extended-only, so the default view is complete at
          // first paint anyway.
          sentences.forEach((obj) => {
            obj["order"] = maxLines;
            obj["sentiment"] = sentClasses[obj["sentiment"]];
            obj["sec_int"] = parseInt(obj["sec"]);
            obj["sec_float"] = parseFloat(obj["sec"]);
            obj["related"] = null;
            obj["contains_numbers"] = /\d/.test(obj["sentence"] || '');
            // Placeholders so the columns are defined at first paint; Phase B fills them.
            if (obj["entities_text"] === undefined) obj["entities_text"] = '';
            if (obj["highlights_text"] === undefined) obj["highlights_text"] = '';
            if (obj["content_mod_labels"] === undefined) obj["content_mod_labels"] = '';
          });

          sentences = AdjaceSpaces(sentences);
          setOriginalRows(sentences);
          const rowsWithSpaces = sentences; // keep a handle for the Phase-B refresh
          adjustmentsRef.current = {};
          undoStackRef.current = [];
          redoStackRef.current = [];
          setCanUndo(false);
          setCanRedo(false);
          sentences = sentences.filter((obj) => !obj["space"]);
          // If any visible clip lacks a poster, background thumbnails are still coming —
          // arm the progressive-thumbnail poll (below) to fill them in without a reload.
          // In LAZY mode every row arrives poster-less by design, so the poll-all would sign
          // the whole transcript on the server every 4s — the exact burst we're avoiding.
          // Disable it; the viewport fetch (ensureClipThumbnails) handles big ETs instead.
          // AUDIO uploads have no server-side posters, BUT the Premiere plugin can push local
          // posters up (cloud-thumbnail sync). So we DO arm the poll for audio ETs missing posters
          // — the poll itself (below) is cautious for audio: no "creating…" badge until posters
          // actually arrive, and it gives up quickly if none do (so a plain audio upload with no
          // plugin doesn't hammer clip-thumbnails for 10 min or light a false badge). LAZY mode
          // stays off the poll entirely — its viewport fetch (ensureClipThumbnails) fills posters.
          const isAudioOnlyEt = AUDIO_ET_EXTS.has(
            String(metadata.ext || metadata.video || '').split('.').pop().toLowerCase()
          );
          isAudioEtRef.current = isAudioOnlyEt;
          thumbnailsPendingRef.current = lazyThumbnailsRef.current
            ? false
            : sentences.some((obj) => !obj["thumbnail"]);
          setRows(sentences);
          // Lazy ET: sign the posters for the first screenful right away so the top of the
          // list isn't all placeholders; scrolling triggers the rest via handleRangeChanged.
          if (lazyThumbnailsRef.current) ensureClipThumbnails(sentences.slice(0, 40));
          // calcDuration(sentences)
          resetFilters(sentences);
          indeterminateCheck(sentences);
          const speakers = getDimension("speaker", sentences);
          filterStore.setSpeakers(speakers, CREATE_VIDEO);
          filterStore.setSpeakers(speakers, CHART);
          filterStore.setSpeakers(speakers, CHERRY_PICK);

          // Phase B — enrich the cross-referenced columns without blocking the main thread.
          // Mutates the shared row objects in chunks, yielding between each so the browser keeps
          // painting/scrolling; when done, bumps originalRows + rows by reference so the columns
          // surface. Only worth deferring when there's something to cross-reference AND enough
          // rows to matter — tiny ETs just do it inline on the next tick.
          const enrichRows = sentences; // visible (non-space) rows; same objects as originalRows
          const hasAux = entity.length || auto_highlight.length || content_moderation.length;
          if (hasAux && enrichRows.length) {
            const CHUNK = 250;
            const enrichChunk = (start) => {
              // Bail if the user navigated to another transcript mid-enrichment.
              if (transcriptIdRef.current !== transcriptId) return;
              const end = Math.min(start + CHUNK, enrichRows.length);
              for (let i = start; i < end; i++) {
                const obj = enrichRows[i];
                const sStart = obj["start_in"];
                const sEnd = obj["end_in"];
                obj["entities_text"] = [...new Set(
                  entity
                    .filter(e => sameSource(e, obj) && e.start_in < sEnd && e.end_in > sStart && e.sentence && e.entity_type)
                    .map(e => `${e.sentence} (${e.entity_type.toLowerCase()})`)
                )].sort().join(', ');
                obj["highlights_text"] = [...new Set(
                  auto_highlight
                    .filter(h => sameSource(h, obj) && h.start_in < sEnd && h.end_in > sStart && h.sentence)
                    .map(h => h.sentence)
                )].sort().join(', ');
                const sentText = (obj.sentence || '').trim().toLowerCase();
                obj["content_mod_labels"] = [...new Set(
                  content_moderation
                    .filter(m => sameSource(m, obj) && m.label && m.sentence && sentText && m.sentence.toLowerCase().includes(sentText))
                    .map(m => m.label)
                )].sort().join(', ');
              }
              if (end < enrichRows.length) {
                setTimeout(() => enrichChunk(end), 0);
              } else {
                // Columns are populated on the shared objects — bump both arrays by reference so
                // the table and the memos keyed on originalRows pick them up (no filter re-run).
                setOriginalRows([...rowsWithSpaces]);
                setRows((prev) => [...prev]);
                setEnrichesDone((n) => n + 1);
              }
            };
            setTimeout(() => enrichChunk(0), 0);
          } else {
            setEnrichesDone((n) => n + 1);
          }
        }

        setOriginalEntities(makeOrder(entity));
        setOriginalCategories(makeOrder(category));
        setOriginalAIB(makeOrder(aib));
        setOriginalAutoHighlight(makeOrder(auto_highlight));
        setOriginalContentModeration(makeOrder(content_moderation));

        setLoadingProgress(100);
        setLoading(false);
        setVideoFile(video_file);
        userStore.setTotalDuration(durationFormat(video_duration * 1000));
    };

    if (archive) {
      // ARCHIVE: rows arrive pre-merged (namespaced ids, source tags). CLONE them — never mutate
      // the prop: React can run this effect twice, and the July build double-mapped sentiment
      // ("0" → "positive" → undefined) by enriching the same objects again. Speaker identities
      // (the cross-video "same person" map) are applied to the clones before ingest.
      const clone = (arr) => (arr || []).map((r) => ({ ...r }));
      const map = archive.speakerMap || {};
      const sentences = clone(archiveRows);
      applySpeakerIdentities(sentences, map);
      const aux = {
        entity: clone(archive.entity), category: clone(archive.category), aib: clone(archive.aib),
        auto_highlight: clone(archive.auto_highlight), content_moderation: clone(archive.content_moderation),
      };
      Object.values(aux).forEach((arr) => applySpeakerIdentities(arr, map));
      const sources = archive.sources || [];
      const allAudio = sources.length > 0 && sources.every((src) => AUDIO_ET_EXTS.has(String(src.ext || "").toLowerCase()));
      lazyThumbnailsRef.current = !!archive.lazy;
      setVideoExt(allAudio ? "mp3" : "mp4");
      hiddenSourcesRef.current = archive.hiddenSources || new Set();
      ingestLoaded({
        sentences, ...aux,
        metadata: { ext: allAudio ? "mp3" : "mp4", video: "", lazy_thumbnails: !!archive.lazy },
        video_file: "", video_duration: archive.totalDuration || 0,
      });
      archiveIngestedRef.current = true;
      Promise.resolve().then(() => filterAll());
      return;
    }

    const fetchDataStream = async (attempt = 1) => {
      const MAX_ATTEMPTS = 3;
      setLoading(true);
      try {
        const response = await apiRequest(
          `${getSentencesStreamUrl}${transcriptId}/`,
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        setLoadingProgress(0);
        const reader = response.body.getReader();
        let buffer = "";
        let metadata = null;
        let streamComplete = false; // set when the backend's __complete__ sentinel arrives
        let sentences = [];
        let entity = [];
        let category = [];
        let aib = [];
        let auto_highlight = [];
        let content_moderation = [];
        let rowsProcessed = 0; // Track processed rows for gzip-safe progress
        let lastReportedProgress = 0; // Track last reported progress to throttle updates
        let video_duration = 0;
        let video_file = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Convert the chunk to text and add to buffer
          buffer += new TextDecoder().decode(value);

          // Process complete lines
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep the incomplete line in buffer

          for (const line of lines) {
            if (!line) continue;
            let data;
            try { data = JSON.parse(line); } catch (e) { continue; }

            // Completion sentinel — the backend's last line, sent only after a full stream.
            if (data.type === "__complete__") { streamComplete = true; continue; }

            // Handle metadata (first line)
            if (!metadata && data.total_rows) {
              metadata = data;
              video_duration = metadata.video_duration;
              video_file = metadata.video;
              setVideoExt(metadata.ext);
              // Big ET: backend sent rows without signed posters — sign them per-viewport.
              lazyThumbnailsRef.current = !!metadata.lazy_thumbnails;
              continue;
            }

            // Update progress using row count (gzip-safe)
            if (!metadata?.total_rows) continue;

            rowsProcessed++;
            const progress = Math.round(
              (rowsProcessed / metadata.total_rows) * 100,
            );

            // Only update React state occasionally for the Loading component
            if (progress - lastReportedProgress >= 10 || progress === 100) {
              setLoadingProgress(progress);
              lastReportedProgress = progress;
            }

            // Process data based on type
            switch (data.type) {
              case "sentence":
                if (!data.data) continue;
                sentences.push(data.data);
                break;
              case "entity":
                entity.push(data.data);
                break;
              case "category":
                category.push(data.data);
                break;
              case "aib":
                aib.push(data.data);
                break;
              case "auto_highlight":
                auto_highlight.push(data.data);
                break;
              case "content_moderation":
                content_moderation.push(data.data);
                break;
              default:
            }
          }
        }

        // A stream can close "cleanly" yet be cut short (deploy restart, proxy/idle timeout,
        // network blip), which would silently render a PARTIAL transcript — e.g. the Categories
        // view stopping at 9/35 min while the data is all in the DB. Trust the sentinel; fall
        // back to a full row count. If incomplete, retry the whole load before showing anything.
        const loadIncomplete = !streamComplete && (!metadata || rowsProcessed < (metadata.total_rows || 0));
        if (loadIncomplete && attempt < MAX_ATTEMPTS) {
          messageStore.setMessage(`Connection dropped while loading — retrying (${attempt + 1}/${MAX_ATTEMPTS})…`, "info", 4000);
          return await fetchDataStream(attempt + 1);
        }
        if (loadIncomplete) {
          messageStore.setMessage("Couldn’t load the full transcript — the connection kept dropping. Showing what loaded; please reload to get the rest.", "error", 14000);
        }

        ingestLoaded({ sentences, entity, category, aib, auto_highlight, content_moderation, metadata, video_file, video_duration });
      } catch (error) {
        console.error("Error fetching transcript data:", error);
        // A connection dropped mid-stream throws here — retry before giving up.
        if (attempt < MAX_ATTEMPTS) {
          messageStore.setMessage(`Connection dropped while loading — retrying (${attempt + 1}/${MAX_ATTEMPTS})…`, "info", 4000);
          return await fetchDataStream(attempt + 1);
        }
        messageStore.setMessage("Failed to load transcript data.", "error");
        setLoading(false);
      }
    };

    // Call the fetchDataStream function
    fetchDataStream()
      .then(() => {
        filterAll();
        setStreamsDone((n) => n + 1);
      })
      .catch((error) => {
        console.error("Error in fetchDataStream:", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptId, archiveRows]); // ARCHIVE: re-ingest when a different set of sheets arrives

  // ARCHIVE: re-apply speaker identities when the map changes (link / unlink / rename) and
  // re-filter when a source is shown or hidden — WITHOUT re-ingesting, so ticks, pen trims and
  // filters all survive. The row objects are shared by originalRows and rows, so mutating
  // `speaker` on them and bumping the array reference is enough for the table to repaint.
  const archiveSpeakerMap = archive ? archive.speakerMap : null;
  const archiveHidden = archive ? archive.hiddenSources : null;
  useEffect(() => {
    if (!archive || !archiveIngestedRef.current) return;
    const map = archiveSpeakerMap || {};
    let changed = 0;
    [originalRows, originalEntities, originalCategories, originalAIB, originalAutoHighlight, originalContentModeration]
      .forEach((list) => { changed += applySpeakerIdentities(list, map); });
    if (!changed) return;
    const speakers = getDimension("speaker", originalRows.filter((r) => !r.space));
    filterStore.setSpeakers(speakers, CREATE_VIDEO);
    filterStore.setSpeakers(speakers, CHART);
    filterStore.setSpeakers(speakers, CHERRY_PICK);
    setOriginalRows((prev) => [...prev]);
    filterAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveSpeakerMap]);
  useEffect(() => {
    if (!archive) return;
    hiddenSourcesRef.current = archiveHidden || new Set();
    if (archiveIngestedRef.current) filterAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveHidden]);

  // Progressive thumbnails (2026-07-08): the ET now opens BEFORE the worker has generated
  // per-clip poster frames (they moved off the critical path to a background task). Once the
  // ET has finished loading, poll the lightweight clip-thumbnails endpoint and merge posters
  // into the clipThumbnails map (ItemContent reads it by clip id). Kept OUT of rows/originalRows
  // on purpose: filling posters in must NOT re-run the filter pipeline (that resets the user's
  // Seconds/Length filters and can stop a playing clip). The <video> is keyed by data.id so it
  // isn't remounted either. Uses polling (the app's proven pattern; the SSE hook can't carry the
  // auth token). Stops only once thumbnails have actually STARTED arriving and then plateaued —
  // so a worker that hasn't begun producing yet (the long-video case this feature targets) is
  // never abandoned early — or at a hard attempt cap.
  useEffect(() => {
    if (!transcriptId || loading) return;        // wait until the ET has finished loading
    if (!thumbnailsPendingRef.current) return;   // nothing missing → skip polling entirely
    // Server video path: posters ARE coming, so show the "creating…" badge right away. Audio
    // path (plugin cloud-sync): posters only MIGHT come, so stay quiet until we actually see one.
    const isAudioEt = isAudioEtRef.current;
    if (!isAudioEt) setThumbnailsGenerating(true);
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    let lastCount = -1;
    let stable = 0;
    let sawAny = false;
    const INTERVAL = 4000;
    const MAX_ATTEMPTS = 150; // hard cap ~10 min (covers a backed-up worker that starts late)
    const STABLE_STOP = 5;    // stop ~20s after thumbnails stop arriving
    // Audio ET with no posters arriving = a plain audio upload (no plugin pushing). Give up fast
    // (~40s) so we don't poll for 10 min or light a badge for posters that will never come.
    const AUDIO_NO_RESULT_STOP = 10;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await apiRequest(`${clipThumbnailsUrl}${transcriptId}/`, { method: "GET" });
        const data = await res.json();
        const map = (data && data.thumbnails) || {};
        const count = data && typeof data.count === "number" ? data.count : Object.keys(map).length;
        if (!cancelled && Object.keys(map).length) {
          setClipThumbnails((prev) => ({ ...prev, ...map }));
        }
        if (count > 0 && !sawAny) {
          sawAny = true;
          // Posters have started landing (plugin push, or a late worker) — now show the badge.
          if (isAudioEt && !cancelled) setThumbnailsGenerating(true);
        } else if (count > 0) {
          sawAny = true;
        }
        stable = count === lastCount ? stable + 1 : 0;
        lastCount = count;
      } catch (e) {
        // transient (network blip / 401 in a share without token) — keep trying until the cap
      }
      if (cancelled) return;
      // Stop when: thumbnails STARTED then plateaued, the hard cap is hit, or (audio only) nothing
      // has arrived within the short probe window.
      if (attempts >= MAX_ATTEMPTS
          || (sawAny && stable >= STABLE_STOP)
          || (isAudioEt && !sawAny && attempts >= AUDIO_NO_RESULT_STOP)) {
        setThumbnailsGenerating(false);
        return;
      }
      timer = setTimeout(poll, INTERVAL);
    };

    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); setThumbnailsGenerating(false); };
  }, [transcriptId, loading]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleToolbarPositionChange = (newPosition) =>
    setToolbarPosition(newPosition);

  const durationFormat = (duration) =>
    new Date(duration)
      .toISOString()
      .slice(11, precision[currentView] ? -1 : -5);

  const calcDuration = (data) => {
    // Use the order field to identify selected items (items with order < maxLines are selected)
    //calculate the duration of the selected items this is the order != maxLines
    // Add safety check to filter out undefined/null objects
    const validData = data.filter(
      (obj) =>
        obj && obj.hasOwnProperty("order") && obj.hasOwnProperty("duration_ms"),
    );
    const durationSelected = validData.filter(
      (obj) => obj["order"] !== maxLines,
    );

    const durationSelectedMs = durationSelected.reduce(
      (a, b) => a + b["duration_ms"],
      0,
    );
    const durationAllMs = validData.reduce((a, b) => a + b["duration_ms"], 0);
    userStore.setDurationInMs(durationSelectedMs);
    userStore.setDuration(durationFormat(durationSelectedMs));
    userStore.setTotalDuration(durationFormat(durationAllMs));
  };

  const onQuickFilterChange = (key, value = null) => {
    setQuickFilters((prev) => {
      let next;
      if (key === 'firstLetter') {
        next = { ...prev, firstLetter: prev.firstLetter === value ? null : value };
      } else if (key === 'durationSec') {
        next = { ...prev, durationSec: prev.durationSec === value ? null : value };
      } else if (key === 'entityType') {
        const arr = prev.entityTypes || [];
        next = { ...prev, entityTypes: arr.includes(value) ? arr.filter(t => t !== value) : [...arr, value] };
      } else if (key === 'contentFlag') {
        const arr = prev.contentFlags || [];
        next = { ...prev, contentFlags: arr.includes(value) ? arr.filter(f => f !== value) : [...arr, value] };
      } else if (key === 'keyPhrase') {
        const arr = prev.keyPhrases || [];
        next = { ...prev, keyPhrases: arr.includes(value) ? arr.filter(p => p !== value) : [...arr, value] };
      } else if (key === 'filterVerb') {
        const arr = prev.filterVerbs || [];
        next = { ...prev, filterVerbs: arr.includes(value) ? arr.filter(w => w !== value) : [...arr, value] };
      } else if (key === 'filterNoun') {
        const arr = prev.filterNouns || [];
        next = { ...prev, filterNouns: arr.includes(value) ? arr.filter(w => w !== value) : [...arr, value] };
      } else if (key === 'filterAdjective') {
        const arr = prev.filterAdjectives || [];
        next = { ...prev, filterAdjectives: arr.includes(value) ? arr.filter(w => w !== value) : [...arr, value] };
      } else {
        next = { ...prev, [key]: !prev[key] };
      }
      shouldStopVideo();
      let filterRows = [...getDataForCurrentView(currentView)];
      filterRows = filterSearch(filterRows);
      filterRows = filterSource(filterRows);
      filterRows = filterSentiment(filterRows);
      filterRows = filterSpeakers(filterRows);
      filterRows = filterSeconds(filterRows);
      filterRows = filterTextLen(filterRows);
      filterRows = applyQuickFilters(filterRows, next);
      setRows(filterRows);
      calcDuration(filterRows);
      return next;
    });
  };

  const COLUMN_SETS = ['standard', 'extended', 'media', 'cbd', 'ayy', 'pillars'];
  const COLUMN_SET_LABELS = {
    standard:  'Actions · Desc · Things',
    extended:  'Entities · Highlights · Flags',
    media:     'Sound · Visuals · Camera',
    cbd:       'Chachma · Bina · Daas',
    ayy:       'Avraham · Yitzchak · Yaakov',
    pillars:   'Torah · Avoda · Gimilat Hasidim',
  };
  const COLUMN_SET_FIELDS = {
    standard:  ['verbs', 'adjectives', 'nouns'],
    extended:  ['entities_text', 'highlights_text', 'content_mod_labels'],
    media:     ['sound_effects', 'visuals_detected', 'camera_shot'],
    cbd:       ['chachma', 'bina', 'daas'],
    ayy:       ['avraham', 'yitzchak', 'yaakov'],
    pillars:   ['torah', 'avoda', 'gimilat_hasidim'],
  };
  const allSetFields = Object.values(COLUMN_SET_FIELDS).flat();

  const parseEntityTypes = (text) => {
    if (!text) return [];
    return [...text.matchAll(/\(([^)]+)\)/g)].map(m => m[1].toLowerCase().trim());
  };
  const parseWords = (text) => {
    if (!text) return [];
    return text.split(/[,\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
  };

  const availableEntityTypes = useMemo(() => {
    const types = new Set();
    originalRows.forEach(r => parseEntityTypes(r.entities_text).forEach(t => types.add(t)));
    return [...types].sort();
  }, [originalRows]);

  const availableContentFlags = useMemo(() => {
    const flags = new Set();
    originalRows.forEach(r => parseWords(r.content_mod_labels).forEach(f => flags.add(f)));
    return [...flags].sort();
  }, [originalRows]);

  const availableKeyPhrases = useMemo(() => {
    const phrases = new Set();
    originalRows.forEach(r => {
      if (!r.highlights_text) return;
      r.highlights_text.split(',').map(s => s.trim()).filter(Boolean).forEach(p => phrases.add(p));
    });
    return [...phrases].sort();
  }, [originalRows]);

  const availableVerbs = useMemo(() => {
    const words = new Set();
    originalRows.forEach(r => parseWords(r.verbs).forEach(w => words.add(w)));
    return [...words].sort();
  }, [originalRows]);

  const availableNouns = useMemo(() => {
    const words = new Set();
    originalRows.forEach(r => parseWords(r.nouns).forEach(w => words.add(w)));
    return [...words].sort();
  }, [originalRows]);

  const availableAdjectives = useMemo(() => {
    const words = new Set();
    originalRows.forEach(r => parseWords(r.adjectives).forEach(w => words.add(w)));
    return [...words].sort();
  }, [originalRows]);

  const toggleColumnSet = () => {
    const idx = COLUMN_SETS.indexOf(columnSet);
    const next = COLUMN_SETS[(idx + 1) % COLUMN_SETS.length];
    setColumnSet(next);
    const nextFields = COLUMN_SET_FIELDS[next];
    // Q? visible on standard/media/cbd/ayy/pillars; Numbers visible on extended
    setHeader((prev) =>
      prev.map((col) => {
        if (allSetFields.includes(col.field)) {
          return { ...col, visible: nextFields.includes(col.field) };
        }
        if (col.field === 'question')         return { ...col, visible: next !== 'extended' };
        if (col.field === 'contains_numbers') return { ...col, visible: next === 'extended' };
        return col;
      })
    );
  };

  const applyQuickFilters = (filterRows, qf = quickFilters) => {
    let result = filterRows;
    if (qf.selectedOnly) {
      const selectedIds = new Set(filterStore.getSelectedSentences(currentView));
      result = result.filter((r) => r.space || selectedIds.has(r.id));
    }
    if (qf.hasQuestion) {
      result = result.filter((r) => r.space || (r.sentence && r.sentence.includes("?")));
    }
    if (qf.firstLetter) {
      const letter = qf.firstLetter.toUpperCase();
      result = result.filter((r) => r.space || (r.sentence && r.sentence.trim().toUpperCase().startsWith(letter)));
    }
    if (qf.durationSec !== null) {
      if (qf.durationSec === 10) {
        result = result.filter((r) => r.space || r.sec_int >= 10);
      } else {
        result = result.filter((r) => r.space || r.sec_int === qf.durationSec);
      }
    }
    if (qf.containsNumbers) {
      result = result.filter((r) => r.sentence && /\d/.test(r.sentence));
    }
    if (qf.entityTypes && qf.entityTypes.length > 0) {
      result = result.filter((r) => {
        const types = parseEntityTypes(r.entities_text);
        return qf.entityTypes.some(t => types.includes(t));
      });
    }
    if (qf.contentFlags && qf.contentFlags.length > 0) {
      result = result.filter((r) => {
        const flags = parseWords(r.content_mod_labels);
        return qf.contentFlags.some(f => flags.includes(f));
      });
    }
    if (qf.keyPhrases && qf.keyPhrases.length > 0) {
      result = result.filter((r) => {
        const phrases = r.highlights_text ? r.highlights_text.split(',').map(s => s.trim()).filter(Boolean) : [];
        return qf.keyPhrases.some(p => phrases.includes(p));
      });
    }
    if (qf.filterVerbs && qf.filterVerbs.length > 0) {
      result = result.filter((r) => {
        const words = parseWords(r.verbs);
        return qf.filterVerbs.some(w => words.includes(w));
      });
    }
    if (qf.filterNouns && qf.filterNouns.length > 0) {
      result = result.filter((r) => {
        const words = parseWords(r.nouns);
        return qf.filterNouns.some(w => words.includes(w));
      });
    }
    if (qf.filterAdjectives && qf.filterAdjectives.length > 0) {
      result = result.filter((r) => {
        const words = parseWords(r.adjectives);
        return qf.filterAdjectives.some(w => words.includes(w));
      });
    }
    return result;
  };

  // Maps a sort token (from the toolbar buttons OR a column-header field) to the
  // column its indicator shows on, plus the ascending/descending wording. Both sort
  // paths funnel through buildSort so the header indicator is always consistent.
  const SORT_META = {
    duration:      { column: 'sec',      asc: 'short→long', desc: 'long→short' },
    sec:           { column: 'sec',      asc: 'short→long', desc: 'long→short' },
    length:        { column: 'length',   asc: 'few→many',   desc: 'many→few' },
    alphabetical:  { column: 'sentence', asc: 'A→Z',        desc: 'Z→A' },
    sentence:      { column: 'sentence', asc: 'A→Z',        desc: 'Z→A' },
    chronological: { column: 'sentence', asc: 'start → end', desc: 'end → start' },
    virality:      { column: 'virality_score', asc: 'low→high', desc: 'high→low' },
  };
  const buildSort = (token, asc) => {
    const m = SORT_META[token] || { column: token, asc: 'ascending', desc: 'descending' };
    return { field: token, direction: asc ? 'asc' : 'desc', column: m.column, label: asc ? m.asc : m.desc };
  };

  const onSortOrderChange = (field) => {
    shouldStopVideo();
    const newDirection = sortOrder.field === field ? (sortOrder.direction === 'asc' ? 'desc' : 'asc') : 'asc';
    const asc = newDirection === 'asc';
    setSortOrder(buildSort(field, asc));
    const dataToSort = [...rows];
    if (field === 'chronological') {
      dataToSort.sort((a, b) => asc ? (a.start_in || 0) - (b.start_in || 0) : (b.start_in || 0) - (a.start_in || 0));
    } else if (field === 'alphabetical') {
      dataToSort.sort((a, b) => {
        const cmp = (a.sentence || '').localeCompare(b.sentence || '');
        return asc ? cmp : -cmp;
      });
    } else if (field === 'duration') {
      dataToSort.sort((a, b) => asc ? (a.sec_float || 0) - (b.sec_float || 0) : (b.sec_float || 0) - (a.sec_float || 0));
    } else if (field === 'virality') {
      dataToSort.sort((a, b) => asc ? (a.virality_score || 0) - (b.virality_score || 0) : (b.virality_score || 0) - (a.virality_score || 0));
    }
    setRows(dataToSort);
  };

  const onSpacesModeChange = (mode) => {
    shouldStopVideo();
    // Remember the clip currently at the top so that adding/removing space rows
    // doesn't fling the view somewhere else when the row count changes.
    const anchor = rows[firstVisibleIndex] || rows[0];
    const anchorId = anchor?.id;
    const anchorStart = anchor?.start_in;

    setSpacesMode(mode);
    filterStore.setSpacesTo(currentView, mode !== 'hide');
    if (mode === 'only') {
      const spaceRows = originalRows.filter(r => r.space === true);
      setRows(spaceRows);
      calcDuration(spaceRows);
    } else {
      filterAll();
    }

    // After the list re-renders with the new row set, scroll back so the same clip
    // (or the nearest one by time) stays put instead of jumping.
    setTimeout(() => {
      const data = rowsRef.current;
      if (!data || !data.length || anchorStart == null) return;
      let idx = anchorId != null ? data.findIndex((r) => r.id === anchorId) : -1;
      if (idx === -1) {
        let best = -1, bd = Infinity;
        data.forEach((r, i) => { const d = Math.abs((r.start_in || 0) - anchorStart); if (d < bd) { bd = d; best = i; } });
        idx = best;
      }
      if (idx !== -1 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({ index: idx, align: 'start' });
      }
    }, 80);
  };

  // Build the visible list by running the full filter pipeline. Returns null when there's
  // no data. Pulled out of filterAll so other actions (e.g. virality scoring) can get the
  // filtered list synchronously and sort it before painting, instead of racing setRows.
  const buildFilteredRows = () => {
    const fullList = [...getDataForCurrentView(currentView)];
    if (fullList.length === 0) return null;

    let filterRows = fullList;
    filterRows = filterSearch(filterRows);
    filterRows = filterSource(filterRows);
    filterRows = filterSentiment(filterRows);
    filterRows = filterSpeakers(filterRows);
    filterRows = filterSeconds(filterRows);
    filterRows = filterTextLen(filterRows);
    filterRows = applyQuickFilters(filterRows);
    filterRows = applyContext(fullList, filterRows); // context window around ALL matches
    filterRows = applyFiller(filterRows);            // AI filler: dim / hide / only
    filterRows = applyVirality(filterRows);          // AI virality: inject the score column
    filterRows = filterVirality(filterRows);         // AI virality: range slider (e.g. 70+)
    return filterRows;
  };

  // Keep only clips whose virality score is in the chosen range (reads the ref by id, so
  // it's order-independent). No-op until a scoring pass has run or when the range is full.
  const filterVirality = (filterRows) => {
    if (!viralityScoresRef.current.size) return filterRows;
    const [min, max] = viralityRangeRef.current;
    if (min <= 0 && max >= 100) return filterRows;
    return filterRows.filter((r) => {
      if (r.space || r._searchContext) return true;
      // applyVirality (run just before this) already computed r.virality_score — including the
      // averaged value for re-segmented clips — so read that, not the raw by-id lookup.
      const s = r.virality_score;
      return s != null && s >= min && s <= max;
    });
  };

  const onViralityRangeChange = (vals) => {
    shouldStopVideo();
    viralityRangeRef.current = vals; // ref first so filterAll reads the new range now
    setViralityRange(vals);
    filterAll();
  };

  const filterAll = () => {
    shouldStopVideo();
    const filterRows = buildFilteredRows();
    if (filterRows === null) return;
    setRows(filterRows);
    calcDuration(filterRows);
  };

  // Inject the AI virality score onto each visible row so the "Viral" column and the virality
  // sort have a value to read. No-op until a scoring pass has run. Scores are only ever computed
  // ONCE, at the original sentence level; when clips are re-segmented we DERIVE each new clip's
  // score from its source sentences (no re-scoring, no AI calls) — a merged/interval clip is the
  // AVERAGE of its sentences, a single word inherits its sentence's score.
  const applyVirality = (filterRows) => {
    if (!viralityScoresRef.current.size) return filterRows;
    const scores = viralityScoresRef.current;
    const avgOf = (ids) => {
      const vals = ids.map((id) => scores.get(id)).filter((v) => v != null);
      if (!vals.length) return null;
      return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    };
    return filterRows.map((r) => {
      let score;
      if (r._mergedIds && r._mergedIds.length) score = avgOf(r._mergedIds);       // custom merge → average
      else if (r._wordOfId != null) score = scores.get(r._wordOfId) ?? null;       // word → inherit its sentence
      else if (r._srcIds && r._srcIds.length) score = avgOf(r._srcIds);            // N-second slice → average of overlapped sentences
      else score = scores.get(r.id) ?? r.virality_score ?? null;                   // plain sentence → direct
      return { ...r, virality_score: score };
    });
  };

  // FILLER pass. 'off' or not-yet-classified → no-op. 'hide' drops filler clips, 'only'
  // keeps just them (the filler montage), 'dim' keeps everything but tags filler rows so
  // ItemContent fades them and brightens the keepers. Spaces/context rows are never hidden.
  const applyFiller = (filterRows) => {
    const mode = fillerModeRef.current;
    if (mode === 'off' || !fillerIdsRef.current.size) return filterRows;
    const isFiller = (r) => fillerIdsRef.current.has(r.id);
    if (mode === 'hide') return filterRows.filter((r) => r.space || r._searchContext || !isFiller(r));
    if (mode === 'only') return filterRows.filter((r) => isFiller(r));
    // 'dim' — tag each row so the renderer fades filler and lifts the keepers
    return filterRows.map((r) => (isFiller(r) ? { ...r, _fillerDim: true } : { ...r, _fillerKeep: true }));
  };

  // Toolbar Dim/Hide/Only toggle (mirrors onSpacesModeChange). Clicking the active mode
  // turns it back off so the list returns to normal.
  const onFillerModeChange = (mode) => {
    shouldStopVideo();
    const next = fillerModeRef.current === mode ? 'off' : mode; // click the active mode again → off
    fillerModeRef.current = next; // update the ref FIRST so filterAll reads the right mode now
    setFillerMode(next);          // then the state, purely to repaint the buttons
    filterAll();
  };

  // Apply the AI's filler verdict — a json array of clip ids (or {id} objects). Stash them
  // in the ref, flip to Dim by default so the user instantly sees the effect, and refilter.
  const applyFillerIds = (text) => {
    shouldStopVideo(); // stop any playing clip BEFORE the list changes (avoids an orphaned, unstoppable video)
    const idMap = new Map(aiSourceRows().filter((r) => !r.space).map((r) => [String(r.id), r]));
    let items = null;
    const fenced = (text || '').match(/```(?:json)?\s*(\[[\s\S]*?\])\s*```/i);
    const bareArray = (text || '').match(/\[[\s\S]*\]/);
    const jsonStr = fenced ? fenced[1] : (bareArray ? bareArray[0] : null);
    if (jsonStr) {
      try {
        const parsed = JSON.parse(jsonStr);
        if (Array.isArray(parsed)) items = parsed.map((x) => (x && typeof x === 'object' ? x : { id: x }));
      } catch (e) { /* not json */ }
    }
    if (!items) {
      const tokens = (text || '').match(/[A-Za-z0-9_-]+/g) || [];
      items = tokens.map((t) => ({ id: t }));
    }
    const ids = new Set();
    for (const it of items) {
      const idStr = String(it.id);
      if (idMap.has(idStr)) ids.add(idMap.get(idStr).id);
    }
    fillerIdsRef.current = ids;
    try { localStorage.setItem(`cc_filler_${persistKey}`, JSON.stringify([...ids])); } catch (e) { /* ignore */ }
    fillerModeRef.current = 'dim'; // start in Dim so the fade is visible immediately
    setFillerDone(true);
    setFillerMode('dim');
    filterAll();
    return ids.size;
  };

  // Apply the AI's virality verdict — a json array of {id, score}. Store the scores by id,
  // reveal the Viral column (rotating out Q?), then rebuild the list and sort high→low.
  const applyViralityScores = (text) => {
    shouldStopVideo(); // stop any playing clip BEFORE we re-sort, or it gets orphaned (plays forever, can't pause)
    const idMap = new Map(aiSourceRows().filter((r) => !r.space).map((r) => [String(r.id), r]));
    const scores = new Map();
    // Tolerant parse: pull every "id:score" pair from the reply. Survives a truncated reply
    // on big videos (we keep whatever scores came through) AND still handles a JSON-ish reply.
    // Ids may be bare numbers or ARCHIVE ids like 6_456 ("6_456:88"). Same tolerance either way.
    const re = /([A-Za-z0-9_-]+)\s*[:=]\s*(\d{1,3})\b/g;
    let m;
    while ((m = re.exec(text || '')) !== null) {
      const row = idMap.get(String(m[1]));
      if (!row) continue;
      const s = Number(m[2]);
      if (!isFinite(s)) continue;
      scores.set(row.id, Math.max(0, Math.min(100, Math.round(s))));
    }
    if (!scores.size) return 0;
    viralityScoresRef.current = scores;
    try { localStorage.setItem(`cc_virality_${persistKey}`, JSON.stringify([...scores])); } catch (e) { /* ignore */ }
    setViralityDone(true);
    // Reveal the Viral column in BOTH the Sentence AND Categories headers (the two views that
    // score), rotating out Q? where it exists. Categories uses a separate header state.
    const revealViral = (prev) => prev.map((col) =>
      col.field === 'virality_score' ? { ...col, visible: true }
        : col.field === 'question' ? { ...col, visible: false } : col);
    setHeader(revealViral);
    setHeaderCategories(revealViral);
    // Rebuild with scores injected, then sort high→low so the bangers rise to the top.
    const built = buildFilteredRows() || [];
    built.sort((a, b) => (b.virality_score || 0) - (a.virality_score || 0));
    setSortOrder(buildSort('virality', false));
    setRows(built);
    calcDuration(built);
    return scores.size;
  };

  // ARCHIVE: hide whole sources (videos) — the source chips in the archive bar. Spacer rows go
  // with their sheet. No-op outside archive mode or when every source is shown. Added to EVERY
  // copy of the filter chain (they are duplicated on purpose in this file).
  const filterSource = (filterRows) => {
    const hidden = hiddenSourcesRef.current;
    if (!archive || !hidden || !hidden.size) return filterRows;
    return filterRows.filter((row) => row._searchContext || !row.source || !hidden.has(String(row.source.transcriptId)));
  };

  const filterSentiment = (filterRows) => {
    if (!filterRows.length || !("sentiment" in filterRows[0]))
      return filterRows;

    const sentimentKeys = new Set(filterStore.getSentimentsTrue(currentView));
    if (!sentimentKeys.size) return filterRows;

    return filterRows.filter((row) => row.space || row._searchContext || sentimentKeys.has(row.sentiment));
  };

  const filterSpeakers = (filterRows) => {
    if (!filterRows.length || !("speaker" in filterRows[0])) return filterRows;

    const speakerKeys = new Set(filterStore.getSpeakersTrue(currentView));
    if (!speakerKeys.size) return filterRows;

    return filterRows.filter((row) => row.space || row._searchContext || speakerKeys.has(row.speaker));
  };

  const filterSeconds = (filterRows) => {
    if (!filterRows.length || !("sec" in filterRows[0])) return filterRows;

    const [min, max] = filterStore.getSeconds(currentView);
    if (!precision[currentView]) {
      return filterRows.filter(
        (row) => row.space || row._searchContext || (row.sec_int >= min && row.sec_int <= max),
      );
    }
    return filterRows.filter(
      (row) => row.space || row._searchContext || (row.sec_float >= min && row.sec_float <= max),
    );
  };

  const filterTextLen = (filterRows) => {
    if (!filterRows.length || !("length" in filterRows[0])) return filterRows;
    let [min, max] = filterStore.getTextLen(currentView);
    if (filterStore.getSpaces(currentView)) {
      min = 0;
    }
    return filterRows.filter(
      (row) => row._searchContext || (row["length"] >= min && row["length"] <= max),
    );
  };

  const filterSearch = (filterRows) => {
    const searchString = filterStore.getSearchEscaped(currentView);

    if (!searchString) return filterRows;

    const [regex, regex1, regex2] = reGex(searchString);
    const isMatch = (row) =>
      !!(row && row["sentence"] &&
        (regex.test(row["sentence"]) || regex1.test(row["sentence"]) || regex2.test(row["sentence"])));

    if (filterStore.getSpaces(currentView)) {
      let newRows = [];
      for (let i = 0; i < filterRows.length; i++) {
        if (isMatch(filterRows[i])) {
          // Add related spaces using the original rows array and stored indices
          if (filterRows[i]["related"] && filterRows[i]["related"].length > 0) {
            const relatedIndices = [...filterRows[i]["related"]]; // Copy array to avoid mutation
            for (const index of relatedIndices) {
              if (originalRows[index] && originalRows[index]["space"]) {
                newRows.push(originalRows[index]);
              }
            }
          }
          newRows.push(filterRows[i]);
        }
      }
      return newRows;
    }

    // Spaces hidden — return only the matches. The context window (search AND AI filters)
    // is applied once at the end of the pipeline by applyContext().
    return filterRows.filter(isMatch);
  };

  // Context window — shared by search AND the AI / Filter-By filters. When the list is
  // narrowed and the slider is > 0, include up to N clips BEFORE and AFTER each match,
  // pulled from the FULL view list (so neighbours bypass every filter), flagged
  // _searchContext (dimmed but still selectable). Hits are flagged false so they stay
  // highlighted. Skipped when spaces are shown, or when nothing is actually narrowing.
  const applyContext = (fullList, matchedRows) => {
    const ctx = searchContextRef.current || 0;
    if (ctx <= 0 || filterStore.getSpaces(currentView)) return matchedRows;
    const ordered = fullList.filter((r) => !r.space);
    if (matchedRows.length >= ordered.length) return matchedRows; // nothing narrowed it
    const matchIds = new Set(matchedRows.map((r) => r.id));
    const flag = new Array(ordered.length).fill(null); // null = exclude, true = context, false = hit
    for (let i = 0; i < ordered.length; i++) {
      if (!matchIds.has(ordered[i].id)) continue;
      for (let j = Math.max(0, i - ctx); j <= Math.min(ordered.length - 1, i + ctx); j++) {
        if (flag[j] === null) flag[j] = true;
      }
    }
    for (let i = 0; i < ordered.length; i++) if (matchIds.has(ordered[i].id)) flag[i] = false;
    const result = [];
    for (let i = 0; i < ordered.length; i++) {
      if (flag[i] !== null) result.push({ ...ordered[i], _searchContext: flag[i] });
    }
    return result;
  };

  const onSentimentChange = (event) => {
    shouldStopVideo();
    filterStore.setSentiments(event.target.value, currentView);
    filterAll();
  };

  const onSpeakerChange = (event) => {
    shouldStopVideo();
    filterStore.setResetSpeakers(event.target.value, currentView);
    filterAll();
  };

  const onSecChange = (values) => {
    shouldStopVideo();
    filterStore.setSeconds(values, currentView);
    filterAll();
  };

  const onSetSpaces = () => {
    shouldStopVideo();

    // Get the currently visible item before toggling spaces
    let currentlyVisibleItem = null;
    if (rows.length > 0 && firstVisibleIndex < rows.length) {
      currentlyVisibleItem = rows[firstVisibleIndex];
      console.log("Preserving position for item:", {
        id: currentlyVisibleItem?.id,
        sentence: currentlyVisibleItem?.sentence?.substring(0, 30) + "...",
        currentIndex: firstVisibleIndex,
        spacesCurrentlyOn: filterStore.getSpaces(currentView),
      });
    }

    // Toggle spaces and apply all filters
    filterStore.setSpacesOnOff(currentView);

    // Apply all filters - this will update rows state and call calcDuration
    filterAll();

    // Find the item in the new data and restore position
    if (currentlyVisibleItem && virtuosoRef.current) {
      // Use a timeout to allow filterAll() to complete and update the rows state
      setTimeout(() => {
        if (rows.length > 0) {
          // Find the item in the new filtered data
          const newIndex = rows.findIndex(
            (row) => row.id === currentlyVisibleItem.id,
          );

          console.log("Restoring position:", {
            itemId: currentlyVisibleItem.id,
            oldIndex: firstVisibleIndex,
            newIndex: newIndex,
            newRowsLength: rows.length,
            spacesNowOn: filterStore.getSpaces(currentView),
          });

          if (newIndex !== -1) {
            virtuosoRef.current.scrollToIndex({
              index: newIndex,
              align: "start",
              behavior: "auto",
            });
          }
        }
      }, 100);
    }
  };

  const onTextLenChange = (values) => {
    shouldStopVideo();
    filterStore.setTextLen(values, currentView);
    filterAll();
  };

  const onSearchChange = (event) => {
    shouldStopVideo();
    const searchTerm = event.target.value.toLowerCase();
    filterStore.setSearch(currentView, searchTerm);
    filterAll();
  };

  const resetFilters = (data, view = currentView) => {
    shouldStopVideo();
    // Only reset filters for specified view
    const lb = getDimensionMinMax("length", data);
    const sb = getDimensionMinMax("sec", data);
    filterStore.resetFilters(lb, sb, view);
    filterStore.resetAllSpeakers();
    // Keep the toolbar's full-range reference in lockstep with the range reset
    // applies, so the Time/Length symbols don't falsely linger after Reset All.
    if (view === currentView) {
      setSecondsBounds(sb);
      setTextLenBounds(lb);
    }
  };

  const updateHeader = () => {
    const newHeader = getHeaderCurrentView().map((item) => {
      item.isUp = false;
      return item;
    });
    updateHeaderDataView(newHeader);
  };

  const onResetFilters = () => {
    shouldStopVideo();
    setSplitCursor(null);
    setRegroupParams(null);
    // RESET ALL — clear the user's WORK too, not just filters: manual trims, pen highlights,
    // text edits, selection, filler/virality view state, and the undo/redo history — back to a
    // clean slate. (The AI ANALYSIS itself — virality scores & filler flags — is kept, since
    // re-generating it costs AI calls; clear those with their own buttons.)
    adjustmentsRef.current = {};
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
    setEditedSentences({});
    editedSentencesRef.current = {};
    filterStore.resetSelectedSentences(currentView);
    filterStore.resetHighlights();
    fillerModeRef.current = 'off';
    setFillerMode('off');
    viralityRangeRef.current = [0, 100];
    setViralityRange([0, 100]);
    setQuickFilters({ selectedOnly: false, hasQuestion: false, firstLetter: null, durationSec: null, containsNumbers: false, entityTypes: [], contentFlags: [], keyPhrases: [], filterVerbs: [], filterNouns: [], filterAdjectives: [] });
    setSpacesMode('hide');
    setSortOrder({ field: null, direction: 'asc', column: null, label: null });
    filterStore.setSpacesTo(currentView, false);
    let newRows = [...getDataForCurrentView(currentView)];
    resetFilters(newRows);
    // Only reset order for current view
    setOrder((prevOrder) => ({
      ...prevOrder,
      [currentView]: 1,
    }));

    setSelectedCount((prevCount) => ({
      ...prevCount,
      [currentView]: 0,
    }));

    updateHeader();
    indeterminateCheck(newRows);
    // calcDuration(newRows)
    setRows(newRows);
    filterAll();

    // Scroll to top after reset
    // only if the first visible index is not 0
    if (firstVisibleIndex !== 0) {
      setTimeout(() => {
        virtuosoRef.current?.scrollToIndex({ index: 0 });
      }, 100);
    }
  };

  const getPrecisionField = (field) => {
    if (field === "sec") {
      return precision[currentView] ? "sec_float" : "sec_int";
    }
    // Sort the Time column by start_in (numeric float) for reliable chronological order
    if (field === "duration") {
      return "start_in";
    }
    return field;
  };

  const getDimensionMinMax = (field, obj) => {
    field = getPrecisionField(field);
    // take in account the spaces on off
    const dimension = [...new Set(obj.map((obj) => obj[field]))];
    return [Math.min(...dimension), Math.max(...dimension)];
  };

  const getDimension = (field, obj) =>
    [...new Set(obj.map((o) => o[field]))].filter(Boolean).sort();

  const setColor = (id, colorClass, op) => {
    const element = document.getElementById(id);
    if (element) {
      if (op === "-") {
        element.classList.remove(colorClass);
        return;
      }
      element.classList.add(colorClass);
    }
  };

  // A clip is a clip whether it's dialog or a space — count ALL visible rows so the
  // CLIPS counter matches the list (e.g. "Spaces: Only" shows the number of spaces,
  // not 0). The SPACES Show/Hide/Only toggle already controls what's in `rows`.
  const countRows = () => rows.length;

  const showSubtitles = () => {
    const elapsed = (performance.now() - userStore.getStartTime()) / 1000;
    userStore.setStartTime(performance.now());

    if (!filterStore.getSubtitlesOnOff(currentView)) {
      userStore.setSentenceStart(userStore.getSentenceStart() + elapsed);
      return;
    }
    const timeLine = userStore.getSentenceStart();
    const previousWord = userStore.getPreviousWord();
    const item = userStore.getItem();
    const data = userStore.getVideoData();
    if (!data || !data["utterance"]) {
      userStore.setSentenceStart(userStore.getSentenceStart() + elapsed);
      return;
    }
    const wordIndex = data["utterance"].findIndex(
      (item) => timeLine >= item[0] && timeLine <= item[1],
    );
    userStore.setSentenceStart(timeLine + elapsed);
    setColor(`s-${item}-${previousWord}`, subtitleColorHighlight, "-");
    setColor(`s-${item}-${wordIndex}`, subtitleColorHighlight, "+");
    userStore.setPreviousWord(wordIndex);
  };

  const onSetResetSubtitles = () => {
    // Store the current subtitles state before toggling
    const currentSubtitlesEnabled = filterStore.getSubtitlesOnOff(currentView);
    // Toggle the subtitles state
    filterStore.setSubtitlesOnOff(currentView);
    // If subtitles are being turned OFF and there's video data, clear any highlighted subtitles
    if (currentSubtitlesEnabled && userStore.getVideoData() !== null) {
      clearSubtitles();
    }
  };

  const clearSubtitles = () => {
    // It ends before the word has a chance to be delighted,
    // so this clears the highlight word when the video stops
    // no matter when it stops
    const item = userStore.getItem();
    const data = userStore.getVideoData();

    if (!data || !data["utterance"]) return;
    for (let i = 0; i < data["utterance"].length; i++) {
      setColor(`s-${item}-${i}`, subtitleColorHighlight, "-");
    }
  };

  const shouldStopVideo = () =>
    userStore.getItem() !== null ? videoStop() : null;

  const handleRangeChanged = (range) => {
    // Do NOT stop playback on scroll (that was the old bug).
    if (!range) return;
    const view = rowsRef.current;
    const start = Math.max(0, (range.startIndex ?? 0) - 4);
    const end = Math.min(view.length, (range.endIndex ?? 0) + 12);
    if (end <= start) return;
    const slice = view.slice(start, end);
    // Lazy ET: sign posters for the clips that just scrolled into view (cherries pop in).
    if (lazyThumbnailsRef.current) ensureClipThumbnails(slice);
    // If a translation language is active, prioritise translating the clips that just scrolled
    // into view so the user never stares at untranslated text while the background fill catches up.
    if (translationLang) ensureTranslated(slice, translationLang, { concurrency: 8, delayMs: 60 });
  };

  const playFired = () => {
    const durationMs = userStore.getVideoDurationMs();
    const endIn = userStore.getEndIn();
    const video = userStore.getVideo();

    // Use requestAnimationFrame (~16ms cadence, synced to screen refresh) so the
    // stop fires within one frame of end_in — inaudible overshoot vs the 50ms
    // setInterval that was letting the next clip's audio bleed in.
    let rafId;
    const checkEndBoundary = () => {
      // Guard: if the store moved on to a different video, this element is orphaned. PAUSE it
      // (don't just stop checking) — otherwise a re-sort/re-render leaves it playing forever
      // with the pause button pointing elsewhere. This is the "endless, can't-pause" bug.
      if (userStore.getVideo() !== video) { try { video.pause(); } catch (e) { /* ignore */ } return; }
      if (video.currentTime >= endIn) {
        videoStop(true);
      } else {
        rafId = requestAnimationFrame(checkEndBoundary);
      }
    };
    rafId = requestAnimationFrame(checkEndBoundary);
    userStore.setTimeUpdateHandler(() => cancelAnimationFrame(rafId));

    userStore.setTimeOutId(setTimeout(() => {
      if (userStore.getVideo() === video) videoStop(true);
    }, durationMs + 1000));
    userStore.setStartTime(performance.now());
    userStore.setIntervalId(setInterval(() => showSubtitles(), INTERVAL));
  };

  const videoStop = (naturalEnd = false) => {
    const finishedItemId = userStore.getItem();
    const video = userStore.getVideo();

    if (video) {
      clearInterval(userStore.getIntervalId());
      clearTimeout(userStore.getTimeOutId());

      // Cancel the requestAnimationFrame stop-check loop
      const cleanup = userStore.getTimeUpdateHandler();
      if (cleanup) {
        cleanup();
        userStore.setTimeUpdateHandler(null);
      }

      video.pause();

      // Safari-compatible poster restoration
      const thumbnail = userStore.getVideoData()?.thumbnail;
      if (thumbnail) {
        // First remove the poster
        video.removeAttribute("poster");
        // Force a reflow
        void video.offsetWidth;
        // Then set it again
        video.setAttribute("poster", thumbnail);
      }

      clearSubtitles();
      video.removeEventListener("playing", playFired);
      userStore.resetVideo();

      // Auto-advance when a clip ends naturally. Play Selected (selection order) takes
      // priority over Play Next (table order). finishedItemId is the CLIP ID (id-based
      // tracking) — find its CURRENT position so a re-sort between clips still lands right.
      if (naturalEnd && finishedItemId !== null) {
        if (playingSelectedRef.current && !playSelectedPausedRef.current) {
          // Advance to the next SELECTED clip in the frozen selection-order queue.
          playSelectedCursorRef.current += 1;
          playSelectedAtCursor();
        } else if (playNextRef.current) {
          const currentIndex = rowsRef.current.findIndex((r) => r.id === finishedItemId);
          const nextIndex = currentIndex === -1 ? -1 : rowsRef.current.findIndex((r, i) => i > currentIndex && !r.space);
          if (nextIndex !== -1) {
            const nextRow = rowsRef.current[nextIndex];
            virtuosoRef.current?.scrollToIndex({ index: nextIndex, behavior: 'smooth', align: 'start' });
            // Poll until Virtuoso renders the row into the DOM (virtualized list)
            let attempts = 0;
            const tryPlay = () => {
              const nextVideoEl = document.getElementById(`v-${nextRow.id}`);
              if (nextVideoEl) {
                userStore.setVideo(effectivePlay(nextRow), nextVideoEl, nextRow.id);
                startVideo();
              } else if (attempts++ < 20) {
                setTimeout(tryPlay, 80);
              }
            };
            setTimeout(tryPlay, 80);
          }
        }
      }
    }
  };

  // If "play highlights only" is on and this clip has a pen highlight, return a copy
  // whose start/end are the highlighted range — so playback covers just that moment.
  const effectivePlay = (row) => {
    if (!row) return row;
    if (playHighlightsRef.current) {
      const pen = getPenTimecodes(row.id, row.utterance, row);
      if (pen) return { ...row, start_in: pen.start_in, end_in: pen.end_in };
    }
    return row;
  };

  const startVideo = () => {
    const video = userStore.getVideo();
    if (!video) return;

    // Safety net: only ONE clip may ever play. Force-pause every OTHER table video, so a
    // recycled element can never keep running loose. Playback is now tracked by CLIP ID
    // (v-${clip.id}, stable across sort/filter) — the real fix for the "plays forever /
    // can't pause" bug; this belt-and-suspenders pause stays as extra insurance.
    try {
      document.querySelectorAll('video[id^="v-"]').forEach((v) => {
        if (v !== video && !v.paused) { try { v.pause(); } catch (e) { /* ignore */ } }
      });
    } catch (e) { /* ignore */ }

    if (video.paused || video.ended) {
      // Start the playback timers on "playing" (when audio ACTUALLY begins, after any
      // seek/buffering) — NOT "play" (which fires the instant play() is called). On a
      // long video, a deep-seek clip buffers before it sounds; binding to "play" started
      // the auto-advance fallback while still silent, flipping clips with no audio.
      video.addEventListener("playing", playFired, { once: true });

      const target = userStore.getStartIn();
      try { video.preload = 'auto'; } catch (_) { /* ignore */ }

      // Play ONLY once we're actually positioned at the clip's start. A "cold" element
      // (no buffered data) could play from its previous position while the transcript
      // highlight — which is timer-driven — ran correctly: that's the "audio is from the
      // wrong clip" bug. So if we're not already at the start, set currentTime and WAIT
      // for the 'seeked' event before play(), so the audio matches the transcript.
      const playFromTarget = () => {
        if (Math.abs((video.currentTime || 0) - target) <= 0.25) {
          video.play().catch(() => {});
        } else {
          video.addEventListener('seeked', () => video.play().catch(() => {}), { once: true });
          video.currentTime = target;
        }
      };

      if (video.readyState >= 1) {
        playFromTarget();
      } else {
        // Cold element (preload was 'none'): load it, then seek + play once it's seekable.
        video.addEventListener('loadedmetadata', playFromTarget, { once: true });
        try { video.load(); } catch (_) { /* ignore */ }
      }
    } else {
      videoStop();
    }
  };

  // Play the clip at the current queue cursor. Skips selected clips that aren't in the
  // current (filtered) view, and ends the sequence when the queue is exhausted. Mirrors the
  // Play-Next virtualized-play recipe: scroll the row into view, poll until its <video>
  // mounts, then setVideo + startVideo. startVideo's own safety net force-pauses every other
  // clip, so only one ever plays.
  const playSelectedAtCursor = () => {
    if (!playingSelectedRef.current || playSelectedPausedRef.current) return;
    const queue = playSelectedQueueRef.current;
    const rowIndexOf = (id) => rowsRef.current.findIndex((r) => r.id === id && !r.space);
    let cursor = playSelectedCursorRef.current;
    // Skip forward over ids that dropped out of the current view (filtered/regrouped away).
    while (cursor < queue.length && rowIndexOf(queue[cursor]) === -1) {
      cursor += 1;
    }
    playSelectedCursorRef.current = cursor;
    if (cursor >= queue.length) { stopPlaySelected(); return; }   // reached the end → stop
    const idx = rowIndexOf(queue[cursor]);
    const row = rowsRef.current[idx];
    virtuosoRef.current?.scrollToIndex({ index: idx, behavior: 'smooth', align: 'start' });
    let attempts = 0;
    const tryPlay = () => {
      if (!playingSelectedRef.current || playSelectedPausedRef.current) return;   // paused/stopped while we were scrolling
      const el = document.getElementById(`v-${row.id}`);
      if (el) {
        userStore.setVideo(effectivePlay(row), el, row.id);
        startVideo();
      } else if (attempts++ < 20) {
        setTimeout(tryPlay, 80);
      } else {
        // Row never mounted — skip it so the sequence doesn't stall.
        playSelectedCursorRef.current += 1;
        playSelectedAtCursor();
      }
    };
    setTimeout(tryPlay, 80);
  };

  const startPlaySelected = () => {
    const ids = filterStore.getSelectedSentences(currentView) || [];
    if (!ids.length) return;
    playSelectedQueueRef.current = [...ids];   // freeze the selection order at start
    playSelectedCursorRef.current = 0;
    playSelectedPausedRef.current = false;
    setPlaySelectedPaused(false);
    playingSelectedRef.current = true;
    setPlayingSelected(true);
    playSelectedAtCursor();
  };

  const pausePlaySelected = () => {
    const video = userStore.getVideo();
    if (video) {
      video.pause();
      // Suspend the auto-advance machinery so a pause never advances to the next clip:
      // kill the safety timeout, cancel the end-boundary rAF loop, stop the subtitle tick.
      clearTimeout(userStore.getTimeOutId());
      const cleanup = userStore.getTimeUpdateHandler();
      if (cleanup) { cleanup(); userStore.setTimeUpdateHandler(null); }
      clearInterval(userStore.getIntervalId());
    }
    playSelectedPausedRef.current = true;
    setPlaySelectedPaused(true);
  };

  const resumePlaySelected = () => {
    const video = userStore.getVideo();
    playSelectedPausedRef.current = false;
    setPlaySelectedPaused(false);
    if (video) {
      // Re-arm the end-boundary + subtitle timers (playFired reads endIn/duration from the
      // store, which we never reset on pause) and continue from the paused position.
      video.addEventListener('playing', playFired, { once: true });
      video.play().catch(() => {});
    } else {
      // Nothing loaded (e.g. row got recycled) — just replay the current cursor.
      playSelectedAtCursor();
    }
  };

  const stopPlaySelected = () => {
    playingSelectedRef.current = false;
    playSelectedPausedRef.current = false;
    setPlayingSelected(false);
    setPlaySelectedPaused(false);
    playSelectedQueueRef.current = [];
    playSelectedCursorRef.current = 0;
  };

  // Toolbar button: idle → play selected in order; playing → pause; paused → resume.
  const onPlaySelectedToggle = () => {
    if (!playingSelectedRef.current) { startPlaySelected(); return; }
    if (playSelectedPausedRef.current) resumePlaySelected();
    else pausePlaySelected();
  };

  const onVideoClick = (event, data, item) => {
    // A manual click takes over playback — leave any Play-Selected sequence.
    if (playingSelectedRef.current) stopPlaySelected();
    const previousVideo = userStore.getVideo();
    if (previousVideo !== event.target) {
      videoStop();
    }
    const video = event.target;
    if (video.paused || video.ended) {
      userStore.setVideo(effectivePlay(data), video, data['id']);
      startVideo();
    } else {
      videoStop();
    }
  };

  const onWordClick = (data, item, wordTime, wordIndex = -1) => {
    const videoEl = document.getElementById(`v-${data['id']}`);
    if (!videoEl) return;
    const previousVideo = userStore.getVideo();
    if (previousVideo && previousVideo !== videoEl) videoStop();
    else if (previousVideo === videoEl) videoStop();
    userStore.setVideo(data, videoEl, data['id']);
    userStore.setSentenceStart(wordTime);
    videoEl.addEventListener("playing", playFired, { once: true });
    videoEl.currentTime = wordTime;
    videoEl.play().catch(() => {});
    setSplitCursor(null);
  };

  const onWordRightClick = (data, wordIndex) => {
    if (wordIndex > 0) setSplitCursor({ rowId: data.id, wordIndex });
  };

  const splitRowAtWord = (cursor) => {
    if (!cursor) return;
    const { rowId, wordIndex } = cursor;
    const rowIndex = rows.findIndex(r => r.id === rowId);
    if (rowIndex === -1) { setSplitCursor(null); return; }
    const orig = rows[rowIndex];
    if (!orig.utterance || orig.utterance.length < 2 || wordIndex <= 0 || wordIndex >= orig.utterance.length) {
      setSplitCursor(null); return;
    }
    const splitTime = orig.utterance[wordIndex][0];
    const words = (orig.sentence || '').split(' ');
    const makeRow = (id, start, end, sentWords, utt) => ({
      ...orig,
      id,
      _splitId: typeof orig.id === 'string' && orig.id.includes('-') ? orig._splitId || orig.id : orig.id,
      start_in: start,
      end_in: end,
      sentence: sentWords.join(' '),
      utterance: utt,
      sec: (end - start).toFixed(2),
      sec_int: Math.floor(end - start),
      sec_float: parseFloat((end - start).toFixed(2)),
      duration_ms: Math.round((end - start) * 1000),
      duration: [toHMS(start), toHMS(end)], // the piece's own in/out on its Clock, not the parent's
      order: maxLines,
    });
    const rowA = makeRow(`${rowId}-a`, orig.start_in, splitTime, words.slice(0, wordIndex), orig.utterance.slice(0, wordIndex));
    const rowB = makeRow(`${rowId}-b`, splitTime, orig.end_in, words.slice(wordIndex), orig.utterance.slice(wordIndex));
    const newRows = [...rows];
    newRows.splice(rowIndex, 1, rowA, rowB);
    setRows(newRows);
    setSplitCursor(null);
  };

  // Embed bridge: "✂ split at playhead" from the parent timeline. Maps the requested time
  // to the nearest word boundary inside the clip (same rule as right-click-a-word) and
  // reuses splitRowAtWord. If the clip was picked, both pieces take its place in pick order.
  embedSplitRef.current = (rowId, t) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row || !row.utterance || row.utterance.length < 2) return;
    let best = -1;
    let bestD = Infinity;
    row.utterance.forEach((wt, i) => {
      if (i > 0) {
        const dd = Math.abs(wt[0] - t);
        if (dd < bestD) { bestD = dd; best = i; }
      }
    });
    if (best <= 0) return;
    splitRowAtWord({ rowId, wordIndex: best });
    const selIds = filterStore.selectedSentences[currentView] || [];
    if (selIds.includes(rowId)) {
      const next = selIds.flatMap((id) => (id === rowId ? [`${rowId}-a`, `${rowId}-b`] : [id]));
      filterStore.resetSelectedSentences(currentView);
      next.forEach((id) => filterStore.setSelectedSentences(id, currentView));
    }
  };

  const unsplitRow = (splitId) => {
    const pieces = rows.filter(r => (r._splitId || r.id) === splitId || r.id === splitId);
    if (pieces.length < 2) return;
    const sorted = [...pieces].sort((a, b) => a.start_in - b.start_in);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const allWords = sorted.flatMap(p => (p.sentence || '').split(' '));
    const allUtt = sorted.flatMap(p => p.utterance || []);
    const restored = {
      ...first,
      id: splitId,
      _splitId: undefined,
      end_in: last.end_in,
      sentence: allWords.join(' '),
      utterance: allUtt,
      sec: (last.end_in - first.start_in).toFixed(2),
      sec_int: Math.floor(last.end_in - first.start_in),
      sec_float: parseFloat((last.end_in - first.start_in).toFixed(2)),
      duration_ms: Math.round((last.end_in - first.start_in) * 1000),
    };
    const firstIdx = rows.findIndex(r => r.id === sorted[0].id);
    const newRows = rows.filter(r => !pieces.includes(r));
    newRows.splice(firstIdx, 0, restored);
    setRows(newRows);
  };

  const splitCursorRef = useRef(null);
  useEffect(() => { splitCursorRef.current = splitCursor; }, [splitCursor]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') { setSplitCursor(null); return; }
      if (e.key === 'Enter') {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        const cur = splitCursorRef.current;
        if (cur) splitRowAtWord(cur);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [rows]);

  const makeOrder = (buffer) => {
    // add order to the buffer
    buffer = buffer.map((obj) => {
      obj["order"] = maxLines;
      obj["space"] = false;
      obj["speaker"] = {};
      obj["sec_int"] = parseInt(obj["sec"]);
      obj["sec_float"] = parseFloat(obj["sec"]);

      return obj;
    });
    return buffer;
  };

  // Pin all selected clips to top of the full unfiltered list, in the order they were selected.
  // Works correctly even when clips were selected across different search/filter states.
  // expandRows (optional): swaps rows for the piece rows of clips an AI split (selectClipsByIds).
  // Button handlers call this with an event — only a function counts.
  const onPinSelectedToTop = (expandRows) => {
    shouldStopVideo();
    if (playSelectedPausedRef.current) stopPlaySelected();   // re-pinned while paused → play from the top next time
    // Authoritative selection order lives in the store — survives any filter/search changes
    const selectedIds = filterStore.getSelectedSentences(currentView); // ordered array
    const orderMap = {};
    selectedIds.forEach((id, idx) => { orderMap[id] = idx + 1; });

    // Clear search and quick filters so we show the full unfiltered list
    filterStore.setSearch(currentView, "");
    setQuickFilters({ selectedOnly: false, hasQuestion: false, firstLetter: null, durationSec: null, containsNumbers: false, entityTypes: [], contentFlags: [], keyPhrases: [], filterVerbs: [], filterNouns: [], filterAdjectives: [] });
    setSpacesMode('hide');
    filterStore.setSpacesTo(currentView, false);

    // Start from the complete unfiltered dataset and apply current sentiment/speaker/seconds filters
    let fullData = [...getDataForCurrentView(currentView)];
    fullData = filterSource(fullData);
    fullData = filterSentiment(fullData);
    fullData = filterSpeakers(fullData);
    fullData = filterSeconds(fullData);
    fullData = filterTextLen(fullData);
    if (typeof expandRows === 'function') fullData = expandRows(fullData);

    // Assign order from store then sort: selected → top in pick order, unselected → bottom
    fullData = fullData.map(row => ({
      ...row,
      order: orderMap[row.id] !== undefined ? orderMap[row.id] : maxLines,
    }));
    fullData.sort((a, b) => a.order - b.order);

    setRows(fullData);
    calcDuration(fullData);
  };

  // Drag-to-reorder — require 8px movement before a drag starts so normal clicks still work
  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: penMode
        ? { distance: 999999 }          // pen mode: row drag disabled
        : { delay: 250, tolerance: 10 },
    }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    if (playSelectedPausedRef.current) stopPlaySelected();   // reordered while paused → play from the top next time
    let reordered = null;
    setRows((prev) => {
      const oldIndex = prev.findIndex((r) => r.id === active.id);
      const newIndex = prev.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      reordered = arrayMove(prev, oldIndex, newIndex);
      return reordered;
    });
    // Dragging a SELECTED clip changes where it plays: re-derive the pick order (1st, 2nd,
    // 3rd…) from the new top-to-bottom order, so moving a clip down makes it play later.
    if (reordered) {
      const selected = new Set(filterStore.getSelectedSentences(currentView));
      if (selected.size > 0) {
        const newOrder = reordered.filter((r) => selected.has(r.id)).map((r) => r.id);
        filterStore.resetSelectedSentences(currentView);
        newOrder.forEach((id) => filterStore.setSelectedSentences(id, currentView));
      }
    }
  };

  // Rebuild the visible rows using an adjustment snapshot (used by undo/redo).
  const applyAdjustmentSnapshot = (snapshot) => {
    setRows(prev => prev.map(r => {
      const noMarkers = { _stolenTextAfter: null, _stolenTextBefore: null, _stolenTimeAfter: null, _stolenTimeBefore: null, _clippedStart: false, _clippedEnd: false };
      const adj = snapshot[r.id];
      if (adj) {
        return { ...r, ...noMarkers, start_in: adj.start_in, end_in: adj.end_in, _adjustedBy: 'user',
          duration_ms: (adj.end_in - adj.start_in) * 1000,
          duration: [secondsToHMS(adj.start_in), secondsToHMS(adj.end_in)] };
      }
      if (r._adjustedBy) {
        // Adjustment was removed — restore original timecodes
        const orig = originalRows.find(o => o.id === r.id);
        if (orig) return { ...r, ...noMarkers, start_in: orig.start_in, end_in: orig.end_in,
          _adjustedBy: undefined, duration_ms: (orig.end_in - orig.start_in) * 1000,
          duration: [secondsToHMS(orig.start_in), secondsToHMS(orig.end_in)] };
      }
      return { ...r, ...noMarkers };
    }));
  };

  const restoreSelectionSnapshot = (selectedIds) => {
    filterStore.resetSelectedSentences(currentView);
    selectedIds.forEach(id => filterStore.setSelectedSentences(id, currentView));
    const count = selectedIds.length;
    setSelectedCount(p => ({ ...p, [currentView]: count }));
    setOrder(p => ({ ...p, [currentView]: count + 1 }));
    const total = getDataForCurrentView().length;
    setChecked(p => ({ ...p, [currentView]: count === 0 ? [false, false] : count === total ? [true, false] : [false, true] }));
    // Build order map directly from snapshot — avoids MobX timing issues
    const orderMap = new Map(selectedIds.map((id, i) => [id, i + 1]));
    setRows(r => r.map(row => ({
      ...row,
      order: orderMap.has(row.id) ? orderMap.get(row.id) : maxLines,
    })));
  };

  const onUndo = () => {
    if (!undoStackRef.current.length) return;
    const prev = undoStackRef.current.pop();
    redoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)], editedSentences: { ...editedSentencesRef.current } });
    const prevAdj = prev.adjustments ?? prev;
    adjustmentsRef.current = prevAdj;
    applyAdjustmentSnapshot(prevAdj);
    if (prev.selectedIds !== undefined) restoreSelectionSnapshot(prev.selectedIds);
    if (prev.editedSentences !== undefined) setEditedSentences(prev.editedSentences);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
  };

  const onRedo = () => {
    if (!redoStackRef.current.length) return;
    const next = redoStackRef.current.pop();
    undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)], editedSentences: { ...editedSentencesRef.current } });
    const nextAdj = next.adjustments ?? next;
    adjustmentsRef.current = nextAdj;
    applyAdjustmentSnapshot(nextAdj);
    if (next.selectedIds !== undefined) restoreSelectionSnapshot(next.selectedIds);
    if (next.editedSentences !== undefined) setEditedSentences(next.editedSentences);
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
  };

  // Global Ctrl+Z / Ctrl+Y for timestamp undo/redo — placed after onUndo/onRedo are defined
  useEffect(() => {
    const handler = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      if (e.key === 'z' || e.key === 'Z') { e.preventDefault(); onUndo(); }
      if (e.key === 'y' || e.key === 'Y') { e.preventDefault(); onRedo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onUndo, onRedo]);

  const onSortClick = (index, direction) => {
    shouldStopVideo();
    // Determine which header to update based on the current view
    const headerToUpdate = getHeaderCurrentView();
    // Sort the currently displayed data (rows) instead of original data
    const dataToSort = [...rows];
    // Update the header arrows for the current view
    const newHeader = headerToUpdate.map((item, i) => {
      if (i === index) {
        item.isUp = !item.isUp;
        return item;
      }
      item.isUp = false;
      return item;
    });

    updateHeaderDataView(newHeader);

    // special case for order: delegate to the fixed pin-to-top handler
    if (headerToUpdate[index].restore) {
      onPinSelectedToTop();
      return;
    }

    const rawField = headerToUpdate[index].field.toLowerCase();
    // Record the active sort so the header shows the same indicator the toolbar does.
    setSortOrder(buildSort(rawField, direction === "up"));
    let sortField = getPrecisionField(rawField);
    dataToSort.sort((a, b) => {
      if (direction === "up") {
        return a[sortField] > b[sortField] ? 1 : -1;
      } else {
        return a[sortField] < b[sortField] ? 1 : -1;
      }
    });
    // Directly set the sorted data since we're working with already filtered data
    setRows(dataToSort);
  };

  // Fully clear the current view's selection — store AND the React state that
  // drives the "X selected" count and the ALL toggle. The toolbar calls this when
  // a segment-mode switch invalidates the selection (the store-only reset left the
  // header count and ALL checkbox stale).
  const clearAllSelections = () => {
    stopPlaySelected();   // clearing the picks ends any in-progress Play-Selected run
    filterStore.resetSelectedSentences(currentView);
    setSelectedCount((prev) => ({ ...prev, [currentView]: 0 }));
    setChecked((prev) => ({ ...prev, [currentView]: [false, false] }));
    setOrder((prev) => ({ ...prev, [currentView]: 1 }));
    userStore.setTotalDuration(durationFormat(0));
  };

  const indeterminateCheck = (newRows) => {
    const howManySelected = filterStore.howManySelectedSentences(currentView);
    const totalRowsForView = newRows.length;

    if (howManySelected === 0) {
      setChecked((prevChecked) => ({
        ...prevChecked,
        [currentView]: [false, false],
      }));
      userStore.setTotalDuration(durationFormat(0));
    } else if (howManySelected === totalRowsForView) {
      setChecked((prevChecked) => ({
        ...prevChecked,
        [currentView]: [true, false],
      }));
    } else {
      setChecked((prevChecked) => ({
        ...prevChecked,
        [currentView]: [false, true],
      }));
    }
  };

  const onCheckAll = (event) => {
    if (!checked[currentView][0] && !checked[currentView][1]) {
      // Select ALL rows in one operation. The old per-row loop read the count
      // and rows from React state on every iteration — which don't update
      // mid-loop — so the displayed count ended up "1" and only the last row's
      // pick-order stuck. Do it directly from the store instead.
      suppressUndoPushRef.current = true;
      undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)] });
      redoStackRef.current = [];
      setCanUndo(true);
      setCanRedo(false);

      filterStore.setAllSelectedSentences(rows, currentView);
      const total = rows.length;
      // Assign sequential pick order 1..N to every row
      const orderMap = new Map(rows.map((r, i) => [r.id, i + 1]));
      setRows(prev => prev.map(r => ({ ...r, order: orderMap.has(r.id) ? orderMap.get(r.id) : maxLines })));
      setSelectedCount(p => ({ ...p, [currentView]: total }));
      setOrder(p => ({ ...p, [currentView]: total + 1 }));
      setChecked(p => ({ ...p, [currentView]: [true, false] }));
      lastCheckedIndexRef.current = total > 0 ? total - 1 : null;

      suppressUndoPushRef.current = false;
    } else {
      checkUncheck(false);
    }
    calcDuration(rows);
  };

  const checkUncheck = (isChecked) => {
    undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)] });
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
    if (isChecked) {
      filterStore.setAllSelectedSentences(rows, currentView);
      // Update the selected count
      setSelectedCount((prevCount) => ({
        ...prevCount,
        [currentView]: rows.length,
      }));
      // Set order to the number of rows + 1 for the next selection
      setOrder((prevOrder) => ({
        ...prevOrder,
        [currentView]: rows.length + 1,
      }));
      // Update checked state for current view
      setChecked((prevChecked) => ({
        ...prevChecked,
        [currentView]: [true, false], // checked, not indeterminate
      }));
    } else {
      // Reset order to maxLines for all rows
      rows.forEach((obj, index) => {
        obj.order = maxLines;
      });
      // Reset selection in the store
      filterStore.resetSelectedSentences(currentView);
      // Reset the selected count
      setSelectedCount((prevCount) => ({
        ...prevCount,
        [currentView]: 0,
      }));
      // Reset order for new selections
      setOrder((prevOrder) => ({
        ...prevOrder,
        [currentView]: 1,
      }));
      // Update checked state
      setChecked((prevChecked) => ({
        ...prevChecked,
        [currentView]: [false, false], // not checked, not indeterminate
      }));
    }
  };

  // Select a single row (check=true) or deselect (check=false), updating all related state.
  const selectRow = (rowIndex, rowId, check) => {
    // Editing the picks while a Play-Selected run is PAUSED drops the paused sequence, so the
    // next tap of the button re-reads the selection and plays from the FIRST selected clip again
    // (matches "I stopped, changed my picks — play from the top"). Active (unpaused) playback
    // keeps its frozen queue.
    if (playSelectedPausedRef.current) stopPlaySelected();
    if (check) {
      filterStore.setSelectedSentences(rowId, currentView);
      setOrder((prev) => ({ ...prev, [currentView]: prev[currentView] + 1 }));
      const newCount = selectedCount[currentView] + 1;
      setSelectedCount((prev) => ({ ...prev, [currentView]: newCount }));
      const isAll = newCount === getDataForCurrentView().length;
      setChecked((prev) => ({ ...prev, [currentView]: isAll ? [true, false] : [false, true] }));
      if (rowIndex < rows.length) {
        // Record the pick order on the row, but do NOT call setRows here. Replacing the
        // whole array hands TableVirtuoso a new `data` reference, which re-renders EVERY
        // visible clip cell — and since all cells share one video source seeked to
        // different times, they all repaint ("jump a frame") on each click. The selection
        // highlight comes from the MobX store (re-renders only this row), and the export/
        // reorder paths recompute `order` from the store, so an in-place write is enough.
        rows[rowIndex]["order"] = filterStore.getSelectedSentences(currentView).length;
      }
    } else {
      filterStore.removeSelectedSentence(rowId, currentView);
      const newCount = selectedCount[currentView] - 1;
      setSelectedCount((prev) => ({ ...prev, [currentView]: newCount }));
      setChecked((prev) => ({ ...prev, [currentView]: newCount === 0 ? [false, false] : [false, true] }));
      if (rowIndex < rows.length) {
        // In-place only (see the checking branch above) — don't re-render the whole table.
        rows[rowIndex]["order"] = maxLines;
      }
    }
  };

  const onCheckboxChange = (event, item, id) => {
    const isChecking = event.target.checked;

    // Shift+click: select/deselect every row between the last clicked row and this one
    if (event.shiftKey && lastCheckedIndexRef.current !== null && lastCheckedIndexRef.current !== item) {
      // Push one snapshot for the entire shift-click range
      suppressUndoPushRef.current = true;
      undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)] });
      redoStackRef.current = [];
      setCanUndo(true);
      setCanRedo(false);
      const lo = Math.min(lastCheckedIndexRef.current, item);
      const hi = Math.max(lastCheckedIndexRef.current, item);
      for (let i = lo; i <= hi; i++) {
        if (i >= rows.length) break;
        const r = rows[i];
        const alreadySelected = filterStore.getSelectedSentences(currentView).includes(r.id);
        // Select the range if we're checking; deselect if we're unchecking
        if (isChecking && !alreadySelected) selectRow(i, r.id, true);
        if (!isChecking && alreadySelected) selectRow(i, r.id, false);
      }
      suppressUndoPushRef.current = false;
      lastCheckedIndexRef.current = item;
      return false;
    }

    // Normal single-click — push snapshot before change
    if (!suppressUndoPushRef.current) {
      undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)] });
      redoStackRef.current = [];
      setCanUndo(true);
      setCanRedo(false);
    }
    selectRow(item, id, isChecking);
    lastCheckedIndexRef.current = item;
    return false;
  };

  const makeMidia = (selectedOptions, customTitle, orientation = 'original', burnCaptions = false, captionMode = 'word', captionSize = 64, captionWordsPerGroup = 3, captionStyle = '', separate = false, captionFont = '', supercut = null, framing = null, captionLook = null, effects = null) => {
    const createMidia = async (selectedOptions, customTitle, orientation, burnCaptions, captionMode, captionSize, captionWordsPerGroup, captionStyle, separate, captionFont) => {
      // Shared-ET mode: a guest exports via the share token. Keyed off the /s/ route (not a
      // lingering sessionStorage flag) so the normal ET is never affected. View-only blocks
      // export; the owner's plan limit + per-share cap are enforced server-side.
      const isSharedMode = typeof window !== 'undefined' && window.location.pathname.startsWith('/s/');
      const sharePerm = (() => { try { return sessionStorage.getItem('cc_share_perm'); } catch (e) { return null; } })();
      if (isSharedMode && sharePerm === 'view') {
        messageStore.setMessage('This is a view-only share — exporting is disabled.', 'error');
        return;
      }
      messageStore.setMessage('Preparing your export…', 'loading', 120000);
      // Check export limit before any make-video calls (skipped for shared guests — server enforces).
      if (!isSharedMode) {
        try {
          const limitsRes = await apiRequest(getUsageLimitsUrl, {
            method: "GET",
          });
          if (limitsRes.ok) {
            const limits = await limitsRes.json();
            const exporUsedCountThisMonth = limits.exports_count_this_month ?? 0;
            const currentMonthExportLimit = limits.exports_limit ?? 0;
            // "Each clip its own video" makes one export per ticked clip for every video kind.
            const perClip = separate && !archive ? (filterStore.getSelectedSentences(currentView) || []).length : 1;
            const createMediaSelectedCount = selectedOptions.reduce(
              (n, o) => n + (['mp4', 'mp4_super_cut', 'audiogram'].includes(optionToKind[o]) ? Math.max(1, perClip) : 1), 0);
            if (
              currentMonthExportLimit > 0 &&
              exporUsedCountThisMonth + createMediaSelectedCount >
                currentMonthExportLimit
            ) {
              messageStore.setMessage(
                `Export limit reached (${exporUsedCountThisMonth}/${currentMonthExportLimit} this month). Resets next month. You requested ${createMediaSelectedCount} creation(s).`,
                "error",
              );
              return;
            }
          }
        } catch (e) {
          console.warn("Could not fetch usage limits for export check:", e);
        }
      }

      // Get current search text
      const searchText = filterStore.getSearch(currentView);

      // Build adjusted_times map from the persistent ref — survives any filter/sort
      // operations that would have wiped _adjustedBy from the rows state.
      const selectedIds = filterStore.getSelectedSentences(currentView);
      const adjustedTimes = { ...adjustmentsRef.current };

      // Seed adjusted_times with every selected clip's current row times as baseline.
      // This ensures EDL export always has explicit source IN/OUT for every clip rather
      // than relying on the backend fallback to sentence.start_in_seconds.
      selectedIds.forEach(id => {
        const rowData = rowsRef.current.find(r => r.id === id) || originalRows.find(r => r.id === id);
        if (!rowData || rowData._splitId !== undefined) return;
        if (!adjustedTimes[id]) {
          adjustedTimes[id] = { start_in: rowData.start_in, end_in: rowData.end_in };
        }
      });

      // Merge pen highlights into adjusted_times — pen wins over manual adjustments
      selectedIds.forEach(id => {
        const rowData = rowsRef.current.find(r => r.id === id) || originalRows.find(r => r.id === id);
        if (!rowData) return;
        const pen = getPenTimecodes(id, rowData.utterance, rowData);
        if (pen) adjustedTimes[id] = pen;
      });

      // Split pieces ("456-a"/"456-b") repeat their parent's pk, each with its own times and
      // words in clip_times; re-segmented spans ("iv-3"/"w-12") go as explicit segments; ✏ edits
      // of real clips ride in edited_text (see export/exportClips.js).
      const edits = editedSentencesRef.current || {};
      const { exportIds, clipTimes, segments, editedText } = buildExportClips(
        selectedIds,
        (id) => rowsRef.current.find(r => r.id === id) || originalRows.find(r => r.id === id),
        { edits, adjustedTimes },
      );
      const clipTimesJson = clipTimes.some(Boolean) ? JSON.stringify(clipTimes) : null;
      // Breathing room (ms) for every export that cuts the media; the Super Cut takes it per word.
      const roomMs = Math.max(0, Math.round(supercut?.padMs || 0));
      // A transition per join, only when the window's joins are exactly this export's (no
      // re-segmented spans, which the worker cuts in time order after the real clips).
      const perJoin = Array.isArray(effects?.transitions) && !archive
        && effects.transitions.length === selectedIds.length - 1 && !selectedIds.some(isSyntheticId)
        ? effects.transitions : null;

      // Create a base data object
      // ARCHIVE: the cut is the ordered picks (pen trims / ± already applied), each mapped back
      // to ITS transcript + explicit times; the worker cuts every segment from its own video.
      // The first source stands in for transcript_id so the form validates and the export lands
      // in the library under the archive title.
      const archiveSegments = archive ? buildArchiveSegments(buildPicks()) : null;
      if (archive && !archiveSegments.length) {
        messageStore.setMessage('Nothing to export — tick some clips first.', 'error');
        return;
      }
      // ARCHIVE Super Cut: only the searched word(s) out of each ticked clip — every occurrence
      // becomes its own segment with its clip's video. (The single-source path finds the words on
      // the worker from `search_text`; here the times are explicit so the worker just cuts.)
      const archiveSupercutSegments = () => {
        const term = (searchText || '').trim().toLowerCase();
        if (!term) return [];
        const out = [];
        buildPicks().forEach((p) => {
          // Supercut toggle already on → picks are per-occurrence ("…-sc0") with their own times.
          if (/-sc\d+$/.test(String(p.id))) { const seg = pickToArchiveSegment(p); if (seg) out.push(seg); return; }
          const row = rowsRef.current.find((r) => r.id === p.id) || originalRows.find((r) => r.id === p.id);
          if (!row) return;
          supercutOccurrences(row, term).forEach((o) => {
            const seg = pickToArchiveSegment({ ...p, start: o.start, end: o.end, sentence: term });
            if (seg) out.push(seg);
          });
        });
        return out;
      };
      // Export-window framing → what the worker matches each cut by: the real clip's pk (a
      // split half rides on its parent's pk) or, for a re-segmented span, just its start.
      const clipLayouts = (framing?.layouts || []).map((l) => {
        const row = rowsRef.current.find((r) => r.id === l.id) || originalRows.find((r) => r.id === l.id);
        if (!row) return null;
        const pk = isSyntheticId(l.id) ? null : (row._splitId !== undefined ? row._splitId : l.id);
        return { pk, start: Number(row.start_in) || 0, ...(l.view ? { view: l.view } : {}), ...(l.caption ? { caption: l.caption } : {}) };
      }).filter(Boolean);
      const baseData = {
        list_of_ids: archive ? [] : exportIds,
        transcript_id: archive ? archive.sources[0].transcriptId : userStore.getUserTranscriptId(),
        download_id: archive ? archive.sources[0].transcriptId : userStore.getUserTranscriptId(),
        title: customTitle || (archive ? archiveExportTitle(archive.sources) : userStore.getUserTranscriptTitle()),
        search_text: searchText,
        model: mapViewToModel[currentView],
        adjusted_times: JSON.stringify(adjustedTimes),
        segments: JSON.stringify(segments),
        clip_times: archive ? null : clipTimesJson,
        archive_segments: archive ? JSON.stringify(archiveSegments) : null,
        edited_text: Object.keys(editedText).length ? JSON.stringify(editedText) : null,
      };

      // Process each selected option
      const results = await Promise.all(
        selectedOptions.map(async (option) => {
          try {
            // EDL export: direct file download, bypasses SQS. Not available to shared-ET
            // guests (the EDL endpoint is owner-authenticated only).
            if (archive && ARCHIVE_UNSUPPORTED_EXPORTS.has(option)) {
              messageStore.setMessage(`${option} isn't available for archives yet.`, 'info');
              return { option, success: false, message: 'Not available for archives' };
            }
            // Transcript: a text document made right here from the ticked rows (✏ edits
            // applied) — nothing goes to the server, so it works on shares and archives too.
            if (option === TRANSCRIPT_OPTION) {
              const clips = selectedIds
                .map((id) => {
                  const row = rowsRef.current.find(r => r.id === id) || originalRows.find(r => r.id === id);
                  if (!row) return null;
                  const start = adjustedTimes[id]?.start_in ?? row.start_in;
                  return { start: Number(start) || 0, speaker: row.speaker, text: edits[id] ?? row.sentence ?? '' };
                })
                .filter(Boolean);
              const title = customTitle || userStore.getUserTranscriptTitle() || 'CherryClipz';
              const blob = new Blob([buildTranscriptText(title, clips)], { type: 'text/plain;charset=utf-8' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              const safeName = title.substring(0, 50).replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/ +/g, '_');
              a.download = `${safeName || 'transcript'}.txt`;
              a.href = url;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              return { option, success: true };
            }
            if (option === 'Export XML for Premiere Pro') {
              if (isSharedMode) {
                messageStore.setMessage('Premiere XML export isn’t available on shared links.', 'info');
                return { option, success: false, message: 'Not available in shared mode' };
              }
              const params = new URLSearchParams();
              params.append('transcript_id', userStore.getUserTranscriptId());
              params.append('list_of_ids', exportIds.join(','));
              params.append('title', customTitle || userStore.getUserTranscriptTitle() || 'CherryClipz');
              params.append('adjusted_times', JSON.stringify(adjustedTimes));
              if (clipTimesJson) params.append('clip_times', clipTimesJson);
              if (roomMs) params.append('clip_pad_ms', String(roomMs));
              const res = await apiRequest(edlExportUrl, { method: 'POST', body: params });
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                messageStore.setMessage(err.message || 'EDL export failed', 'error');
                return { option, success: false };
              }
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              const safeName = (customTitle || userStore.getUserTranscriptTitle() || 'export')
                .substring(0, 50).replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/ +/g, '_');
              a.download = `${safeName || 'export'}.xml`;
              a.href = url;
              a.click();
              URL.revokeObjectURL(url);
              messageStore.setMessage('Premiere XML downloaded — import with File → Import in Premiere Pro', 'success', 10000);
              return { option, success: true };
            }

            // ARCHIVE Super Cut: swap in the word-level segments for this option only.
            let archiveOverride = {};
            if (archive && option === 'Super Cut (searched words only)') {
              const sc = archiveSupercutSegments();
              if (!sc.length) {
                messageStore.setMessage('Super Cut needs a searched word that appears in your ticked clips.', 'error');
                return { option, success: false, message: 'No occurrences of the searched word' };
              }
              archiveOverride = { archive_segments: JSON.stringify(sc) };
            }
            // Create a data object for this specific kind
            const isVideoKind = ['mp4', 'mp4_super_cut', 'audiogram'].includes(optionToKind[option]);
            const data = {
              ...baseData,
              kind: optionToKind[option],
              ...archiveOverride,
              orientation: orientation || 'original',
              ...(isVideoKind && burnCaptions && {
                burn_captions: 'true',
                // The export window's Font (the phone's four, bundled on the worker) wins.
                caption_font_key: captionFont || transcriptStyle?.fontKey || 'sans',
                caption_size: String(captionSize || 64),
                caption_mode: captionMode || 'word',
                words_per_group: String(captionWordsPerGroup || 3),
                // Palette key (cherry / allcherry / white / yellow); empty = the worker's default.
                ...(captionStyle ? { caption_style: captionStyle } : {}),
                // The export window's Style (outline / thick / box / shadow) and CAPITALS.
                ...(captionLook?.look ? { caption_look: captionLook.look } : {}),
                ...(captionLook?.upper ? { caption_upper: 'true' } : {}),
                ...(captionLook?.highlight ? { caption_highlight: captionLook.highlight } : {}),
                ...(captionLook?.reveal && captionLook.reveal !== 'all' ? { caption_reveal: captionLook.reveal } : {}),
                ...(captionLook?.bounce && captionLook.bounce !== 'none' ? { caption_bounce: captionLook.bounce } : {}),
                // A one-click caption preset (Story Stack draws the words itself)
                ...(captionLook?.preset ? { caption_preset: captionLook.preset } : {}),
                // Sharpness: captions burned on an HD / 4K frame (crisp on a low-res video)
                ...(captionLook?.res ? { caption_res: captionLook.res } : {}),
              }),
              // Transitions (export window): a start effect, and the join between stitched clips —
              // one for every join, and (Video / Audio + logo) the window's choice per join, when
              // its joins are this export's clips in this order.
              ...(isVideoKind && effects?.intro && effects.intro !== 'none' ? { intro: effects.intro } : {}),
              ...(isVideoKind && effects?.transition && effects.transition !== 'none' ? { transition: effects.transition } : {}),
              // Your logo (My Style) — {key, x, y, size, opacity}; the server checks it is yours.
              ...(isVideoKind && effects?.logo ? { logo: JSON.stringify(effects.logo) } : {}),
              ...(['mp4', 'audiogram'].includes(optionToKind[option]) && perJoin ? { transitions: JSON.stringify(perJoin) } : {}),
              // Breathing room: more time around every clip, for every export that cuts the media.
              ...(['mp4', 'audiogram', 'mp3', 'gif'].includes(optionToKind[option]) && roomMs ? { clip_pad_ms: String(roomMs) } : {}),
              // Reposition: per-clip pan/zoom/face + where the captions sit.
              ...(isVideoKind || optionToKind[option] === 'gif' ? {
                ...(clipLayouts.length ? { clip_layouts: JSON.stringify(clipLayouts) } : {}),
                ...(framing?.captionPos ? { caption_pos: JSON.stringify(framing.captionPos) } : {}),
              } : {}),
              // Super Cut handles: breathing room per word + "every cut as long as the longest".
              ...(optionToKind[option] === 'mp4_super_cut' && supercut && {
                supercut_pad_ms: String(Math.max(0, Math.round(supercut.padMs || 0))),
                supercut_equalize: supercut.equalize ? 'true' : 'false',
              }),
            };
            // One make-video call. "Each clip its own video" calls it once per clip (a pk or a
            // re-segmented span), numbering the titles; otherwise once for the stitched cut.
            const postOne = async (payload) => {
              const urlEncodedData = new URLSearchParams();
              for (const [key, value] of Object.entries(payload)) {
                if (value !== null) {
                  urlEncodedData.append(key, value);
                }
              }
              // Make API call (shared-ET guests post to the token-gated share export endpoint)
              const response = await apiRequest(isSharedMode ? shareMakeVideoUrl : makeVideoUrl, {
                method: "POST",
                mode: "cors",
                body: urlEncodedData,
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                  "X-CSRFToken": getCookie("csrftoken"),
                },
              });
              const result = await response.json();
              return { response, result };
            };
            const reportFailure = async ({ response, result }) => {
              if (response.status === 403) {
                const msg = await handleLimitError(
                  response,
                  "Export limit reached. Resets next month.",
                );
                messageStore.setMessage(msg, "error");
                return { option, success: false, message: msg };
              }
              messageStore.setMessage(
                `Failed to create ${option}: ${result.message || result.error}`,
                "error",
              );
              return {
                option,
                success: false,
                message: result.message || result.error,
              };
            };

            if (separate && isVideoKind && !archive) {
              const pieces = [
                ...exportIds.map((pk, k) => ({ list_of_ids: [pk], clip_times: clipTimes[k] ? JSON.stringify([clipTimes[k]]) : null, segments: '[]' })),
                ...segments.map((seg) => ({ list_of_ids: [], segments: JSON.stringify([seg]) })),
              ];
              const creationIds = [];
              let failure = null;
              for (let i = 0; i < pieces.length; i += 1) {
                const one = await postOne({
                  ...data,
                  ...pieces[i],
                  title: `${data.title || 'CherryClipz'} (${i + 1} of ${pieces.length})`,
                });
                if (one.response.status !== 200) {
                  failure = await reportFailure(one);
                  if (one.response.status === 403) break;
                } else {
                  creationIds.push(one.result.creation_id);
                }
              }
              if (!creationIds.length) return failure || { option, success: false };
              return { option, success: true, creationId: creationIds[0], creationIds };
            }

            const one = await postOne(data);
            if (one.response.status !== 200) return reportFailure(one);
            return { option, success: true, creationId: one.result.creation_id };
          } catch (error) {
            messageStore.setMessage(
              `Error creating ${option}: ${error.message}`,
              "error",
            );
            return { option, success: false, message: error.message };
          }
        }),
      );
      // Process results
      const successfulOperations = results.filter((r) => r.success);
      // Show a summary message
      if (successfulOperations.length > 0) {
        const exportNames = successfulOperations.map((r) => {
          const opt = r.option || '';
          if (opt.includes('MP3')) return 'MP3 audio';
          if (opt.includes('GIF')) return 'GIF';
          if (opt.includes('SRT')) return 'SRT file';
          if (opt.includes('VTT')) return 'VTT file';
          if (opt.includes('zip')) return 'SRT stems zip';
          if (opt.includes('EDL')) return 'EDL';
          if (opt.includes('transcript')) return 'transcript';
          if (opt.includes('Super Cut')) return 'Super Cut';
          return 'video';
        });
        const uniqueNames = [...new Set(exportNames)];
        const listText = uniqueNames.length === 1
          ? uniqueNames[0]
          : uniqueNames.slice(0, -1).join(', ') + ' & ' + uniqueNames.slice(-1);
        if (isSharedMode) {
          // Guests have no Library, so we can't point them there. Instead poll each finished
          // export and download it straight to their device — the multi-clip counterpart to
          // the per-clip ⬇ arrow. (A copy still lands in the owner's Library server-side.)
          const withIds = successfulOperations.filter((r) => r.creationId);
          if (withIds.length) {
            const staffOwner = staffShareOwner();
            messageStore.setMessage(
              staffOwner
                ? `Sent to ${staffOwner}’s library ✓ You can close this tab — or stay and it will download here too.`
                : `Building your ${listText}… it’ll download automatically when ready (this can take a moment).`,
              'info',
              12000,
            );
            withIds.forEach((r) => (r.creationIds || [r.creationId]).forEach((cid) => pollAndDownloadSharedCreation(cid)));
          } else {
            messageStore.setMessage(`Your ${listText} ${uniqueNames.length === 1 ? 'is' : 'are'} processing!`, 'success', 8000);
          }
        } else {
          messageStore.setMessage(
            `Your CherryClipz ${listText} ${uniqueNames.length === 1 ? 'is' : 'are'} processing!`,
            "success",
            8000,
            { text: 'View in My Library', url: '/my-library/exports/', newTab: true }
          );
        }
      }
      return results;
    };
    createMidia(selectedOptions, customTitle, orientation, burnCaptions, captionMode, captionSize, captionWordsPerGroup, captionStyle, separate, captionFont)
      .then(() => {})
      .catch((error) => {
        console.error("Error in createMidia:", error);
      });
  };

  const makeMidiaWithWarning = async (selectedOptions, customTitle, orientation = 'original', burnCaptions = false, captionMode = 'word', captionSize = 64, captionWordsPerGroup = 3, captionStyle = '', separate = false, captionFont = '', supercut = null, framing = null, captionLook = null, effects = null) => {
    const durationSec = (userStore.getDurationInMs() || 0) / 1000;
    // Free accounts have a hard length cap (usage-limits `export_max_seconds`, 5 min by
    // default — a long export ties up the worker everyone shares); paid plans only get the
    // warning below. A share-link guest's export is the owner's, so no cap is read here.
    let capSeconds = null;
    const isSharedMode = typeof window !== 'undefined' && window.location.pathname.startsWith('/s/');
    if (durationSec > 60 && !isSharedMode) {
      try {
        const res = await apiRequest(getUsageLimitsUrl, { method: "GET" });
        if (res.ok) capSeconds = (await res.json()).export_max_seconds ?? null;
      } catch (e) { /* unknown → no cap; the long-export warning still applies */ }
    }
    const overCap = !!capSeconds && durationSec > capSeconds;
    if (overCap || durationSec > 300) {
      setExportWarning({ selectedOptions, customTitle, orientation, burnCaptions, captionMode, captionSize, captionWordsPerGroup, captionStyle, separate, captionFont, supercut, framing, captionLook, effects, capSeconds: overCap ? capSeconds : null });
      return;
    }
    makeMidia(selectedOptions, customTitle, orientation, burnCaptions, captionMode, captionSize, captionWordsPerGroup, captionStyle, separate, captionFont, supercut, framing, captionLook, effects);
  };

  // Supercut: every occurrence of the searched term inside one clip, as
  // {start,end} pairs from the row's word timings (utterance[i] = [start,end]
  // for word i). Single word matches as a substring — mirroring the backend
  // Super Cut export (Word__icontains), so "america" also catches "american";
  // a multi-word phrase matches an exact consecutive run.
  // Breathing room on each supercut word, in seconds — user-controlled from the
  // panel (✂ buffer button) rather than a hidden constant, so it's visible and
  // can be zeroed. Clamped so the start never goes negative; words from
  // different clips never touch, so a pad can't overlap.
  const supercutPadRef = useRef(0);
  const supercutOccurrences = (row, phrase) => {
    const utt = row && row.utterance;
    if (!utt || !utt.length || !phrase) return [];
    const terms = phrase.split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    const single = terms.length === 1;
    const words = (row.sentence || "")
      .toLowerCase()
      .replace(/[.,!?;:"'’“”()\[\]]/g, "")
      .split(/\s+/);
    const out = [];
    for (let i = 0; i + terms.length <= words.length; i++) {
      let match = true;
      for (let j = 0; j < terms.length; j++) {
        const w = words[i + j];
        if (single ? !w.includes(terms[j]) : w !== terms[j]) { match = false; break; }
      }
      if (match) {
        const s = utt[i] && utt[i][0];
        const e = utt[i + terms.length - 1] && utt[i + terms.length - 1][1];
        if (s != null && e != null && e > s) {
          const pad = supercutPadRef.current || 0;
          out.push({ start: Math.max(0, s - pad), end: e + pad });
        }
        i += terms.length - 1; // don't re-match inside this run
      }
    }
    return out;
  };

  const getPenTimecodes = (clipId, utterance, row = null) => {
    const highlight = filterStore.getHighlight(clipId);
    if (!highlight || !utterance || !utterance.length) return null;
    const start = utterance[highlight.start]?.[0];
    const end = utterance[highlight.end]?.[1];
    if (start === undefined || end === undefined) return null;
    // Apply any +/- breathing room the user nudged onto the highlight (padStart pulls the
    // in-point earlier, padEnd pushes the out-point later). Clamp so it can't invert.
    let sPad = Math.max(0, start - (highlight.padStart || 0));
    let ePad = Math.max(sPad + 0.05, end + (highlight.padEnd || 0));
    // "Include borrowed words": stretch the highlight to the clip's own (already-extended)
    // bounds, which is where the red stolen words live. incAfter → reach to end_in (the
    // borrowed-after words); incBefore → reach back to start_in (the borrowed-before words).
    if (row) {
      if (highlight.incAfter && row.end_in != null) ePad = Math.max(ePad, row.end_in);
      if (highlight.incBefore && row.start_in != null) sPad = Math.min(sPad, Math.max(0, row.start_in));
    }
    return { start_in: sPad, end_in: ePad };
  };

  // Shared-ET guests have no Library, so a stitched Export can't "appear in My Library" for
  // them. Instead we poll the export the worker is building and, the moment it's done, stream
  // it straight to their device — the multi-clip counterpart to the per-clip ⬇ arrow. A
  // persistent "stitching…" indicator (sharedExportBuilding) covers the build gap. A copy of
  // the export still lands in the OWNER's Library server-side.
  // On the team's "Open ET" link the export is the CLIENT's (it lands in their library and in
  // the admin's exports list), so the tab is free to close: wait much longer, and never ask
  // for a second export, which would only make a duplicate.
  const staffShareOwner = () => {
    try {
      return sessionStorage.getItem('cc_share_staff') === '1' ? (sessionStorage.getItem('cc_share_owner') || 'the owner') : null;
    } catch (e) { return null; }
  };

  const pollAndDownloadSharedCreation = async (creationId) => {
    if (!creationId) return;
    setSharedExportBuilding((n) => n + 1);
    const startTs = Date.now();
    const staffOwner = staffShareOwner();
    const TIMEOUT_MS = (staffOwner ? 30 : 6) * 60 * 1000;
    const POLL_MS = 4000;
    try {
      while (Date.now() - startTs < TIMEOUT_MS) {
        await new Promise((res) => setTimeout(res, POLL_MS));
        let data;
        try {
          const res = await apiRequest(`${creationStatusSharedUrl}?creation_id=${creationId}`, { method: 'GET' });
          if (!res.ok) continue;
          data = await res.json();
        } catch (e) {
          continue;
        }
        if (data && data.ready) {
          try {
            const fileRes = await apiRequest(`${creationDownloadSharedUrl}?creation_id=${creationId}`, { method: 'GET' });
            if (!fileRes.ok) {
              messageStore.setMessage('Your clip is ready but the download failed — please try Export again.', 'error');
              return;
            }
            const blob = await fileRes.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            const fallback = (data.title || 'clip').substring(0, 50).replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/ +/g, '_') || 'clip';
            a.download = data.file_name || `${fallback}.mp4`;
            a.href = url;
            a.click();
            URL.revokeObjectURL(url);
            messageStore.setMessage('Your clip is ready — downloaded to your device. 🍒', 'success', 8000);
          } catch (e) {
            messageStore.setMessage('Download error: ' + e.message, 'error');
          }
          return;
        }
      }
      messageStore.setMessage(
        staffOwner
          ? `Still stitching. It will be in ${staffOwner}’s library and the admin’s exports list when it’s done — no need to export again.`
          : 'Still stitching — it’s taking longer than usual. Please try Export again in a moment.',
        'info', 10000);
    } finally {
      setSharedExportBuilding((n) => Math.max(0, n - 1));
    }
  };

  /** Hand a blob to the device as a file download. The ending both the Save button and every
   *  browser that can't share arrive at. */
  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.download = filename;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onSingleClipDownload = async (rowData) => {
    // Premiere panel: exports run through PREMIERE (full quality, local file,
    // no server render) — hand the clip over and let the panel drive.
    if (getEmbedParentOrigin() === "file://") {
      const adjP = adjustmentsRef.current[rowData.id];
      postToEmbedParent({
        type: "cc-et-export",
        scope: "clip",
        id: rowData.id,
        start: adjP ? adjP.start_in : (rowData.start_in ?? 0),
        end: adjP ? adjP.end_in : (rowData.end_in ?? 0),
        sentence: rowData.sentence || "",
      });
      return;
    }
    const cut = await trimClip(rowData);
    if (!cut) return;
    // With the Share switch OFF this button must behave exactly as it always did: download,
    // full stop. The "ready" card belongs to the sharing work and is gated with it, so a
    // merge with every switch off changes nothing for anyone.
    saveBlob(cut.blob, cut.filename);
    messageStore.setMessage('Clip downloaded', 'success');
  };

  /**
   * Share ONE clip — the row's Share button and the Share pill both land here.
   * Opens the share box when that switch is on; otherwise cuts and offers the sheet directly.
   */
  const onSingleClipShare = async (rowData) => {
    if (!rowData) return;
    if (labs.postFlow) {
      setShareBoxFor({ row: rowData, title: clipTitleOf(rowData), timecode: clipTimecodeOf(rowData) });
      return;
    }
    const cut = await trimClip(rowData);
    if (!cut) return;
    if (canShareFiles()) {
      setPendingShare({ blob: cut.blob, filename: cut.filename, text: clipShareText(cut.title) });
      return;
    }
    saveBlob(cut.blob, cut.filename);
    messageStore.setMessage('Sharing a file only works on a phone \u2014 saved to your Downloads instead.', 'info', 8000);
  };

  /** The words a clip is named by — the pen highlight if there is one, else the sentence. */
  const clipTitleOf = (rowData) => {
    const penHL = filterStore.getHighlight(rowData.id);
    return penHL && rowData.utterance && rowData.sentence
      ? rowData.sentence.split(' ').slice(penHL.start, penHL.end + 1).join(' ')
      : (rowData.sentence || 'clip');
  };

  const clipTimecodeOf = (rowData) => {
    const pen = getPenTimecodes(rowData.id, rowData.utterance, rowData);
    const adj = adjustmentsRef.current[rowData.id];
    const a = pen ? pen.start_in : (adj ? adj.start_in : (rowData.start_in ?? 0));
    const b = pen ? pen.end_in : (adj ? adj.end_in : (rowData.end_in ?? 0));
    // start_in / end_in are SECONDS here (the same numbers the Premiere bridge posts),
    // not the milliseconds durationFormat() takes.
    const hms = (sec) => {
      const t = Math.max(0, Math.floor(Number(sec) || 0));
      const p2 = (n) => String(n).padStart(2, '0');
      return `${p2(Math.floor(t / 3600))}:${p2(Math.floor(t / 60) % 60)}:${p2(t % 60)}`;
    };
    return `${hms(a)} \u2192 ${hms(b)} \u00b7 ${Math.max(0, Math.round(b - a))}s`;
  };

  /**
   * Cut one clip on the server and hand back the file.
   *
   * The single place a clip becomes a file — Download, Share and every platform button run
   * through here — so the "still working" indicator can never be forgotten by one caller.
   * Returns null on failure, having already told the user why.
   */
  const trimClip = async (rowData, stage = 'Cutting your clip…') => {
    const pen = getPenTimecodes(rowData.id, rowData.utterance, rowData);
    const adj = adjustmentsRef.current[rowData.id];
    const params = new URLSearchParams();
    params.append('sentence_id', rowData.id);
    params.append('transcript_id', userStore.getUserTranscriptId());
    params.append('start_in', pen ? pen.start_in : (adj ? adj.start_in : (rowData.start_in ?? 0)));
    params.append('end_in', pen ? pen.end_in : (adj ? adj.end_in : (rowData.end_in ?? 0)));

    // A toast that disappears after six seconds while the work carries on is why people tap
    // twice (Isaac, 2026-09-24). The card stays up until the file actually exists.
    if (labs.progress) setWorking({ stage, sub: 'Usually a few seconds. You can keep working.' });
    else messageStore.setMessage('Trimming clip…', 'info');
    try {
      const res = await apiRequest(clipDirectDownloadUrl, { method: 'POST', body: params });
      if (!res.ok) {
        const result = await res.json().catch(() => ({}));
        messageStore.setMessage(result.message || 'Download failed', 'error');
        return null;
      }
      const blob = await res.blob();
      const title = clipTitleOf(rowData);
      if (!labs.progress) messageStore.resetMessage();
      return { blob, filename: clipFileName(title), title };
    } catch (e) {
      messageStore.setMessage('Download error: ' + e.message, 'error');
      return null;
    } finally {
      setWorking(null);
    }
  };

  /**
   * This sheet's public /s/ link — the one you send someone so they can cut their own clips
   * from the same video, with no sign-in.
   *
   * On a /s/ page we are already standing on it, so there is nothing to ask for (and a guest
   * has no right to mint one anyway). Everywhere else it is created once and remembered.
   */
  const ensureSheetLink = async () => {
    if (sheetLink) return sheetLink;
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/s/')) {
      const here = `${window.location.origin}${window.location.pathname}`;
      setSheetLink(here);
      return here;
    }
    const transcriptId = userStore.getUserTranscriptId();
    if (!transcriptId) return '';
    setSheetLinkBusy(true);
    try {
      const body = new URLSearchParams();
      body.append('transcript_id', String(transcriptId));
      body.append('permission_level', 'export');
      const res = await apiRequest(createShareUrl, {
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url_path) {
        const url = `${window.location.origin}${data.url_path}`;
        setSheetLink(url);
        return url;
      }
      messageStore.setMessage(data.message || "Couldn't create a share link.", 'error');
    } catch (e) {
      messageStore.setMessage("Couldn't create a share link. Please try again.", 'error');
    } finally {
      setSheetLinkBusy(false);
    }
    return '';
  };

  // ── SHARE BOX ACTIONS ────────────────────────────────────────────────────────
  // Every one of these cuts the clip first (with the progress card up) and only then decides
  // where it goes, so nothing is rendered speculatively.

  /** "Share video" / "Save video" — the sheet if this device has one, else a download. */
  const shareBoxShare = async (caption) => {
    const ctx = shareBoxFor;
    if (!ctx) return;
    const cut = await trimClip(ctx.row);
    if (!cut) return;
    setShareBoxFor(null);
    if (canShareFiles()) {
      setPendingShare({ blob: cut.blob, filename: cut.filename, text: caption || clipShareText(cut.title) });
      return;
    }
    saveBlob(cut.blob, cut.filename);
    messageStore.setMessage('Clip saved to your Downloads.', 'success');
  };

  const shareBoxSave = async () => {
    const ctx = shareBoxFor;
    if (!ctx) return;
    const cut = await trimClip(ctx.row);
    if (!cut) return;
    setShareBoxFor(null);
    saveBlob(cut.blob, cut.filename);
    messageStore.setMessage('Clip downloaded', 'success');
  };

  /**
   * One platform button.
   *
   * A browser cannot put a file into another site's composer, so this does the most the device
   * allows: on a phone the system sheet carries the real mp4 and the user picks the app there;
   * on a laptop the clip lands in Downloads and the composer opens with the caption ready.
   */
  const shareBoxTarget = async (targetKey, caption) => {
    const ctx = shareBoxFor;
    const target = targetByKey(targetKey);
    if (!ctx || !target) return;
    const action = targetAction(target, canShareFiles());
    if (action === 'unavailable') {
      messageStore.setMessage(`${target.name} only takes uploads from its own app \u2014 open this on your phone.`, 'info', 8000);
      return;
    }
    const cut = await trimClip(ctx.row, `Cutting your clip for ${target.name}\u2026`);
    if (!cut) return;
    setShareBoxFor(null);

    if (action === 'sheet') {
      // NOT shareVideoFile() straight away. The trim above has already spent the tap that
      // started this, and a share outside a fresh user gesture throws NotAllowedError \u2014 which
      // fell through to the link route and opened WhatsApp with the words but no video
      // (Isaac, on his iPhone, 2026-09-24). The card gives the share a tap of its own.
      setPendingShare({
        blob: cut.blob,
        filename: cut.filename,
        text: caption || clipShareText(cut.title),
        target: target.name,
      });
      return;
    }
    const url = await ensureSheetLink();
    if (target.web) {
      // Open the composer BEFORE saving: Chrome's "where do you want to save this" sheet is
      // modal over the window, and saving first parked it on top of the tab we had just
      // opened, so Isaac cancelled the save to get at Facebook (2026-09-24).
      const win = window.open(target.web({ text: caption || cut.title, url: url || window.location.href }), '_blank', 'noopener');
      saveBlob(cut.blob, cut.filename);
      messageStore.setMessage(
        win ? `${target.name} is open in a new tab \u2014 your clip is saving to Downloads, drag it in.`
            : `Clip saved to your Downloads. ${target.name} was blocked from opening \u2014 allow pop-ups to jump straight there.`,
        win ? 'success' : 'info', 9000);
    } else {
      saveBlob(cut.blob, cut.filename);
      messageStore.setMessage('Clip saved to your Downloads.', 'success');
    }
  };

  /** The spreadsheet link buttons at the bottom of the share box. */
  const shareBoxSheetLink = async (how) => {
    const url = await ensureSheetLink();
    if (!url) return;
    if (how === 'copy') {
      try {
        await navigator.clipboard.writeText(url);
        messageStore.setMessage('\ud83d\udd17 Link copied.', 'success');
      } catch (e) {
        messageStore.setMessage('Couldn\u2019t copy \u2014 the link is shown in the box.', 'info');
      }
      return;
    }
    const result = await shareLink({ url, title: 'CherryClipz', text: 'Cut your own clips from this:' });
    if (result === 'copied') messageStore.setMessage('\ud83d\udd17 Link copied.', 'success');
    else if (result === 'shared') messageStore.setMessage('Link shared \ud83c\udf52', 'success');
    else if (result === 'error') messageStore.setMessage('Couldn\u2019t share that link.', 'error');
  };

  const onPrecisionChange = () => {
    setPrecision((prevPrecision) => {
      const newPrecision = !prevPrecision[currentView];
      return {
        ...prevPrecision,
        [currentView]: newPrecision,
      };
    });
  };

  const onVideoWidthChange = (width) => {
    filterStore.setVideoWidth(width, currentView);
  };

  // Convert float seconds to "HH:MM:SS.mmm" for the Clock display
  const secondsToHMS = (totalSeconds) => {
    const ms = Math.round((totalSeconds % 1) * 1000);
    const s = Math.floor(totalSeconds) % 60;
    const m = Math.floor(totalSeconds / 60) % 60;
    const h = Math.floor(totalSeconds / 3600) % 24;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
  };

  // Adjusts a row's in/out points by deltaStart and deltaEnd (each ±1 = ±0.1s, see STEP below).
  // _adjustedBy:'user' = manually changed (shows cyan).
  // _adjustedBy:'cascade' = automatically pushed to avoid overlap (shows amber).
  //
  // Cascade rules (like Premiere's slip/ripple trim):
  //   - Extend OUT → push subsequent clips' IN points forward to avoid forward overlap
  //   - Move IN earlier → push previous clips' OUT points back to avoid backward overlap
  //
  // Changes are in-memory only — reload resets them.
  const onAdjustTimes = (itemData, deltaStart, deltaEnd) => {
    const STEP = 0.1;
    // If this clip is pen-highlighted, the +/- buttons nudge the HIGHLIGHT's breathing room
    // (give the kept phrase more/less space) instead of sliding the whole clip. The words
    // stay the same — only the in/out padding changes, and the display + export read it live.
    const hl = filterStore.getHighlight(itemData.id);
    if (hl) {
      if (!precision[currentView]) {
        setPrecision((prev) => ({ ...prev, [currentView]: true }));
      }
      const padStart = (hl.padStart || 0) - deltaStart * STEP; // "-" on IN → pull in earlier → more room
      const padEnd = (hl.padEnd || 0) + deltaEnd * STEP;        // "+" on OUT → push out later → more room
      filterStore.setHighlightPad(itemData.id, padStart, padEnd);
      return;
    }
    // Auto-reveal decimals so the adjusted timestamps show millisecond precision
    if (!precision[currentView]) {
      setPrecision((prev) => ({ ...prev, [currentView]: true }));
    }
    // Snapshot current state for undo before making changes
    undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)] });
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
    setRows((prev) => {
      const updated = prev.map((r) => ({ ...r }));
      const targetIdx = updated.findIndex((r) => r.id === itemData.id);
      if (targetIdx === -1) return prev;

      const row = updated[targetIdx];

      // Sort first so we can find neighbors before computing capped bounds
      // Exclude space rows — they are visual-only and must not be treated as clip neighbors
      const byTime = [...updated].filter(r => !r.space).sort((a, b) => a.start_in - b.start_in);
      const targetInTime = byTime.findIndex((r) => r.id === itemData.id);

      // Compute tentative new bounds
      let newStart = Math.max(0, row.start_in + deltaStart * STEP);
      let newEnd = Math.max(newStart + 0.1, row.end_in + deltaEnd * STEP);

      // Cap: don't extend OUT past the next clip's end ONLY if that next clip is one you're
      // actually using (selected) — otherwise let it vanish, the user wants its material.
      if (deltaEnd > 0 && targetInTime + 1 < byTime.length) {
        const nextClip = byTime[targetInTime + 1];
        if (filterStore.isSelectedSentence(nextClip.id, currentView)) {
          const hardMax = nextClip.end_in - 0.1;
          if (newEnd > hardMax) newEnd = Math.max(newStart + 0.1, hardMax);
        }
      }
      // Cap: don't move IN before the previous clip's start ONLY if that previous clip is selected.
      if (deltaStart < 0 && targetInTime - 1 >= 0) {
        const prevClip = byTime[targetInTime - 1];
        if (filterStore.isSelectedSentence(prevClip.id, currentView)) {
          const hardMin = prevClip.start_in + 0.1;
          if (newStart < hardMin) { newStart = hardMin; newEnd = Math.max(newStart + 0.1, newEnd); }
        }
      }

      // Check if values are back to original — if so, clear the adjustment
      const origRow = originalRows.find(o => o.id === itemData.id);
      const backToOriginal = origRow &&
        Math.abs(newStart - origRow.start_in) < 0.05 &&
        Math.abs(newEnd - origRow.end_in) < 0.05;

      const adjDurSec = backToOriginal ? (origRow.end_in - origRow.start_in) : (newEnd - newStart);
      updated[targetIdx] = {
        ...row,
        start_in: backToOriginal ? origRow.start_in : newStart,
        end_in: backToOriginal ? origRow.end_in : newEnd,
        duration_ms: adjDurSec * 1000,
        duration: backToOriginal
          ? [secondsToHMS(origRow.start_in), secondsToHMS(origRow.end_in)]
          : [secondsToHMS(newStart), secondsToHMS(newEnd)],
        sec: adjDurSec.toFixed(2),
        sec_int: Math.floor(adjDurSec),
        sec_float: parseFloat(adjDurSec.toFixed(2)),
        _adjustedBy: backToOriginal ? undefined : 'user',
      };
      // Persist to ref so the adjustment survives filter/sort re-renders
      if (backToOriginal) {
        const { [itemData.id]: _removed, ...rest } = adjustmentsRef.current;
        adjustmentsRef.current = rest;
      } else {
        adjustmentsRef.current = { ...adjustmentsRef.current, [itemData.id]: { start_in: newStart, end_in: newEnd } };
      }

      // OUT extended → push ONLY the immediate next clip's IN point forward.
      // Its OUT stays fixed (clip gets shorter). No chain cascade — only one neighbor moves.
      if (newEnd > row.end_in && targetInTime + 1 < byTime.length) {
        const next = byTime[targetInTime + 1];
        if (next.start_in < newEnd) {
          const nextIdx = updated.findIndex((r) => r.id === next.id);
          const keptEnd = updated[nextIdx].end_in;
          const adjStart = newEnd; // guaranteed < keptEnd - 0.1 due to hard cap above
          const nextDur = Math.max(0, keptEnd - adjStart);
          updated[nextIdx] = {
            ...updated[nextIdx],
            start_in: adjStart,
            duration_ms: nextDur * 1000,
            duration: [secondsToHMS(adjStart), secondsToHMS(keptEnd)],
            sec: nextDur.toFixed(2),
            sec_int: Math.floor(nextDur),
            sec_float: parseFloat(nextDur.toFixed(2)),
            _adjustedBy: updated[nextIdx]._adjustedBy || 'cascade',
          };
          adjustmentsRef.current = { ...adjustmentsRef.current, [next.id]: { start_in: adjStart, end_in: keptEnd } };
        }
      }

      // IN moved earlier → push ONLY the immediate previous clip's OUT point back.
      // Its IN stays fixed (clip gets shorter). No chain cascade — only one neighbor moves.
      if (newStart < row.start_in && targetInTime - 1 >= 0) {
        const prevClip = byTime[targetInTime - 1];
        if (prevClip.end_in > newStart) {
          const prevIdx = updated.findIndex((r) => r.id === prevClip.id);
          const keptStart = updated[prevIdx].start_in;
          const adjEnd = newStart; // guaranteed > keptStart + 0.1 due to hard cap above
          const prevDur = Math.max(0, adjEnd - keptStart);
          updated[prevIdx] = {
            ...updated[prevIdx],
            end_in: adjEnd,
            duration_ms: prevDur * 1000,
            duration: [secondsToHMS(keptStart), secondsToHMS(adjEnd)],
            sec: prevDur.toFixed(2),
            sec_int: Math.floor(prevDur),
            sec_float: parseFloat(prevDur.toFixed(2)),
            _adjustedBy: updated[prevIdx]._adjustedBy || 'cascade',
          };
          adjustmentsRef.current = { ...adjustmentsRef.current, [prevClip.id]: { start_in: keptStart, end_in: adjEnd } };
        }
      }

      // ---- Text stealing markers ----
      // Recompute visual markers based on how each clip moved from its original bounds.
      const origA = originalRows.find(o => o.id === itemData.id);
      if (origA) {
        const curA = updated[targetIdx];

        // OUT extended past original → stole words from next clip's beginning
        if (curA.end_in > origA.end_in + 0.05 && targetInTime + 1 < byTime.length) {
          const nextRef = byTime[targetInTime + 1]; // pre-cascade ref retains original utterance
          const nextWords = (nextRef.sentence || '').split(' ');
          const stolen = [];
          let stolenTimeAfter = null;
          if (nextRef.utterance) {
            nextRef.utterance.forEach((wt, i) => {
              if (wt[0] >= origA.end_in - 0.05 && wt[0] < curA.end_in + 0.05 && nextWords[i]) {
                if (stolenTimeAfter === null) stolenTimeAfter = wt[0];
                stolen.push(nextWords[i]);
              }
            });
          }
          updated[targetIdx] = { ...updated[targetIdx], _stolenTextAfter: stolen.length ? stolen.join(' ') : null, _stolenTimeAfter: stolenTimeAfter };
        } else {
          updated[targetIdx] = { ...updated[targetIdx], _stolenTextAfter: null, _stolenTimeAfter: null };
        }

        // IN extended past original → stole words from prev clip's end
        if (curA.start_in < origA.start_in - 0.05 && targetInTime - 1 >= 0) {
          const prevRef = byTime[targetInTime - 1];
          const prevWords = (prevRef.sentence || '').split(' ');
          const stolen = [];
          let stolenTimeBefore = null;
          if (prevRef.utterance) {
            prevRef.utterance.forEach((wt, i) => {
              if (wt[0] >= curA.start_in - 0.05 && wt[0] < origA.start_in + 0.05 && prevWords[i]) {
                if (stolenTimeBefore === null) stolenTimeBefore = wt[0];
                stolen.push(prevWords[i]);
              }
            });
          }
          updated[targetIdx] = { ...updated[targetIdx], _stolenTextBefore: stolen.length ? stolen.join(' ') : null, _stolenTimeBefore: stolenTimeBefore };
        } else {
          updated[targetIdx] = { ...updated[targetIdx], _stolenTextBefore: null, _stolenTimeBefore: null };
        }
      }

      // Next clip: mark _clippedStart if its IN was pushed forward past original
      if (targetInTime + 1 < byTime.length) {
        const nextRef = byTime[targetInTime + 1];
        const origNext = originalRows.find(o => o.id === nextRef.id);
        const nextIdx2 = updated.findIndex(r => r.id === nextRef.id);
        if (nextIdx2 !== -1 && origNext) {
          updated[nextIdx2] = { ...updated[nextIdx2], _clippedStart: updated[nextIdx2].start_in > origNext.start_in + 0.05 };
        }
      }

      // Prev clip: mark _clippedEnd if its OUT was pushed back past original
      if (targetInTime - 1 >= 0) {
        const prevRef2 = byTime[targetInTime - 1];
        const origPrev = originalRows.find(o => o.id === prevRef2.id);
        const prevIdx2 = updated.findIndex(r => r.id === prevRef2.id);
        if (prevIdx2 !== -1 && origPrev) {
          updated[prevIdx2] = { ...updated[prevIdx2], _clippedEnd: updated[prevIdx2].end_in < origPrev.end_in - 0.05 };
        }
      }

      return updated;
    });
    // Embed bridge: a trim made INSIDE the ET must also reach the parent timeline.
    // (No-op unless framed by an allowlisted parent — broadcast checks the origin.)
    window.setTimeout(() => { if (embedBroadcastRef.current) embedBroadcastRef.current(); }, 90);
  };
  // Keep the embed command listener pointed at the live handler (defined above).
  embedAdjustRef.current = onAdjustTimes;

  // "Open in full transcript" — drop everything that's narrowing or reordering the
  // list (search, every filter, the sort) and scroll to this exact clip so the user
  // sees it among its neighbours. KEEPS the user's selection and the current
  // segmentation, so their picks survive and the clip stays findable by id.
  const onJumpToItem = (itemData) => {
    if (closeSearchModalRef) closeSearchModalRef();
    const targetId = itemData.id;
    const targetStart = itemData.start_in;
    shouldStopVideo();

    const clearedQF = { selectedOnly: false, hasQuestion: false, firstLetter: null, durationSec: null, containsNumbers: false, entityTypes: [], contentFlags: [], keyPhrases: [], filterVerbs: [], filterNouns: [], filterAdjectives: [] };

    filterStore.setSearch(currentView, "");
    // Clear sentiment by toggling off whichever sentiments are currently on
    // (iterates a snapshot, so toggling mid-loop is safe).
    filterStore.getSentimentsTrue(currentView).forEach((k) => filterStore.setSentiments(k, currentView));
    filterStore.resetAllSpeakers();
    filterStore.setSeconds(secondsBounds, currentView);
    filterStore.setTextLen(textLenBounds, currentView);
    // Reveal context WITH spaces on, so the silent gaps around the clip are visible.
    filterStore.setSpacesTo(currentView, true);
    setSpacesMode('show');
    setSortOrder({ field: null, direction: 'asc', column: null, label: null });
    setQuickFilters(clearedQF);

    // Rebuild the visible rows NOW from the full base (don't wait on the async React
    // state updates) so the list is the complete, in-order transcript before we scroll.
    let fresh = [...getDataForCurrentView(currentView)];
    fresh = filterSearch(fresh);
    fresh = filterSource(fresh);
    fresh = filterSentiment(fresh);
    fresh = filterSpeakers(fresh);
    fresh = filterSeconds(fresh);
    fresh = filterTextLen(fresh);
    fresh = applyQuickFilters(fresh, clearedQF);
    setRows(fresh);
    calcDuration(fresh);

    setTimeout(() => {
      let idx = fresh.findIndex((row) => row.id === targetId);
      // If the clip was a re-segmented one that no longer matches by id, fall back to
      // the clip whose time range covers it.
      if (idx === -1 && targetStart != null) {
        idx = fresh.findIndex((row) => (row.start_in <= targetStart && (row.end_in ?? row.start_in) >= targetStart));
      }
      if (idx !== -1 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({ index: idx, align: "center", behavior: "smooth" });
      }
    }, 100);
  };

  // The list is "narrowed" (search / any filter / sort active) → show the per-clip
  // "open in full transcript" jump button above each timecode. Recomputed each render.
  const qf = quickFilters || {};
  // Which columns are actively filtering. The header turns cherry so you can see at a
  // glance WHY the list is short, and where to click to undo it.
  const activeFilterFields = useMemo(() => {
    const f = new Set();
    const q = quickFilters || {};
    if (filterStore.getSearch(currentView)) f.add('sentence');
    const sec = filterStore.getSeconds(currentView) || [];
    if (secondsBounds.length && (sec[0] !== secondsBounds[0] || sec[1] !== secondsBounds[1])) f.add('sec');
    const len = filterStore.getTextLen(currentView) || [];
    if (textLenBounds.length && (len[0] !== textLenBounds[0] || len[1] !== textLenBounds[1])) f.add('length');
    const spk = filterStore.getSpeakers(currentView) || {};
    if (Object.keys(spk).some((k) => spk[k])) f.add('speaker');
    const sent = filterStore.getSentiments(currentView) || {};
    if (Object.keys(sent).some((k) => sent[k])) f.add('sentiment');
    if (q.hasQuestion) f.add('question');
    if (q.containsNumbers) f.add('contains_numbers');
    if ((q.filterVerbs || []).length) f.add('verbs');
    if ((q.filterNouns || []).length) f.add('nouns');
    if ((q.filterAdjectives || []).length) f.add('adjectives');
    return f;
  }, [quickFilters, currentView, rows, secondsBounds, textLenBounds, filterStore]);

  const isViewNarrowed = (
    filterStore.getSearch(currentView).length > 0 ||
    sortOrder.field != null ||
    filterStore.getSentimentsTrue(currentView).length > 0 ||
    filterStore.getSpeakersTrue(currentView).length > 0 ||
    spacesMode === 'show' || spacesMode === 'only' ||
    !!qf.hasQuestion || !!qf.containsNumbers || !!qf.selectedOnly ||
    !!qf.firstLetter || qf.durationSec != null ||
    (qf.entityTypes || []).length > 0 || (qf.contentFlags || []).length > 0 ||
    (qf.keyPhrases || []).length > 0 || (qf.filterVerbs || []).length > 0 ||
    (qf.filterNouns || []).length > 0 || (qf.filterAdjectives || []).length > 0 ||
    (secondsBounds[1] > 0 && (filterStore.getSeconds(currentView)[0] > secondsBounds[0] || filterStore.getSeconds(currentView)[1] < secondsBounds[1])) ||
    (textLenBounds[1] > 0 && (filterStore.getTextLen(currentView)[0] > textLenBounds[0] || filterStore.getTextLen(currentView)[1] < textLenBounds[1]))
  );

  // ── AI clip picker (no API) ────────────────────────────────────────────────
  // Build a copy-paste pack: instructions + the clips as JSON (each with its id),
  // so ChatGPT/Gemini picks from your REAL whole-sentence clips by id. Two modes:
  //   'rough'   → return whole clips (a list of ids)
  //   'precise' → may also trim each clip to its best phrase (drives the pen tool)
  // The AI picker works on whatever view you're in: the sentence clips in Create-a-Video,
  // or the bigger TOPIC CHUNKS in Categories/AIB (great for "pick whole segments", and the
  // precise pen-trim can carve a long chunk down to its best line). Returns that view's rows.
  const aiSourceRows = () => {
    if (currentView === 'AIB') return originalAIB;
    if (currentView === 'Categories') return originalCategories;
    return originalRows;
  };

  const buildAiClipPack = (mode = 'rough', goal = '', precise = false) => {
    // Lean clips: let the AI READ the transcript and form its OWN understanding rather
    // than feeding it noisy AssemblyAI entity dumps. id + text + who is speaking + the
    // emotional read is plenty for editorial judgment; timecodes/entities are omitted
    // (CherryClipz looks those up on the way back).
    const sorted = [...aiSourceRows()]
      .filter((r) => !r.space && r.sentence)
      .sort((a, b) => (a.start_in || 0) - (b.start_in || 0));
    // ARCHIVE: clips grouped per video with "v" + STRING ids; "gap" measured inside each video.
    const clips = archive ? buildArchiveClips(sorted) : sorted.map((r, i) => {
      const next = sorted[i + 1];
      const dur = Math.max(0, (r.end_in ?? 0) - (r.start_in ?? 0));            // how long this line takes
      const gap = next ? Math.max(0, (next.start_in ?? 0) - (r.end_in ?? 0)) : 0; // silence after it
      return {
        id: r.id,
        text: r.sentence,
        speaker: r.speaker || undefined,
        sentiment: r.sentiment || undefined,
        sec: Number(dur.toFixed(1)),   // duration — for rhythm/pacing
        gap: Number(gap.toFixed(1)),   // silence after — for pauses/suspense
      };
    });
    // ARCHIVE: the multi-video preamble every mode gets (which videos, which speakers are linked,
    // timecodes restart per video), plus string-id examples so the model copies ids exactly
    // ("6_456") instead of inventing bare numbers — the July "I only have one transcript" bug.
    const archiveLines = archive ? archivePreambleLines(archive.sources, sorted) : [];
    const exIds = archive ? archiveExampleIds(clips) : [12, 47, 88];
    const q = (id) => (archive ? `"${id}"` : String(id));
    const exArr = `[${exIds.map(q).join(', ')}]`;
    const exRough = `[ {"id": ${q(exIds[0])}}, {"id": ${q(exIds[1])}}, {"id": ${q(exIds[2])}} ]`;
    const exPrecise = `[ {"id": ${q(exIds[0])}, "keep": "we were in a war"}, {"id": ${q(exIds[1])}}, {"id": ${q(exIds[2])}, "keep": "for the family"} ]`;
    const exPrecise2 = `[ {"id": ${q(exIds[0])}, "keep": "we were in a war"}, {"id": ${q(exIds[1])}, "keep": "for the family"}, {"id": ${q(exIds[2])}} ]`;
    const exVir = `${exIds[0]}:88, ${exIds[1]}:21, ${exIds[2]}:54`;
    const withV = (c, extra) => (archive ? { id: c.id, v: c.v, ...extra } : { id: c.id, ...extra });

    // 'filler' = a one-shot classifier. The AI reads every clip and returns ONLY the ids of
    // the throwaway ones, so CherryClipz can fade / hide / isolate them. No prose, just ids.
    if (mode === 'filler') {
      return [
        'You are a sharp documentary editor. Below is a video as a JSON list of clips, each with an "id" and spoken "text".',
        ...archiveLines,
        '',
        'Find the FILLER — the clips that carry no real meaning and that an editor would cut first:',
        '• Verbal throat-clearing and hesitation: "um", "uh", "you know", "like", "so", "I mean", "basically", "right?", "okay".',
        '• False starts, restarts, and self-corrections that say nothing on their own.',
        '• Run-up / wind-down words and dead air that only exist to get to (or trail off from) the real point.',
        '• Pure repetition of a thing already said, and stock pleasantries ("thank you", "as I said") with no content.',
        '',
        'KEEP (do NOT mark as filler) anything with real meaning: a claim, a fact, a story beat, emotion, a question that matters, a punchline. When unsure, do NOT mark it — only flag clips you are confident are throwaway.',
        '',
        'Return ONLY a single json code block: an array of the filler clip ids, nothing else. Example:',
        '```json',
        exArr,
        '```',
        'No prose, no explanation — just the json array. Pick only from the ids below.',
        '',
        'CLIPS:',
        JSON.stringify(clips.map((c) => withV(c, { text: c.text }))),
      ].join('\n');
    }

    // 'virality' = score every clip 0–100 for shareability. Returns {id, score} pairs only.
    if (mode === 'virality') {
      return [
        'You are a viral-content strategist who has studied what makes short clips spread on TikTok, Reels, YouTube Shorts, and X.',
        'Below is a video as a JSON list of clips, each with an "id", spoken "text", "speaker", and "sentiment".',
        ...archiveLines,
        '',
        'Score EVERY clip from 0 to 100 for its standalone viral potential — how likely THIS line, on its own, would make someone stop scrolling, react, quote it, or share it.',
        'Score HIGH (70–100): bold or surprising claims, strong emotion, conflict or a hot take, a vivid story beat, humor, a punchy quotable one-liner, something that makes you go "wait, what?".',
        'Score MID (40–69): interesting or substantive but not explosive on its own.',
        'Score LOW (0–39): setup, logistics, pleasantries, filler, throat-clearing, or lines that mean nothing without context.',
        'Judge the LINE ITSELF, not the topic in general. Be decisive and spread the scores out — do not cluster everything in the middle.',
        '',
        'Return ONLY the scores as compact "id:score" pairs separated by commas — NO JSON, no clip text, no prose, no explanation. This keeps the reply short so it never gets cut off. Example:',
        exVir,
        'Score EVERY id below, in order.',
        '',
        'CLIPS:',
        JSON.stringify(clips.map((c) => withV(c, { text: c.text, speaker: c.speaker, sentiment: c.sentiment }))),
      ].join('\n');
    }

    // 'chat' = the in-app live assistant. Short, friendly replies; soundbites by TEXT;
    // ids hidden in a json block the app reads but the user never sees.
    if (mode === 'chat') {
      return [
        "You are CherryClipz's in-app AI co-editor — not a clip-stacker, but an editor with taste, instinct, and a point of view.",
        'A single transcript can be cut a near-infinite number of ways. Most cuts are noise; a rare few say something true. Your job is to find those — the cut that reveals who the speaker really is, what they feel underneath the words, and what matters. Honor meaning over mechanics. When a choice is between a tidy edit and a moving one, choose moving.',
        '',
        ...archiveLines,
        'Below is the video as a JSON list of clips. Each clip has: "id", spoken "text", "speaker", "sentiment"' + (archive ? ', "v" (which video it is from)' : '') + ', "sec" (how long the line lasts), and "gap" (seconds of silence right after it).',
        '',
        'PACING & RHYTHM — this is as important as the words. Edit like a master, not a clip-stacker:',
        '• Vary the rhythm — alternate shorter, punchy lines (low "sec") with longer ones; never stack many long clips in a row.',
        '• Use a short, sharp line to land a point or accelerate; a longer line to build.',
        '• A meaningful pause builds suspense — a clip with a notable "gap", or a short beat placed right before a reveal, creates a pregnant pause and invites curiosity. Use them on purpose.',
        '• Shape an arc: open on a hook, build tension, deliver a payoff, end on a button (a line that lands).',
        '• Set a rhythm, then break it for impact — but only when it serves the story. Always favor the better story over a mechanical pattern.',
        '• Match pace to emotion: faster and tighter for energy; slower, more spaced for gravity or emotion.',
        '',
        'CONTINUITY & COHERENCE — this is the MOST IMPORTANT rule. The clips must sound like ONE connected statement, never a disjointed list:',
        '• Before you finalize, READ your chosen sequence back to yourself IN ORDER. Each line must follow naturally from the one before it. If a listener would think "wait — what does that refer to?", the cut is wrong.',
        '• NEVER pick a clip that opens with an unresolved reference — a pronoun like "they / it / this / that / he / she" whose subject was never introduced by the clip right before it. (Example of a BAD pick: "They ran out of money" placed after a clip about veterans — who is "they"? It does not connect.) Either choose a clip that establishes the subject first, or drop that clip.',
        '• AVOID jarring topic jumps. Two strong lines on different subjects only belong next to each other if the transition still feels intentional and smooth.',
        '• A slightly less flashy clip that CONNECTS beats a great-sounding clip that breaks the flow. Coherence comes first, always.',
        '• Prefer runs where one thought leads into the next; when you switch speakers, make sure the new line still answers or advances the previous one.',
        '',
        ...(precise ? [
          'TRIM TO THE BEST PART (the user turned this ON) — you MUST cut WITHIN clips, not just pick whole lines:',
          '• For MOST clips you choose, include a "keep": the single strongest consecutive phrase from that clip. Trim away filler, throat-clearing, run-up words, and weak edges so each line lands tight and punchy.',
          '• Format: {"id": N, "keep": "exact consecutive words"}. "keep" MUST be an exact run of consecutive words copied VERBATIM from that clip\'s "text" — never paraphrase, reorder, fix grammar, or invent words.',
          '• When you trim, drop an orphaned lead-in word whose context you cut — a dangling "However", "But", "So", or a pronoun that no longer refers to anything — so the phrase starts clean.',
          '• Aim to trim the MAJORITY of clips. Only omit "keep" when the line is already short and tight. Returning clips with no "keep" at all means this mode did nothing.',
          '',
        ] : []),
        'When the user asks for an edit:',
        '• Reply in 1–3 SHORT, friendly sentences — what you chose and the editorial logic (mention pacing when it shaped your choice). Be concise and conversational, like a co-editor.',
        '• You may quote a few of the chosen lines by their TEXT (short quotes) if it helps the user picture it.',
        '• NEVER show ids, JSON, or technical formatting in your sentences, and never mention them.',
        '• CRITICAL: ALWAYS end your reply with ONE json code block of the chosen clip ids, in play order, e.g.:',
        '```json',
        precise ? exPrecise : exRough,
        '```',
        'This block is REQUIRED on EVERY reply — the edit does NOT happen without it, even for creative, abstract, or poetic requests. Never end a reply without it. The user never sees it, so do not mention ids or json in your sentences.',
        'Pick only from the ids below (use the real id numbers shown). The user can refine across messages — adjust the selection each time they ask.',
        '',
        'CLIPS:',
        JSON.stringify(clips),
      ].join('\n');
    }

    const goalLine = (goal && goal.trim())
      ? goal.trim()
      : '(type your goal here — e.g. "make a 30-second trailer of the most surprising moments")';

    const idExample = mode === 'precise' ? exPrecise2 : exRough;

    const intro = [
      'You are a world-class documentary film editor helping me build a video in CherryClipz.',
      '',
      'MY GOAL:',
      goalLine,
      '',
      archive
        ? `Below are the full transcripts of my ${archive.sources.length} videos as ONE JSON list of clips, in chronological order within each video. Each clip is one sentence with a unique "id" and a "v" saying which video it is from.`
        : 'Below is the full transcript of my video as a JSON list of clips, in chronological order. Each clip is one sentence with a unique "id".',
      ...archiveLines,
      '',
      'Work in THREE steps, and SHOW me each one so I understand your thinking:',
      '',
      '1) UNDERSTAND — Read the whole transcript first. In 3–5 sentences tell me what this video is about, who is speaking, and the main themes or story. This proves you understood it before cutting.',
      '',
      '2) PLAN — Explain your editorial approach for my goal: the angle you are taking, the arc you will build (hook → build → payoff), and why. Think like a great editor — open on the strongest hook, cut filler and rambling, keep only what earns its place, shape an emotional rhythm.',
    ];

    const selectStep = mode === 'precise' ? [
      '3) SELECT — Choose the clips that best achieve my goal, in the exact order they should play. You MAY trim a clip down to its strongest phrase and combine pieces from different clips into one stronger line. Put ONLY the result in a single JSON code block at the very end, like this:',
      '```json',
      idExample,
      '```',
      'Each item: "id" = the clip the words come from; "keep" (optional) = the EXACT consecutive words to use from that clip\'s "text", copied verbatim (omit "keep" to use the whole clip).',
      'RULES for "keep": it must be an exact run of consecutive words found in that clip\'s "text" — never paraphrase, reorder, fix grammar, or invent words. Use each id at most once. Pick only from the ids below.',
    ] : [
      '3) SELECT — Choose the clips that best achieve my goal, in the exact order they should play. Put ONLY the chosen ids in a single JSON code block at the very end, like this:',
      '```json',
      idExample,
      '```',
      'RULES: pick only from the ids below. Use whole clips (do not trim or merge). Use each id at most once.',
    ];

    const editorNotes = [
      '',
      'EDITOR BEST PRACTICES (how a pro approaches this):',
      '• Lead with the single most gripping line — earn attention in the first 3 seconds.',
      '• Every clip must move the story forward; cut anything that repeats or sags.',
      '• Favor tension, surprise, emotion, or a clear bold claim — those are what travel.',
      '• End on a button: a punchline, a turn, or a line that resonates and lands.',
      '',
      'Write your UNDERSTAND and PLAN as normal text, but the final ids MUST be in ONE json code block, separate from your notes, so I can copy it cleanly.',
      '',
      'CLIPS:',
      JSON.stringify(clips),
    ];

    return [...intro, '', ...selectStep, ...editorNotes].join('\n');
  };

  // Apply the AI's reply. Handles BOTH formats automatically:
  //   • rough:   "12, 47, 3, 88"      → select whole clips
  //   • precise: JSON [{id, keep?}…]  → select clips, pen-highlight the kept phrase
  //   • pieces:  the same id more than once (or keep: [phrases]) → split that clip at the
  //              phrases' edges and tick the kept pieces where the AI put them (utils/aiPieces.js)
  // Selects in the given order (pick/export order), returns to the Sentence view so
  // ids line up, and pins to the top. Returns how many clips were selected.
  const selectClipsByIds = (text, includeSilence = true) => {
    const idMap = new Map(aiSourceRows().filter((r) => !r.space).map((r) => [String(r.id), r]));
    let items = null;
    // Prefer a fenced ```json [...] ``` code block (the AI now replies with prose notes
    // PLUS a clean code block); fall back to the last [...] array in the text, then to
    // bare comma-separated ids. This keeps the AI's summary/plan from confusing the parser.
    const fenced = (text || '').match(/```(?:json)?\s*(\[[\s\S]*?\])\s*```/i);
    const bareArray = (text || '').match(/\[[\s\S]*\]/);
    const jsonStr = fenced ? fenced[1] : (bareArray ? bareArray[0] : null);
    if (jsonStr) {
      try {
        const parsed = JSON.parse(jsonStr);
        if (Array.isArray(parsed)) items = parsed.map((x) => (x && typeof x === 'object' ? x : { id: x }));
      } catch (e) { /* not JSON — fall through to plain ids */ }
    }
    if (!items) {
      const tokens = (text || '').match(/[A-Za-z0-9_-]+/g) || [];
      items = tokens.map((t) => ({ id: t }));
    }
    const known = items.filter((it) => it && idMap.has(String(it.id)));
    if (!known.length) return 0;
    // What plays, in order: whole clips, trims (one phrase) and pieces (2+ phrases of one clip).
    const { plan } = planAiPicks(known, (id) => idMap.get(String(id)).sentence);
    const rangesById = new Map();
    plan.forEach((p) => {
      if (p.range) rangesById.set(String(p.id), [...(rangesById.get(String(p.id)) || []), p.range]);
    });
    // A clip with 2+ pieces is split like a manual ✂ split (not in Archive — its ids span videos).
    const splits = new Map();
    if (!archive) {
      rangesById.forEach((ranges, key) => {
        if (ranges.length < 2) return;
        const split = splitRowIntoPieces(idMap.get(key), ranges, maxLines);
        if (split) splits.set(key, split);
      });
    }
    const seen = new Set();
    const picks = [];
    plan.forEach((p) => {
      const key = String(p.id);
      const split = splits.get(key);
      if (split) { picks.push({ row: idMap.get(key), pieceId: split.idFor(p.range) }); return; }
      if (seen.has(key)) return; // couldn't split (no word timings): the clip plays once
      seen.add(key);
      picks.push({ row: idMap.get(key), range: p.range });
    });

    // For "include trailing silence": extend each WHOLE-clip pick's end into the
    // silence that follows it (capped, and never past the next spoken clip), so
    // trailing sounds like the "…ge" of "fudge" are never cut off. Pen-trimmed
    // clips keep their phrase. Reflected in both the display and the export.
    const sentencesByTime = [...aiSourceRows()]
      .filter((r) => !r.space && r.start_in != null)
      .sort((a, b) => a.start_in - b.start_in);
    const SILENCE_CAP = 0.4;
    const TRIM_BUFFER = 0.15; // cushion (seconds) added each side of a trimmed phrase so cuts breathe
    const paddedEnd = (row) => {
      const i = sentencesByTime.findIndex((r) => r.id === row.id);
      // ARCHIVE: the "next" clip must be from the SAME video — timecodes restart per source.
      let nextS = sentencesByTime[i + 1];
      if (row.source) {
        nextS = undefined;
        for (let j = i + 1; j < sentencesByTime.length; j++) {
          if (sameSource(sentencesByTime[j], row)) { nextS = sentencesByTime[j]; break; }
        }
      }
      const ceil = nextS ? nextS.start_in - 0.03 : row.end_in + SILENCE_CAP;
      return Math.min(row.end_in + SILENCE_CAP, Math.max(row.end_in, ceil));
    };

    // Clean slate, then apply. resetFilters drops every filter for THIS view AND recomputes
    // the Time/Length bounds from the CURRENT view's own data — crucial for Categories, whose
    // longer chunks were being hidden by stale sentence-view bounds (selected but invisible).
    // It also clears old selection + pen-trims, so a new paste fully replaces the last edit —
    // no Reset needed between videos. We then re-apply this paste's picks on top.
    resetFilters(getDataForCurrentView(currentView), currentView);
    setQuickFilters({ selectedOnly: false, hasQuestion: false, firstLetter: null, durationSec: null, containsNumbers: false, entityTypes: [], contentFlags: [], keyPhrases: [], filterVerbs: [], filterNouns: [], filterAdjectives: [] });
    setSortOrder({ field: null, direction: 'asc', column: null, label: null });
    const newAdj = {};
    picks.forEach(({ row, range, pieceId }) => {
      if (pieceId) {
        // A piece of a split clip: its own row with its own times, like a manual split.
        filterStore.setSelectedSentences(pieceId, currentView);
        filterStore.clearHighlight(row.id);
        return;
      }
      filterStore.setSelectedSentences(row.id, currentView);
      delete newAdj[row.id]; // start each picked clip clean
      // Sub-clip trim: pen-highlight the kept phrase. planAiPicks gives no range when the
      // AI's "keep" is basically the whole sentence (or didn't match), so that clip plays
      // whole — no pointless full-sentence highlight — and also gets the pad.
      let trimmed = false;
      if (range) {
        filterStore.setHighlight(row.id, range.start, range.end);
        // Give the trimmed phrase a small breathing-room cushion on both sides so the cut
        // doesn't land abruptly mid-breath. Tied to "Include trailing silence" — same idea
        // (don't clip the edges), just applied to the phrase instead of the whole clip.
        if (includeSilence) filterStore.setHighlightPad(row.id, TRIM_BUFFER, TRIM_BUFFER);
        trimmed = true;
      } else {
        filterStore.clearHighlight(row.id);
      }
      if (!trimmed && includeSilence) {
        const pEnd = paddedEnd(row);
        if (pEnd > row.end_in) newAdj[row.id] = { start_in: row.start_in, end_in: pEnd };
      }
    });
    adjustmentsRef.current = newAdj;
    setSelectedCount((p) => ({ ...p, [currentView]: picks.length }));
    setChecked((p) => ({ ...p, [currentView]: [false, true] }));
    setRegroupParams(null);
    // Split clips swap in their piece rows as the list is rebuilt, so the ticked pieces exist.
    const expandSplits = splits.size
      ? (data) => data.flatMap((r) => (splits.has(String(r.id)) ? splits.get(String(r.id)).pieces : [r]))
      : undefined;
    setTimeout(() => {
      onPinSelectedToTop(expandSplits);
      applyAdjustmentSnapshot(adjustmentsRef.current); // reflect the silence pads in the rows
    }, 80);
    return picks.length;
  };
  selectByIdsRef.current = selectClipsByIds; // AI link applies relay picks through this

  const onViewChange = (view) => {
    setCurrentView(view);
    setViewConfig(CURRENT_VIEW_CONFIG[view]);
    setRegroupParams(null); // reset regroup when switching views
    const newRows = [...getDataForCurrentView(view)];
    setRows(newRows);
    console.log("onViewChange", view, "rows:", newRows.length);

    // Reset filters for the new view using the view parameter instead of currentView
    resetFilters(newRows, view);
    indeterminateCheck(newRows);
    // calcDuration(newRows);
    updateHeader();

    // No need to call filterAll() here since we already have the correct data
    // and filters are reset. The data will be filtered when user interacts with filters.
  };

  // ?view=clips|categories|super opens the ET in that view (the notetaker's email links).
  // After a tick, so the loader's own first filterAll() has landed and can't overwrite it.
  const urlViewAppliedRef = useRef(false);
  useEffect(() => {
    if (loading || urlViewAppliedRef.current) return;
    urlViewAppliedRef.current = true;
    const view = urlViews[new URLSearchParams(window.location.search).get("view")];
    if (view && view !== currentView) setTimeout(() => onViewChange(view), 0);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ?word=video selects every clip that says that word, and ?speaker=B every clip that speaker
  // said, in time order, through the same path as AI picks (pinned to the top, numbered): the
  // email's "Most said" and "Everything Speaker B said" links, a supercut one Export away.
  // Waits for the loader's last rebuilds (streamsDone / enrichesDone), which would undo the pin.
  // Runs after every load that has settled: a reload rebuilds the list and drops the ticks.
  useEffect(() => {
    if (loading || !streamsDone || streamsDone !== enrichesDone) return;
    const params = new URLSearchParams(window.location.search);
    const word = (params.get("word") || "").trim();
    const speaker = (params.get("speaker") || "").trim();
    // ?picks=12,7,30 (the library's quick look → "More export options"): those clips, in that
    // order, through the same path; &export=1 then opens the exporter on them.
    const picks = (params.get("picks") || "").split(",").map((t) => t.trim()).filter((t) => /^\d+$/.test(t));
    if (picks.length) {
      setTimeout(() => setTimeout(() => {
        selectByIdsRef.current?.(JSON.stringify(picks.map(Number)), true);
        if (params.get("export") === "1") setTimeout(() => openExportLatest.current?.(), 400);
      }, 0), 0);
      return;
    }
    if (!word && !speaker) return;
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const saysIt = new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}($|[^\\p{L}\\p{N}])`, "iu");
    const ids = originalRows
      .filter((r) => !r.space && (!word || saysIt.test(r.sentence || "")))
      .filter((r) => !speaker || String(r.speaker || "").trim() === speaker)
      .map((r) => r.id);
    // Two ticks, so a ?view switch has re-rendered and the ref holds that view's picker.
    if (ids.length) setTimeout(() => setTimeout(() => selectByIdsRef.current?.(JSON.stringify(ids), true), 0), 0);
  }, [loading, streamsDone, enrichesDone]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loading value={loadingProgress} />;

  const renderCurrentView = (toolbarPosition) => {
    switch (currentView) {
      case "Super Transcript":
        return (
          <div style={{ flex: 1, height: "100%", minHeight: 0, overflow: "hidden", padding: "12px", boxSizing: "border-box", ...getContentStyle(toolbarPosition) }}>
            <SuperTranscriptView
              sentences={getOriginalDataForCurrentView("Create a Video") || []}
              videoFile={videoFile}
            />
          </div>
        );
      case "Cherry Pick":
        return (
          <CherryPickGrid
            toolbarPosition={toolbarPosition}
            data={rows}
            filterStore={filterStore}
            videoFile={videoFile}
            onVideoClick={onVideoClick}
            onCheckboxChange={onCheckboxChange}
            bottomOffset={
              toolbarPosition === "bottom" ? DRAG_TOOLBAR_HEIGHT : 0
            }
            currentView={currentView}
          />
        );
      case "Cherry Classic":
        return (
          <CherryClassicGrid
            toolbarPosition={toolbarPosition}
            data={rows}
            filterStore={filterStore}
            videoFile={videoFile}
            onVideoClick={onVideoClick}
            bottomOffset={toolbarPosition === "bottom" ? DRAG_TOOLBAR_HEIGHT : 0}
            currentView={currentView}
          />
        );
      case "Charts":
        return <ChartsGrid toolbarPosition={toolbarPosition} data={rows} />;
      case "Mosaic":
        return (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', ...getContentStyle(toolbarPosition) }}>
            <MosaicView rows={rows} />
          </Box>
        );
      case "Chessboard":
        return (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', ...getContentStyle(toolbarPosition) }}>
            <ChessboardView rows={rows} videoFile={videoFile} />
          </Box>
        );
      case "Cube":
        return (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', ...getContentStyle(toolbarPosition) }}>
            <CubeView rows={rows} videoFile={videoFile} onOpenChessboard={() => {
              setCurrentView("Chessboard");
              setViewConfig(CURRENT_VIEW_CONFIG["Chessboard"]);
            }} />
          </Box>
        );
      case "AIB":
      case "Entities":
      case "Categories":
      case "Auto Highlight":
      case "Content Moderation":
      case "Create a Video":
        calcDuration(rows);
        break;
      default:
        console.log("default should not happen");
    }
    const sortableIds = rows.map((r) => r.id);

    return (
      <RowsRefContext.Provider value={rowsRef}>
        <DndContext
          sensors={dndSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
      <TableVirtuoso
        ref={virtuosoRef}
        scrollerRef={(ref) => {
          scrollerRef.current = ref;
        }}
        data={rows}
        style={{
          overflow: "auto",
          height: "100%",
          width: "100%",
          boxSizing: "border-box",
          ...getContentStyle(toolbarPosition),
        }}
        components={virtuosoComponents}
        fixedHeaderContent={() => (
          <Header
            activeFilterFields={activeFilterFields}
            header={getHeaderCurrentView()}
            onClick={onSortClick}
            sortOrder={sortOrder}
            checked={checked[currentView]}
            handleCheck={onCheckAll}
            userStore={userStore}
            precision={precision[currentView]}
            filterStore={filterStore}
            currentView={currentView}
            showExtraMobileColumns={showExtraMobileColumns}
          />
        )}
        itemContent={(index, rowData) => (
          <ItemContent
            item={index}
            data={rowData}
            header={getHeaderCurrentView()}
            filterStore={filterStore}
            videoFile={videoFile}
            onVideoClick={onVideoClick}
            onWordClick={onWordClick}
            onWordRightClick={onWordRightClick}
            onUnsplit={unsplitRow}
            onCheckboxChange={onCheckboxChange}
            currentView={currentView}
            userStore={userStore}
            precision={precision[currentView]}
            onJumpToItem={onJumpToItem}
            showJump={isViewNarrowed}
            /* Only honest while the list is still in recording order — a heading above rows
               that are no longer contiguous would claim a grouping that is not there. Same
               signal that gates the reveal-in-context button. */
            speakerHeadings={speakerHeadings && !isViewNarrowed}
            // Click a word to search it, click the same word again to clear — otherwise you
            // have to go find the search box to undo a one-click action.
            // Clicking a data point filters by it — the number you are looking at IS the
            // control. Same values the sliders set, so nothing new to learn.
            onSecondsClick={(n) => {
              const v = Math.round(Number(n)); if (isNaN(v)) return;
              const cur = filterStore.getSeconds(currentView) || [];
              if (cur[0] === v && cur[1] === v) { onSecChange(secondsBounds); sayShowing("Showing every length again"); }
              else { onSecChange([v, v]); sayShowing(`Now showing clips ${v}s long`); }
            }}
            onLengthClick={(n) => {
              const v = Math.round(Number(n)); if (isNaN(v)) return;
              const cur = filterStore.getTextLen(currentView) || [];
              if (cur[0] === v && cur[1] === v) { onTextLenChange(textLenBounds); sayShowing("Showing every length again"); }
              else { onTextLenChange([v, v]); sayShowing(`Now showing clips of ${v} characters`); }
            }}
            onSpeakerClick={(letter) => {
              // Isolate: show only this speaker. Click the same one again to bring the rest back.
              const map = filterStore.getSpeakers(currentView) || {};
              const keys = Object.keys(map);
              const soloed = map[letter] && keys.every((k) => k === letter || !map[k]);
              keys.forEach((k) => {
                const want = soloed ? false : k === letter;
                if (!!map[k] !== want) filterStore.setResetSpeakers(k, currentView);
              });
              filterAll();
              sayShowing(soloed ? "Showing every speaker again" : `Now showing ${(speakerNames && speakerNames[letter]) || (archive ? letter : "Speaker " + letter)} only`);
            }}
            onBoolClick={(field) => {
              const key = field === 'contains_numbers' ? 'containsNumbers' : 'hasQuestion';
              const wasOn = !!(quickFilters || {})[key];
              onQuickFilterChange(key);
              sayShowing(wasOn ? "Showing everything again"
                : key === 'hasQuestion' ? "Now showing questions only" : "Now showing clips with a number");
            }}
            onSentimentClick={(mood) => {
              const cur = filterStore.getSentiments(currentView) || {};
              const keys = Object.keys(cur);
              const soloed = cur[mood] && keys.every((k) => k === mood || !cur[k]);
              keys.forEach((k) => {
                const want = soloed ? false : k === mood;
                if (!!cur[k] !== want) filterStore.setSentiments(k, currentView);
              });
              filterAll();
              sayShowing(soloed ? "Showing every mood again" : `Now showing ${mood} clips only`);
            }}
            onWordSearch={(w) => {
              const cur = (filterStore.getSearch(currentView) || "").trim().toLowerCase();
              const next = cur === w.trim().toLowerCase() ? "" : w;
              onSearchChange({ target: { value: next } });
              sayShowing(next ? `Now showing clips that say "${next}"` : "Showing everything again");
            }}
            prevSpeaker={index > 0 ? (rowsRef.current[index - 1] || {}).speaker : null}
            onAdjustTimes={onAdjustTimes}
            showExtraMobileColumns={showExtraMobileColumns}
            onSingleClipDownload={onSingleClipDownload}
            onSingleClipShare={labs.sharePill ? onSingleClipShare : null}
            penMode={penMode}
            splitCursor={splitCursor}
            transcriptStyle={transcriptStyle}
            translations={translations}
            speakerNames={speakerNames}
            onRenameSpeaker={onRenameSpeaker}
            clipThumbnails={viewClipThumbnails}
            thumbnailsGenerating={thumbnailsGenerating}
            editedSentences={editedSentences}
            onEditSentence={editMode ? (row) => { setEditingRow(row); setEditDraft(editedSentences[row.id] ?? row.sentence ?? ''); } : null}
          />
        )}
        rangeChanged={handleRangeChanged}
      />
          </SortableContext>
        </DndContext>
      </RowsRefContext.Provider>
    );
  };
  return (
    <>
      <CreateVideoLogo />
      {/* Remote-driver (phone → Premiere) banner. On a /s/…#cc_remote=<token> link, ticking here
          drives the desktop Premiere timeline. "live" once a push is accepted; "disconnected" if
          the desktop disarmed (the relay returns 409). Fixed so it's always visible on mobile. */}
      {remoteDriverStatus && (
        <Box
          sx={{
            position: "fixed", top: 0, left: 0, right: 0, zIndex: 1500,
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            padding: "6px 12px", fontSize: "13px", fontWeight: 600, fontFamily: "Outfit, sans-serif",
            color: "#fff",
            background: remoteDriverStatus === "disconnected" ? "#7a1520" : "#b3123a",
          }}
        >
          <span style={{ fontSize: "10px" }}>{remoteDriverStatus === "disconnected" ? "⚪" : "🔴"}</span>
          {remoteDriverStatus === "disconnected"
            ? "Desktop disconnected — re-arm “Allow phone control” in the plugin"
            : remoteDriverStatus === "live"
              ? "Live — you’re controlling Premiere on your desktop"
              : "Connecting — tick a clip to start controlling Premiere"}
        </Box>
      )}
      {/* Spacer so the fixed banner doesn't cover the CherryClipz logo below it. */}
      {remoteDriverStatus && <div style={{ height: 34 }} />}
      {/* Panel-mode floating Undo/Redo. Cmd+Z already calls onUndo, but in the
          docked Premiere panel the keystroke reaches the ET only when the iframe
          has focus — otherwise Premiere eats it. A visible button removes that
          focus dependency (Isaac's "hovering undo on the corner"). Undo restores
          the prior selection → the pick reaction fires → the timeline follows. */}
      {getEmbedParentOrigin() === "file://" && (canUndo || canRedo) && (
        <Box
          sx={{
            position: "fixed", bottom: 12, right: 12, zIndex: 1400,
            display: "flex", gap: "6px",
          }}
        >
          {/* Both buttons always render so the layout never shifts — Redo just
              greys out when there's nothing to redo (Isaac: "don't push one out
              of the way"). Horizontal ← / → arrows read as back/forward. */}
          <button
            type="button"
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo the last change (also ⌘Z when the sheet has focus)"
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              background: "rgba(20,20,28,0.94)", color: canUndo ? "#f4f4f5" : "#5b5b63",
              border: "1px solid rgba(233,30,79,0.45)", borderRadius: "8px",
              padding: "7px 12px", fontSize: "13px", fontWeight: 600,
              cursor: canUndo ? "pointer" : "default",
              boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
            }}
          >
            <span style={{ fontSize: "15px", lineHeight: 1 }}>←</span> Undo
          </button>
          <button
            type="button"
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo the last undone change (also ⌘Y)"
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              background: "rgba(20,20,28,0.94)", color: canRedo ? "#f4f4f5" : "#5b5b63",
              border: "1px solid rgba(233,30,79,0.28)", borderRadius: "8px",
              padding: "7px 12px", fontSize: "13px", fontWeight: 600,
              cursor: canRedo ? "pointer" : "default",
              boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
            }}
          >
            Redo <span style={{ fontSize: "15px", lineHeight: 1 }}>→</span>
          </button>
        </Box>
      )}
      {(thumbnailsGenerating || postersFlowing) && (() => {
        // Live progress so the user can SEE it's still working: how many clip frames have
        // landed vs. the total. Both numbers are already in memory (clipThumbnails is keyed by
        // Sentence id; originalRows is the Sentence set) — pure render math, no extra requests.
        // The count climbs as posters arrive (panel push AND/OR cloud poll), and it's the same
        // frames the Categories/Entities/etc views borrow, so it also tracks THOSE filling in.
        const total = originalRows.filter((r) => !r.space).length;
        const ready = total ? Math.min(Object.keys(clipThumbnails).length, total) : Object.keys(clipThumbnails).length;
        return (
        <Box
          sx={{
            // Bottom-left, not under the header. It used to sit at toolbar height + 8, which
            // is exactly where the column headers are — you could not read or aim at Sort
            // while posters were arriving, and on a long video that is most of the session.
            position: "fixed",
            bottom: 14,
            left: 14,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.75,
            py: 0.75,
            borderRadius: 999,
            bgcolor: "rgba(20,20,28,0.94)",
            border: "1px solid rgba(233,30,79,0.45)",
            boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
            color: "#f4f4f5",
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          <CircularProgress size={13} thickness={5} sx={{ color: "#e91e4f" }} />
          <span>Creating thumbnails…{total ? ` ${ready}/${total}` : ""}</span>
        </Box>
        );
      })()}
    <Box
      sx={{
        position: "absolute",
        width: "100%",
        top: toolbarHidden ? 0 : DRAG_TOOLBAR_HEIGHT,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        overflow: "hidden",
        // The tool rail lives in this padding rather than on top of the clips, so a
        // ticked checkbox or the right-most column is never hidden underneath it.
        paddingRight: `${rail.pad}px`,
        transition: "padding-right .24s ease",
        // Zoom the SHEET only. The bubbles and Play float above it and stay thumb-sized —
        // scaling them too would just move the problem.
        ...(etZoom !== 100 ? { zoom: etZoom / 100 } : null),
      }}
    >
      {/* On a real phone this floated at bottom-right, exactly where the tool bubbles now sit,
          so it moves into Settings > "You're on the phone view" there. But hiding it whenever
          the layout is compact was a trap: a desktop user who clicks "Phone" makes
          isXSmallScreen true, the toggle vanishes, and there is no visible way back. So keep
          it whenever the mode was chosen deliberately — only auto-on-a-small-screen hides it. */}
      {!exportModalOpen
        && (viewToggleShown ? viewToggleShown === "on"
             : (!isXSmallScreen || etViewMode !== "auto"))
        && <EtViewToggle />}
      <Box sx={{ display: toolbarHidden ? "none" : "contents" }}>
      <DraggableToolbar
        onExportModalOpenChange={setExportModalOpen}
        archiveInfo={archive ? { sources: archive.sources, key: archive.key } : null}
        viewConfig={viewConfig}
        onPositionChange={handleToolbarPositionChange}
        rowCount={countRows()}
        selectedCount={selectedCount[currentView]}
        totalCount={originalRows.length}
        sentiments={filterStore.getSentiments(currentView)}
        onSentimentChange={onSentimentChange}
        speakers={filterStore.getSpeakers(currentView)}
        onSpeakerChange={onSpeakerChange}
        speakerNames={speakerNames}
        textLen={filterStore.getTextLen(currentView)}
        secondsBounds={secondsBounds}
        textLenBounds={textLenBounds}
        onLengthChange={onTextLenChange}
        seconds={filterStore.getSeconds(currentView)}
        onSecondsChange={onSecChange}
        onResetFilters={onResetFilters}
        subtitlesEnabled={filterStore.getSubtitlesOnOff(currentView)}
        onSetResetSubtitles={onSetResetSubtitles}
        spacesOnOff={filterStore.getSpacesOnOff(currentView)}
        onSetSpaces={onSetSpaces}
        onSearchChange={onSearchChange}
        searchValue={filterStore.getSearch(currentView)}
        searchContext={searchContext}
        onSearchContextChange={onSearchContextChange}
        store={filterStore}
        currentView={currentView}
        onViewChange={onViewChange}
        makeMidia={makeMidiaWithWarning}
        selectedDurationMs={userStore.getDurationInMs()}
        precision={precision[currentView]}
        onPrecisionChange={onPrecisionChange}
        videoWidth={filterStore.getVideoWidth(currentView)}
        onVideoWidthChange={onVideoWidthChange}
        userStore={userStore}
        onJumpToItem={onJumpToItem}
        setCloseSearchModalRef={setCloseSearchModalRef}
        setOpenExportModalRef={setOpenExportModalRef}
        setOpenBlackCherryRef={setOpenBlackCherryRef}
        setToolbarOpeners={setToolbarOpeners}
        videoFileExtension={videoExt}
        quickFilters={quickFilters}
        onQuickFilterChange={onQuickFilterChange}
        columnSet={columnSet}
        onToggleColumnSet={toggleColumnSet}
        sortOrder={sortOrder}
        onSortOrderChange={onSortOrderChange}
        spacesMode={spacesMode}
        onSpacesModeChange={onSpacesModeChange}
        availableFirstLetters={availableFirstLetters}
        availableDurations={availableDurations}
        defaultExportTitle={defaultExportTitle}
        exportPreview={exportPreview}
        onShareLink={archive ? null : ensureSheetLink}
        onEditText={editPickText}
        showExtraMobileColumns={showExtraMobileColumns}
        onToggleMobileColumns={() => setShowExtraMobileColumns(v => !v)}
        onPinSelectedToTop={onPinSelectedToTop}
        onBuildAiPack={buildAiClipPack}
        onSelectByIds={selectClipsByIds}
        aiLink={aiLinkAvailable ? aiLink : null}
        onToggleAiLink={aiLinkAvailable ? toggleAiLink : null}
        onApplyFiller={applyFillerIds}
        fillerMode={fillerMode}
        fillerDone={fillerDone}
        onFillerModeChange={onFillerModeChange}
        onApplyVirality={applyViralityScores}
        viralityDone={viralityDone}
        onSortVirality={() => onSortOrderChange('virality')}
        viralityRange={viralityRange}
        onViralityRangeChange={onViralityRangeChange}
        onUndo={onUndo}
        onRedo={onRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        penMode={penMode}
        onPenModeChange={() => setPenMode(p => !p)}
        availableEntityTypes={availableEntityTypes}
        availableContentFlags={availableContentFlags}
        availableKeyPhrases={availableKeyPhrases}
        availableVerbs={availableVerbs}
        availableNouns={availableNouns}
        availableAdjectives={availableAdjectives}
        regroupParams={regroupParams}
        onRegroupChange={(params) => {
          setRegroupParams(params);
          if (!params) filterAll();
        }}
        onClearSelections={clearAllSelections}
        originalRowCount={originalRows.filter(r => !r.space).length}
        transcriptStyle={transcriptStyle}
        onTranscriptStyleChange={setTranscriptStyle}
        translationLang={translationLang}
        onTranslationLangChange={setTranslationLang}
        onTranslateAll={translateAll}
        translating={translating}
        translationProgress={translationProgress}
        onClearTranslation={() => { setTranslationsCache(prev => { const n = {...prev}; delete n[translationLang]; return n; }); }}
        hasTranslations={Object.keys(translationsCache[translationLang] || {}).length > 0}
        onToggleTranslation={toggleTranslation}
        canToggleTranslation={canToggleTranslation}
        lastTranslationLang={lastTranslationLang}
        editMode={editMode}
        onEditModeChange={() => setEditMode(p => !p)}
        playNext={playNext}
        onPlayNextChange={() => setPlayNext(p => !p)}
        playHighlightsOnly={playHighlightsOnly}
        onPlayHighlightsChange={() => setPlayHighlightsOnly(p => !p)}
      />
      </Box>

      {!exportModalOpen && (
        <Box
          component="button"
          onClick={() => setToolbarHidden((v) => !v)}
          title={toolbarHidden ? "Bring the toolbar back" : "Hide the toolbar"}
          sx={{
            // Sits at the very top edge in both states, so hiding and revealing are the same
            // gesture and neither one is buried in Settings.
            position: "fixed", top: toolbarHidden ? 0 : DRAG_TOOLBAR_HEIGHT - 1, left: "50%",
            transform: "translateX(-50%)", zIndex: 11500,
            display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", font: "inherit",
            px: 1.6, py: "3px", borderRadius: "0 0 9px 9px", bgcolor: "#1a1420",
            border: "1px solid #3a2b46", borderTop: "none", color: "#8b8592",
            fontSize: 10, fontWeight: 800, letterSpacing: ".08em",
            "&:hover": { color: "#fff", borderColor: "#e0466b" },
          }}>
          {toolbarHidden ? "▾ SHOW TOOLBAR" : "▴ HIDE TOOLBAR"}
        </Box>
      )}

      {renderCurrentView(toolbarPosition)}

      {/* Scroll Time Display */}
      {scrollTimeDisplay.show && (
        <Box
          sx={{
            position: "fixed",
            left: scrollTimeDisplay.position.x,
            top: scrollTimeDisplay.position.y,
            zIndex: 1500,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(40, 40, 40, 0.95)"
                : "rgba(255, 255, 255, 0.95)",
            color: theme.palette.text.primary,
            padding: "8px 16px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(8px)",
            fontFamily: "monospace",
            fontSize: "14px",
            fontWeight: "bold",
            pointerEvents: "none",
            transition: "opacity 0.2s ease-in-out",
            opacity: scrollTimeDisplay.show ? 1 : 0,
          }}
        >
          {scrollTimeDisplay.time}
        </Box>
      )}
    </Box>
    {/* ── TEXT EDIT DIALOG ──────────────────────────────────────── */}
    <Dialog open={!!editingRow} onClose={() => setEditingRow(null)} maxWidth="sm" fullWidth sx={{ zIndex: 9999 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <span style={{ fontSize: '15px', fontWeight: 600 }}>✏ Edit Transcript Text</span>
        {editingRow && editedSentences[editingRow.id] && (
          <Button size="small" variant="text" color="warning"
            onClick={() => { setEditedSentences(prev => { const n = {...prev}; delete n[editingRow.id]; return n; }); setEditingRow(null); }}
            sx={{ fontSize: '11px', textTransform: 'none' }}>
            Clear edit — restore original
          </Button>
        )}
      </DialogTitle>
      <DialogContent>
        {editingRow && editedSentences[editingRow.id] && editedSentences[editingRow.id] !== editingRow.sentence && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Original (read-only)
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', lineHeight: 1.5 }}>
              {(editingRow.sentence || '').split(' ').map((word, i) => {
                const editWords = new Set((editedSentences[editingRow.id] || '').toLowerCase().split(' '));
                const removed = !editWords.has(word.toLowerCase());
                return <span key={i} style={{ textDecoration: removed ? 'line-through' : 'none', color: removed ? '#ef9a9a' : 'inherit', marginRight: '4px' }}>{word}</span>;
              })}
            </Typography>
          </Box>
        )}
        <textarea
          autoFocus
          value={editDraft}
          onChange={e => setEditDraft(e.target.value)}
          style={{
            width: '100%', minHeight: '120px', padding: '10px 12px',
            fontSize: `${transcriptStyle?.size || 16}px`,
            fontFamily: transcriptStyle?.fontCSS || 'inherit',
            lineHeight: 1.5, borderRadius: '6px', resize: 'vertical', boxSizing: 'border-box',
            backgroundColor: 'rgba(255,255,255,0.06)', color: 'inherit',
            border: '1px solid rgba(255,255,255,0.2)', outline: 'none',
          }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '10px', display: 'block', mt: 0.5, textAlign: 'right' }}>
          {editDraft.length} chars — playback timecodes are unchanged
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setEditingRow(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={() => {
          if (editingRow) {
            undoStackRef.current.push({ adjustments: { ...adjustmentsRef.current }, selectedIds: [...filterStore.getSelectedSentences(currentView)], editedSentences: { ...editedSentencesRef.current } });
            redoStackRef.current = [];
            setCanUndo(true);
            setCanRedo(false);
            setEditedSentences(prev => ({ ...prev, [editingRow.id]: editDraft }));
          }
          setEditingRow(null);
        }} sx={{ textTransform: 'none', backgroundColor: '#f59e0b', '&:hover': { backgroundColor: '#d97706' } }}>
          Save Edit
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── EXPORT DURATION WARNING DIALOG ─────────────────────── */}
    <Dialog open={!!exportWarning} onClose={() => setExportWarning(null)} maxWidth="xs" fullWidth sx={{ zIndex: 9999 }}>
      {exportWarning?.capSeconds ? (<>
      {/* Free plan: over the length cap — no "Export Anyway"; the way out is fewer clips or a plan. */}
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
        Free exports are up to {formatDurationShort(exportWarning.capSeconds)}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5 }}>
          Your selected clips total <strong>{userStore.getDuration()}</strong>.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Untick a few clips to get under {formatDurationShort(exportWarning.capSeconds)}, or subscribe for longer exports.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={() => setExportWarning(null)} sx={{ textTransform: 'none' }}>
          OK
        </Button>
        <Button variant="contained" onClick={() => { setExportWarning(null); navigate('/subscription/'); }}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          See plans
        </Button>
      </DialogActions>
      </>) : (<>
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
        Long Export
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1.5 }}>
          Your selected clips total <strong>{userStore.getDuration()}</strong>, which is over 5 minutes.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Long exports take more processing time. Consider breaking it into shorter segments for faster turnaround. Continue anyway?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={() => setExportWarning(null)} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" color="warning"
          onClick={() => {
            const { selectedOptions, customTitle, orientation, burnCaptions, captionMode, captionSize, captionWordsPerGroup, captionStyle, separate, captionFont, supercut, framing, captionLook, effects } = exportWarning;
            setExportWarning(null);
            makeMidia(selectedOptions, customTitle, orientation, burnCaptions, captionMode, captionSize, captionWordsPerGroup, captionStyle, separate, captionFont, supercut, framing, captionLook, effects);
          }}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Export Anyway
        </Button>
      </DialogActions>
      </>)}
    </Dialog>

    {/* ── GUEST STITCHED-EXPORT BUILDING INDICATOR ───────────── */}
    {/* At the TOP: the bottom of the screen belongs to the Play / Combine buttons, which
        used to sit right on top of this and hide it. */}
    {sharedExportBuilding > 0 && (
      <Box sx={{
        position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100vw - 32px)',
        zIndex: 10000, display: 'flex', alignItems: 'center', gap: 1.5,
        bgcolor: '#1f1115', color: '#fff', border: '1px solid #7a2e3e',
        borderRadius: 999, px: 2.5, py: 1.25, boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
      }}>
        <CircularProgress size={20} thickness={5} sx={{ color: '#e0466b' }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {staffShareOwner()
            ? `Stitching… it goes to ${staffShareOwner()}’s library, and downloads here if you stay 🍒`
            : `Stitching your clip${sharedExportBuilding > 1 ? 's' : ''}… it’ll download automatically when ready 🍒`}
        </Typography>
      </Box>
    )}

    {/* ── FLOATING "PLAY SELECTED" BUTTON — hovers just above the export bar ──
        Big, thumb-friendly primary action on desktop ET, mobile ET, and the shared /s/
        spreadsheet on a phone. Plays the selected clips in the exact order they were picked,
        one after another, stopping when the last ends. Greyed until ≥1 selected; turns into
        a ⏸ Pause while playing, "Resume" when paused. (In the Premiere panel this previews in
        the iframe today; a phone→Premiere "play the sequence" branch is the deferred Phase 2.) */}
    {viewConfig?.createVideo && !exportModalOpen && (() => {
      const pickedCount = selectedCount[currentView] || 0;
      const disabled = !pickedCount && !playingSelected;
      const playing = playingSelected && !playSelectedPaused;
      // Show HOW LONG the picked clips run, right on the button you press to watch them. The
      // number existed only in the header's PICK total, the export dialog and Settings — all
      // easy to miss while you are picking (Isaac: "it is not so obvious"). userStore.getDuration()
      // is the same selected-total the export uses, so they can never disagree.
      // Same value as the export dialog, just without the leading "00:" hour so a two-second
      // pick reads "0:02" on a button instead of "00:00:02". Long picks keep their hours.
      const pickedTime = pickedCount
        ? String(userStore.getDuration() || '').replace(/^00:0?/, '')
        : '';
      const label = !playingSelected
        ? (pickedCount ? `Play ${pickedCount}${pickedTime ? ` · ${pickedTime}` : ''}` : 'Play selected')
        : (playSelectedPaused ? 'Resume' : 'Playing');
      // Share rides beside Play, and only with exactly one clip ticked \u2014 it cuts ONE clip.
      // With two or more the purple Combine pill is the action, so a Share there would be a
      // different (and much slower) promise.
      const withShare = labs.sharePill && pickedCount === 1 && !playingSelected
        && getEmbedParentOrigin() !== "file://";
      const shareRow = withShare ? (
        <Button
          onClick={() => {
            const ids = filterStore.getSelectedSentences(currentView);
            const row = rowsRef.current.find((r) => r.id === ids[0]) || originalRows.find((r) => r.id === ids[0]);
            if (row) onSingleClipShare(row);
          }}
          className="cc-share-pill"
          sx={{
            position: 'fixed', bottom: isXSmallScreen ? 74 : 92, left: '50%',
            transform: 'translateX(calc(-50% + 96px))',
            zIndex: 10001, textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap',
            fontSize: isXSmallScreen ? 15 : 18, borderRadius: 999,
            px: isXSmallScreen ? 2.2 : 3, py: isXSmallScreen ? 1.1 : 1.5,
            color: '#fff', bgcolor: '#E61D50', gap: 1,
            boxShadow: '0 6px 18px rgba(0,0,0,0.85), 0 8px 34px rgba(230,29,80,0.42)',
            '&:hover': { filter: 'brightness(1.08)' },
            // The mark slides in on hover \u2014 a wink, not a label change. No hover on a
            // phone, so there it simply never appears and the label carries the button.
            '& .cc-mark': { maxWidth: 0, opacity: 0, overflow: 'hidden', display: 'inline-flex', transition: 'max-width .26s ease, opacity .2s ease' },
            '&:hover .cc-mark': { maxWidth: '26px', opacity: 1 },
            '@media (prefers-reduced-motion: reduce)': { '& .cc-mark': { transition: 'none' } },
          }}
        >
          <ShareIcon size={isXSmallScreen ? 17 : 19} />
          Share
          <Box component="span" className="cc-mark">
            <Box component="img" src={ccLogoRed} alt="" sx={{ height: 18, width: 'auto', ml: 0.6 }} />
          </Box>
        </Button>
      ) : null;
      return (
        <>
        {shareRow}
        <Button
          onClick={disabled ? undefined : onPlaySelectedToggle}
          disabled={disabled}
          startIcon={playing ? <PauseIcon /> : <PlayArrowIcon />}
          sx={{
            position: 'fixed', bottom: isXSmallScreen ? 74 : 92, left: '50%',
            transform: withShare ? 'translateX(calc(-50% - 78px))' : 'translateX(-50%)',
            transition: 'transform .22s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            zIndex: 10000, textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap',
            fontSize: isXSmallScreen ? 15 : 18,
            borderRadius: 999,
            px: isXSmallScreen ? 3 : 4.5, py: isXSmallScreen ? 1.1 : 1.5,
            color: '#fff',
            background: playSelectedPaused ? 'linear-gradient(100deg,#f59e0b,#d97706)'
              : 'linear-gradient(100deg,#fb7185,#e0466b)',
            boxShadow: '0 6px 18px rgba(0,0,0,0.85), 0 8px 34px rgba(224,70,107,0.42)',
            '& .MuiButton-startIcon > svg': { fontSize: isXSmallScreen ? 22 : 27 },
            '&:hover': { filter: 'brightness(1.08)' },
            '&.Mui-disabled': { color: '#6b6475', background: '#221c2b', boxShadow: 'none' },
          }}
        >
          {label}
        </Button>
        </>
      );
    })()}

    {/* Quieter bubbles: smaller at rest on one tray, full size under the pointer. Applied as
        a stylesheet rather than threaded through every bubble's sx, so turning the switch off
        leaves the original styling completely untouched. */}
    {labs.quietRail && (
      <Box component="style">{`
        .cc-rail { background: rgba(14,11,19,.82); border: 1px solid #2b2335; border-radius: 999px;
                   padding: 8px 12px; backdrop-filter: blur(9px);
                   box-shadow: 0 10px 30px rgba(0,0,0,.45); }
        .cc-rail .ccbub { width: 38px !important; height: 38px !important;
                          transition: transform .18s cubic-bezier(.2,.8,.3,1.2); }
        .cc-rail .ccbub > * { transform: scale(.72); }
        .cc-rail .ccbub:hover { transform: scale(1.3); z-index: 50; }
        @media (prefers-reduced-motion: reduce) {
          .cc-rail .ccbub { transition: none; }
          .cc-rail .ccbub:hover { transform: none; }
        }
      `}</Box>
    )}

    {/* Cutting a clip takes as long as it takes, and the old toast vanished after six
        seconds while the work carried on. This stays until the file exists. */}
    {labs.progress && working && <ProgressCard stage={working.stage} sub={working.sub} />}

    {/* The share box: the clip, the platforms, then the spreadsheet link. */}
    {labs.postFlow && shareBoxFor && (
      <ShareBox
        clip={shareBoxFor}
        canShareFile={canShareFiles()}
        sheetUrl={sheetLink}
        busy={!!working}
        onTarget={shareBoxTarget}
        onShare={shareBoxShare}
        onSave={shareBoxSave}
        onSheetLink={shareBoxSheetLink}
        onClose={() => setShareBoxFor(null)}
      />
    )}

    {/* ── CLIP READY: Share or Save ────────────────────────────────────────────
        Shown once a trimmed clip is in hand, on phones that can hand a file to another app.
        It exists for a timing reason, not a design one: a share must begin within about five
        seconds of a tap, and the trim already spent the tap that started it. This card gives
        the share a tap of its own. Cancelling the sheet leaves the card up, so Save is still
        there. Desktop never sees this — the file just downloads, as it always did. */}
    {pendingShare && (
      <Box sx={{
        position: 'fixed', left: 12, right: 12, bottom: isXSmallScreen ? 18 : 28,
        mx: 'auto', maxWidth: 420, zIndex: 12000,
        bgcolor: '#1a1420', border: '1px solid #3a2b46', borderRadius: '13px',
        p: 1.5, boxShadow: '0 14px 34px rgba(0,0,0,0.6)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
            {pendingShare.target ? `Ready for ${pendingShare.target} 🍒` : 'Your clip is ready 🍒'}
          </Typography>
          <Button
            onClick={() => setPendingShare(null)}
            sx={{ ml: 'auto', minWidth: 'unset', p: 0.3, color: '#8b8592', '&:hover': { color: '#fff' } }}
            aria-label="Dismiss"
          >
            ✕
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={async () => {
              const result = await shareVideoFile(pendingShare);
              if (result === 'shared') {
                setPendingShare(null);
                messageStore.setMessage('Shared 🍒', 'success');
              } else if (result === 'unsupported' || result === 'error') {
                // The sheet refused outright — don't strand the user with a clip they can't have.
                saveBlob(pendingShare.blob, pendingShare.filename);
                setPendingShare(null);
                messageStore.setMessage('Sharing is not available here — downloaded instead.', 'info');
              }
              // 'cancelled' → they swiped it away. Leave the card up; Save is still there.
            }}
            sx={{
              flex: 1, textTransform: 'none', fontWeight: 700, fontSize: 14, borderRadius: '999px',
              color: '#fff', background: 'linear-gradient(100deg, #a855f7, #7c3aed)',
              '&:hover': { filter: 'brightness(1.1)' },
            }}
          >
            📤 {pendingShare.target ? `Send to ${pendingShare.target}` : 'Share'}
          </Button>
          <Button
            onClick={() => {
              saveBlob(pendingShare.blob, pendingShare.filename);
              setPendingShare(null);
              messageStore.setMessage('Clip downloaded', 'success');
            }}
            sx={{
              flex: 1, textTransform: 'none', fontWeight: 700, fontSize: 14, borderRadius: '999px',
              color: '#c9b8d6', border: '1px solid #3a2b46',
              '&:hover': { borderColor: '#e0466b', color: '#fff' },
            }}
          >
            ⬇ Save
          </Button>
        </Box>
      </Box>
    )}

    {/* ── FLOATING EXPORT/DOWNLOAD BAR: always present in the ET, bottom-center ──
        0 clips picked → "Select clips" prompt (red checkbox) — guides the user to tick a row
        1 clip picked  → Download (single trimmed clip straight to the device)
        2+ picked      → Export N clips (opens the same Create modal as the toolbar)
        Renders at every screen size (desktop + mobile) and flows into the K/B home iframes. */}
    {viewConfig?.createVideo && !exportModalOpen && (() => {
      const pickedCount = selectedCount[currentView] || 0;
      const barSx = {
        position: 'fixed', bottom: isXSmallScreen ? 18 : 28, left: '50%', transform: 'translateX(-50%)',
        zIndex: 10000, textTransform: 'none', fontWeight: 700,
        fontSize: isXSmallScreen ? 14 : 17,
        borderRadius: 999,
        px: isXSmallScreen ? 2.5 : 4, py: isXSmallScreen ? 1 : 1.5,
        boxShadow: '0 8px 28px rgba(0,0,0,0.5)', whiteSpace: 'nowrap',
        '& .MuiButton-startIcon > svg': { fontSize: isXSmallScreen ? 20 : 25 },
      };
      // 0 selected → passive prompt (doesn't intercept clicks) nudging the user to tick a clip.
      if (!pickedCount) {
        // Used to be a dead label, which told a newcomer nothing about HOW to pick a clip.
        // Now it says so on hover, and clicking it selects everything currently listed.
        const visible = countRows();
        return (
          <Button
            onClick={() => onCheckAll({})}
            startIcon={<CheckBoxOutlineBlankIcon />}
            title={`Tick the box on the left of a clip, or double-click its text — or click here to select all ${visible} listed clips`}
            sx={{
              ...barSx, display: 'flex', alignItems: 'center', gap: 1,
              bgcolor: '#1a1420', color: '#c9b8d6', border: '1px solid #3a2b46',
              '& .MuiButton-startIcon > svg': { color: '#e0466b' },
              '&:hover': { bgcolor: '#241c2b', borderColor: '#e0466b', color: '#fff' },
            }}
          >
            Pick clips to begin
          </Button>
        );
      }
      // 1 selected → download the single trimmed clip straight to the device (works for share guests too).
      if (pickedCount === 1) {
        // Premiere panel: the clip is already on the timeline; a "Download clip"
        // that just opens Adobe Media Encoder is unwanted (Isaac). No button.
        if (getEmbedParentOrigin() === "file://") return null;
        return (
          <Button
            startIcon={<ContentCutIcon />}
            onClick={() => {
              const ids = filterStore.getSelectedSentences(currentView);
              const row = rowsRef.current.find(r => r.id === ids[0]) || originalRows.find(r => r.id === ids[0]);
              if (row) onSingleClipDownload(row);
            }}
            sx={{ ...barSx, color: '#fff', bgcolor: '#1f1115', border: '1px solid #7a2e3e', '&:hover': { bgcolor: '#2a161d' } }}
          >
            Extract this clip
          </Button>
        );
      }
      // 2+ selected → stitched export. Blocked on view-only shares (makeMidia would refuse anyway).
      const isViewOnlyShare = typeof window !== 'undefined' && window.location.pathname.startsWith('/s/') &&
        (() => { try { return sessionStorage.getItem('cc_share_perm') === 'view'; } catch (e) { return false; } })();
      if (isViewOnlyShare) return null;
      // Premiere panel: no stitched-export button — the picks ARE a sequence
      // and exporting is Premiere's own job (Export tab). A button that can't
      // open that UI reliably is misleading (Isaac's call, 2026-07-21).
      if (getEmbedParentOrigin() === "file://") return null;
      return (
        <Button
          startIcon={<ContentCutIcon />}
          title="Renders one new video from your picks, in the order you picked them — it lands in My Library."
          onClick={() => { if (openExportModalRef) openExportModalRef(); }}
          sx={{ ...barSx, color: '#fff', background: 'linear-gradient(100deg, #a855f7, #7c3aed)', '&:hover': { filter: 'brightness(1.1)' } }}
        >
          Combine {pickedCount} clips &rarr; new video
        </Button>
      );
    })()}

    {/* ── TIMELINE DOCK ────────────────────────────────────────────────────────
        The same timeline the marketing page drives from outside an iframe, but here it
        is fed directly by buildPicks() — the exact cut the plugin and the export see,
        so it can never show you a different edit from the one you will get. Opt-in in
        Settings, revealed by a tab, and it works with the toolbar hidden. */}
    {(timelineEnabled || labs.timelineTab) && viewConfig?.createVideo && !exportModalOpen && (() => {
      const picks = buildPicks();
      const H = isXSmallScreen ? 300 : 380;
      return (
        <>
          <Box
            component="button"
            onClick={() => setTimelineOpen((v) => !v)}
            title={timelineOpen ? "Hide the timeline" : "Show the timeline"}
            sx={{
              position: "fixed", left: "50%", transform: "translateX(-50%)",
              bottom: timelineOpen ? H : 0, zIndex: 11400,
              display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", font: "inherit",
              px: 1.8, py: "3px", borderRadius: "9px 9px 0 0", bgcolor: "#1a1420",
              border: "1px solid #3a2b46", borderBottom: "none", color: "#8b8592",
              fontSize: 10, fontWeight: 800, letterSpacing: ".08em",
              transition: "bottom .28s ease",
              "&:hover": { color: "#fff", borderColor: "#e0466b" },
            }}>
            {timelineOpen ? "▾ HIDE TIMELINE" : `▴ TIMELINE${picks.length ? ` · ${picks.length}` : ""}`}
          </Box>

          <Box sx={{
            position: "fixed", left: 0, right: 0, bottom: 0, height: H, zIndex: 11350,
            bgcolor: "#0f0e14", borderTop: "1px solid #3a2b46",
            boxShadow: "0 -12px 40px rgba(0,0,0,0.6)", overflow: "hidden",
            transform: timelineOpen ? "translateY(0)" : `translateY(${H}px)`,
            transition: "transform .28s ease",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}>
            {timelineOpen && (
              picks.length ? (
                <EtTimelineDock
                  picks={picks}
                  videoUrl={videoFile || (archive && archive.sources[0] ? archive.sources[0].video : "") || ""}
                  sendCmd={(c) => {
                    // No postMessage here — the ET owns these actions directly.
                    if (!c) return;
                    // Resolve to the live row: onAdjustTimes takes the ROW (it reads the
                    // utterance for pen-trims), and onCheckboxChange wants the row INDEX so
                    // shift-range selection keeps working.
                    const idx = rowsRef.current.findIndex((r) => r.id === c.id);
                    const row = idx >= 0 ? rowsRef.current[idx] : null;
                    if (c.cmd === "deselect") {
                      if (row) onCheckboxChange({ target: { checked: false }, shiftKey: false }, idx, c.id);
                    } else if (c.cmd === "select" && Array.isArray(c.ids)) {
                      selectClipsByIds(c.ids);
                    } else if (c.cmd === "adjust") {
                      if (row) onAdjustTimes(row, c.deltaStart || 0, c.deltaEnd || 0);
                    } else if (c.cmd === "blackcherry") {
                      if (openTool) openTool("blackCherry");
                    } else if (c.cmd === "ai-chat" && c.text) {
                      if (openTool) openTool("chatAsk", c.text);
                    }
                  }}
                />
              ) : (
                <Box sx={{ height: "100%", display: "grid", placeItems: "center", color: "#7d7488", fontSize: 14, px: 3, textAlign: "center" }}>
                  Tick some clips and they will assemble here, in the order you picked them.
                </Box>
              )
            )}
          </Box>
        </>
      );
    })()}

    {/* A quiet line on the right telling you what just changed, then it leaves on its own.
        Never blocks anything — no close button, no focus, no pointer events. */}
    {nowShowing && (
      <Box sx={{
        position: "fixed", right: 22, top: "50%", transform: "translateY(-50%)", zIndex: 11800,
        pointerEvents: "none", maxWidth: 280,
        px: 2, py: 1.2, borderRadius: "10px",
        bgcolor: "rgba(20,17,26,0.96)", border: "1px solid #e0466b",
        boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
        color: "#f2eef6", fontSize: 13.5, fontWeight: 600, lineHeight: 1.4,
        animation: "ccSay .22s ease",
        "@keyframes ccSay": { from: { opacity: 0, transform: "translate(10px, -50%)" }, to: { opacity: 1, transform: "translate(0, -50%)" } },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}>
        {nowShowing}
      </Box>
    )}

    {/* ── THE ORIGINAL UPLOAD ─────────────────────────────────────────────────
        The ET only ever shows clips. Sometimes you just want to watch the thing you
        uploaded, straight through — same URL the per-clip players seek against. */}
    <Dialog open={rawVideoOpen} onClose={() => setRawVideoOpen(false)} maxWidth="lg" fullWidth
      sx={{ zIndex: 12050 }}
      PaperProps={{ sx: { bgcolor: "#141218", border: "1px solid #3a2b46", borderRadius: "12px" } }}>
      <DialogTitle sx={{ color: "#fff", fontSize: 16, fontWeight: 800, pb: 1 }}>
        The original video
        <Typography component="div" sx={{ fontSize: 12, fontWeight: 400, color: "#8b8397", mt: 0.3 }}>
          Your upload, uncut — nothing filtered, nothing trimmed.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        {rawVideoOpen && (rawVideoSrc || videoFile) ? (
          <video
            src={rawVideoSrc || videoFile}
            controls
            autoPlay
            style={{ width: "100%", maxHeight: "72vh", borderRadius: 8, background: "#000" }}
          />
        ) : (
          <Typography sx={{ color: "#8b8397", py: 4, textAlign: "center" }}>
            No source file is attached to this transcript.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setRawVideoOpen(false)} sx={{ textTransform: "none", color: "#c9b8d6" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* ── FLOATING TOOL RAIL ───────────────────────────────────────────────────
        The toolbar carries ~20 equal-weight sections; a newcomer has no idea what to press
        first. This groups them into bubbles. Every button inside a bubble's box calls
        openTool(name), which fires the toolbar's OWN control — a box is a friendlier way
        to reach the same panel, never a second copy of it. BUBBLES is a plain array, so
        adding another smart button later is one entry. */}
    {viewConfig?.createVideo && !exportModalOpen && openTool && (() => {
      const S = rail.size;
      // Several toolbar panels are MUI ClickAwayListener poppers (Segment by, Text style,
      // AI Filters). Opening one inside the same click that dismissed our box made the
      // listener close it again immediately — so hand off on the next tick instead.
      const fire = (name) => { setOpenBubble(null); setTimeout(() => openTool(name), 0); };
      const act = (fn) => { setOpenBubble(null); fn(); };
      const sendAsk = (text) => {
        const t = (text || "").trim();
        if (!t) return;
        setOpenBubble(null); setAiAsk("");
        // The persona is a prompt prefix, not a separate mode — it just tells Cherry
        // whose head to think with.
        const full = aiPersona ? `${aiPersona} ${t}` : t;
        setTimeout(() => openTool("chatAsk", full), 0);
      };

      const qf = quickFilters || {};
      const listKeys = ["entityTypes", "contentFlags", "keyPhrases", "filterVerbs", "filterNouns", "filterAdjectives"];
      const activeFilters =
        (filterStore.getSearch(currentView) ? 1 : 0) +
        (qf.selectedOnly ? 1 : 0) + (qf.hasQuestion ? 1 : 0) + (qf.containsNumbers ? 1 : 0) +
        (qf.durationSec !== null && qf.durationSec !== undefined ? 1 : 0) +
        (qf.firstLetter ? 1 : 0) +
        listKeys.reduce((n, k) => n + ((qf[k] || []).length), 0);

      // A small motion per bubble, each matching what the tool actually does: the lens
      // tips up, the sliders slide, AI breathes, the gear turns.
      const ANIM = {
        // A magnifier magnifies — so it just zooms. No tilt.
        search: {
          transition: "transform .25s ease",
          ".ccbub:hover &": { transform: "scale(1.3)" },
        },
        // No bounce — it read as a glitch. Just a small settle, like a slider being nudged.
        filters: {
          transition: "transform .2s ease",
          ".ccbub:hover &": { transform: "scale(1.12)" },
        },
        ai: {
          ".ccbub:hover &": { animation: "ccBreathe 1.1s ease-in-out infinite" },
          "@keyframes ccBreathe": {
            "0%,100%": { transform: "scale(1)", opacity: 1 },
            "50%": { transform: "scale(1.14)", opacity: 0.82 },
          },
        },
        // A gear turns steadily rather than jumping to an angle and stopping.
        settings: {
          ".ccbub:hover &": { animation: "ccTurn 4s linear infinite" },
          "@keyframes ccTurn": { to: { transform: "rotate(360deg)" } },
        },
      };

      const BUBBLES = [
        // Search goes straight to the search box — no menu in front of the thing you asked for.
        { key: "search",   icon: "🔍", label: "Search", direct: () => fire("search") },
        { key: "filters",  icon: "🎛️", label: "Filters", badge: activeFilters },
        {
          key: "ai", label: "Ask Cherry",
          icon: (
            <Box component="span" sx={{
              fontSize: rail.embedded ? 13 : rail.compact ? 16 : 18, fontWeight: 900, letterSpacing: "-.03em", lineHeight: 1,
              background: "linear-gradient(120deg,#e9d5ff,#a855f7)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            }}>
              AI
            </Box>
          ),
        },
        { key: "settings", icon: "⚙️", label: "Settings" },
      ];

      const pill = (key, text, opts = {}) => (
        <Button key={key} size="small" onClick={opts.onClick} disabled={opts.disabled} title={opts.title || undefined}
          sx={{
            fontSize: 13, lineHeight: 1.5, px: 1.6, py: 0.7, minWidth: "unset",
            ...(opts.wide ? { width: "100%", justifyContent: "flex-start" } : null),
            textTransform: "none", fontWeight: 600, borderRadius: "4px",
            border: "1px solid", whiteSpace: "nowrap",
            borderColor: opts.active ? (opts.color || "#e0466b") : `${opts.color || "#8b8592"}66`,
            color: opts.active ? "#fff" : (opts.color || "#c9b8d6"),
            bgcolor: opts.active ? (opts.color || "#e0466b") : "transparent",
            "&.Mui-disabled": { color: "#5a5464", borderColor: "#3a3542" },
            "&:hover": { borderColor: opts.color || "#e0466b", bgcolor: opts.active ? (opts.color || "#e0466b") : "#ffffff14" },
          }}>
          {text}
        </Button>
      );

      const group = (label, children, desc) => (
        <Box key={label} sx={{ mb: 1.7 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#d6cee0", mb: desc ? 0.15 : 0.7, letterSpacing: ".01em" }}>
            {label}
          </Typography>
          {desc && (
            <Typography sx={{ fontSize: 11, color: "#8b8397", mb: 0.7, lineHeight: 1.35 }}>{desc}</Typography>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>{children}</Box>
        </Box>
      );

      // A heavier rule between the phases of the box, so it reads as sections not a list.
      const band = (label) => (
        <Box key={`band-${label}`} sx={{ mt: 2.2, mb: 1.2, display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase", color: "#e0466b", whiteSpace: "nowrap" }}>
            {label}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", bgcolor: "#3a3542" }} />
        </Box>
      );

      const sentiments = filterStore.getSentiments(currentView) || {};
      const speakerMap = filterStore.getSpeakers(currentView) || {};
      // Match the toolbar's own sentiment colours rather than tinting them all cherry.
      const SENT = { positive: "#4db6ac", neutral: "#9aa0a6", negative: "#ef5350" };
      const sentColor = (k) => SENT[String(k).toLowerCase()] || "#9aa0a6";

      // The specialty views live in the AI bubble — they are all AI-derived read-outs.
      const AI_VIEWS = [
        ["Create a Video", "📝 Transcript"], ["Entities", "Entities"],
        ["Auto Highlight", "Highlights"], ["AIB", "Categories"],
        ["Content Moderation", "Content flags"],
      ];

      const SORTS = [
        ["chronological", "🕐 Chrono", "Order they were said"],
        ["alphabetical", "🔤 A–Z", "By first word"],
        ["duration", "⏱ Duration", "Shortest first"],
        ["virality", "📈 Viral", "Best AI score first"],
      ];

      const BOX = {
        // Changes still being judged. Their own box, because mixing seven switches into
        // Settings made that panel unreadable (Isaac, 2026-09-24).
        labs: { title: "What’s new — try these", groups: [
          band("What's new \u2014 try these"),
          <Typography key="labs-why" sx={{ fontSize: 11.5, color: "#9d94a8", lineHeight: 1.45, mb: 1.2 }}>
            Each one changes the editor on its own. Turn any of them off and it goes straight
            back to how it was.
          </Typography>,
          ...LABS.map((l) => (
            <Box key={l.key} sx={{ mb: 1.2 }}>
              {group("", [
                pill(l.key, `${labs[l.key] ? "\u2713 " : ""}${l.name}`, {
                  color: "#E61D50", wide: true, active: !!labs[l.key],
                  onClick: () => setLab(l.key, !labs[l.key]),
                }),
              ], l.hint)}
            </Box>
          )),
          group("", [
            pill("labs-reset", "\u21ba Put everything back", {
              color: "#9aa0a6", wide: true, onClick: () => resetLabs(),
            }),
          ], "Turns all of the above off at once."),
        ]},

        filters: { title: "Filters", groups: [
          band("Narrow the list"),
          group("Who's talking", Object.keys(speakerMap).map((k) =>
            pill(k, speakerNames?.[k] || k, { color: "#29b6f6", active: !!speakerMap[k], onClick: () => onSpeakerChange({ target: { value: k } }) })),
            "Turn a speaker off to hide everything they said."),
          group("Mood of the line", Object.keys(sentiments).map((k) =>
            pill(k, k, { color: sentColor(k), active: !!sentiments[k], onClick: () => onSentimentChange({ target: { value: k } }) })),
            "AI tagged every clip positive, neutral or negative."),
          group("Only clips that contain", [
            pill("q", "❓ A question", { color: "#a855f7", active: !!qf.hasQuestion, onClick: () => onQuickFilterChange("hasQuestion") }),
            pill("n", "# A number", { color: "#22c55e", active: !!qf.containsNumbers, onClick: () => onQuickFilterChange("containsNumbers") }),
          ], "Handy for pulling stats or Q&A moments."),
          group("How long the clip is", [
            pill("t", "🕐 Clip Time", { color: "#f59e0b", onClick: () => fire("clipTime") }),
            pill("l", "# Characters", { color: "#4db6ac", onClick: () => fire("length") }),
          ], "Opens a slider — drag to keep only clips in that range."),
          group("Gaps between clips", ["show", "hide", "only"].map((m) =>
            pill(m, m[0].toUpperCase() + m.slice(1), { color: "#29b6f6", active: spacesMode === m, onClick: () => onSpacesModeChange(m) })),
            "Silences can be shown as their own rows, hidden, or listed alone."),
          group("What the AI found", [
            pill("af", "✨ Entities · phrases · flags", { color: "#ef5350", active: activeFilters > 0, wide: true, onClick: () => fire("aiFilters") }),
          ], "Filter by names, places, key phrases and content flags."),

          band("Put it in order"),
          group("Sort the list", SORTS.map(([k, label, tip]) => {
            const on = sortOrder?.field === k;
            const arrow = on ? (sortOrder?.direction === "asc" ? " ↑" : " ↓") : "";
            const dead = k === "virality" && !viralityDone;
            return pill(k, label + arrow, {
              color: "#f59e0b", active: on, disabled: dead,
              title: dead ? "Run Score virality in Ask Cherry first" : tip,
              onClick: () => onSortOrderChange(k),
            });
          }), "Click the one you're already on to flip the direction."),

          band("Your picks"),
          group("The clips you ticked", [
            pill("sel", "✓ Show only my picks", { color: "#e0466b", active: !!qf.selectedOnly, onClick: () => onQuickFilterChange("selectedOnly") }),
            pill("pin", "📌 Pin picked to top", { color: "#29b6f6", onClick: () => onPinSelectedToTop() }),
          ], "Double-click anywhere in a row to pick it."),

          band("Playback"),
          group("What happens when a clip ends", [
            pill("nx", "▶ Roll on to the next clip", { color: "#4db6ac", active: !!playNext, wide: true, onClick: () => setPlayNext((v) => !v) }),
          ]),
          group("Highlighted words", [
            pill("ho", "🖍 Play only the highlights", { color: "#f59e0b", active: !!playHighlightsOnly, wide: true, onClick: () => setPlayHighlightsOnly((v) => !v) }),
          ], "Use Highlight words in Settings to mark them first."),

          band("Start over"),
          group("Clear everything", [
            pill("r", "⟲ Reset all filters", { color: "#ef5350", wide: true, onClick: () => fire("clearFilters") }),
          ], "Filters, search and sort — your picks are kept."),
        ]},

        ai: { title: "Ask Cherry AI", chat: true, groups: [
          // This bubble is a chat window first. Everything else is folded away below so the
          // thing you are meant to do — type what you want — is the whole surface.
          // Two ways to build a cut, stated up front — the same choice the home page makes.
          <Box key="ways" sx={{ display: "flex", gap: "6px", mb: 1.4 }}>
            <Box sx={{
              flex: 1, px: 1.4, py: 1.1, borderRadius: "10px",
              display: "flex", alignItems: "center", gap: "10px",
              border: "1px solid #453d52", color: "#9a93a3", cursor: "pointer",
              transition: "border-color .15s, color .15s",
              "&:hover": { borderColor: "#e0466b", color: "#f2eef6" },
            }} onClick={() => setOpenBubble(null)}>
              <Box component="img" src={ccLogoRed} alt="" sx={{ height: 30, width: "auto", flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, textAlign: "left" }}>
                <Box sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.2 }}>You select</Box>
                <Box sx={{ fontSize: 11, mt: 0.2, opacity: 0.85 }}>Tick clips yourself</Box>
              </Box>
            </Box>
            <Box sx={{
              flex: 1, px: 1.4, py: 1.1, borderRadius: "10px",
              display: "flex", alignItems: "center", gap: "10px",
              border: "1px solid transparent", color: "#fff",
              background: "linear-gradient(120deg,#7c3aed,#5a2bd0)",
              boxShadow: "0 6px 20px -8px rgba(124,58,237,0.9)",
            }}>
              <Box component="img" src={ccLogoPurple} alt="" sx={{ height: 30, width: "auto", flexShrink: 0, filter: "brightness(0) invert(1)" }} />
              <Box sx={{ minWidth: 0, textAlign: "left" }}>
                <Box sx={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.2 }}>AI selects</Box>
                <Box sx={{ fontSize: 11, mt: 0.2, opacity: 0.9 }}>Describe it below</Box>
              </Box>
            </Box>
          </Box>,

          <Box key="ask" sx={{ mb: 1.4 }}>
            <Box component="textarea" rows={5} value={aiAsk} autoFocus
              onChange={(e) => setAiAsk(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && aiAsk.trim()) { e.preventDefault(); sendAsk(aiAsk); }
              }}
              placeholder={askHint ? `${askHint}▌` : "Tell Cherry what to make…"}
              sx={{
                width: "100%", resize: "vertical", fontFamily: "inherit", fontSize: 14, lineHeight: 1.6,
                p: 1.6, borderRadius: "10px", bgcolor: "#1a1720", color: "#f2eef6",
                border: "1px solid #453d52", outline: "none",
                "&:focus": { borderColor: "#8b5cf6", boxShadow: "0 0 0 3px rgba(139,92,246,0.18)" },
                "&::placeholder": { color: "#6a6472" },
              }} />
            <Box sx={{ display: "flex", gap: "6px", mt: 1, alignItems: "center" }}>
              <Button size="small" disabled={!aiAsk.trim()} onClick={() => sendAsk(aiAsk)}
                sx={{
                  flex: 1, textTransform: "none", fontWeight: 800, fontSize: 13.5, borderRadius: "8px", py: 0.9,
                  color: "#fff", background: "linear-gradient(100deg,#a855f7,#7c3aed)",
                  "&:hover": { filter: "brightness(1.1)" },
                  "&.Mui-disabled": { color: "#6b6475", background: "#241c2b" },
                }}>
                <Box component="img" src={ccLogoPurple} alt="" sx={{ height: 24, width: "auto", mr: 1, flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                Ask Cherry
              </Button>
              {pill("open", "Open chat", { title: "Open the full Black Cherry window without sending anything", onClick: () => fire("chatAsk") })}
            </Box>
            <Typography sx={{ fontSize: 10.5, color: "#6a6472", mt: 0.7, mb: 1.2 }}>
              Enter to send · Shift+Enter for a new line
            </Typography>
            {group("Or start from one of these", (AI_GOAL_CHIPS || []).slice(0, 6).map((c) =>
              pill(c.t, `${c.e} ${c.t}`, { color: "#a855f7", title: c.p, onClick: () => sendAsk(c.p) })))}
          </Box>,

          // 🔗 AI link: let ChatGPT / Claude (through the CherryClipz connector) drive this editor
          // live. Stays open on click so the status is visible; toggleAiLink owns the relay.
          aiLinkAvailable && (
            <Box key="ailink" sx={{ mt: 0.5, pt: 1.2, borderTop: "1px solid #3a3542" }}>
              {group("Let ChatGPT or Claude drive this editor", [
                pill("al",
                  !aiLink.on ? "🔗 AI link: off"
                    : aiLink.status === "arming" ? "🔗 Linking…"
                    : aiLink.status === "disconnected" ? "🔗 Link lost — click to retry"
                    : aiLink.status === "live" ? `🔗 Linked · ${aiLink.count} clips from AI`
                    : "🔗 Linked · waiting for picks",
                  { color: aiLink.status === "disconnected" ? "#e0466b" : "#8b5cf6", active: !!aiLink.on, wide: true,
                    onClick: () => toggleAiLink() }),
              ], "Add the CherryClipz connector in ChatGPT or Claude, turn this on, and every cut the assistant makes ticks and orders itself here — no copy-paste.")}
            </Box>
          ),

          <Box key="more" sx={{ mt: 0.5, pt: 1.2, borderTop: "1px solid #3a3542" }}>
            <Box component="button" onClick={() => setShowAiMore((v) => !v)}
              sx={{
                width: "100%", textAlign: "left", cursor: "pointer", p: 0, font: "inherit",
                bgcolor: "transparent", border: "none", color: "#7d7488",
                fontSize: 9.5, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase",
                "&:hover": { color: "#c9b8d6" },
              }}>
              {showAiMore ? "▾" : "▸"} Other AI tools
            </Box>
            {showAiMore && (
              <Box sx={{ mt: 1.2 }}>
                {group("How deep should Cherry cut?", [
                  pill("cr", "🍒 Keep whole clips", { color: "#8b5cf6", onClick: () => fire("chatRough") }),
                  pill("cp", "✂ Trim inside clips", { color: "#a855f7", onClick: () => fire("chatPrecise") }),
                ])}
                {group("Bring an answer back from elsewhere", [
                  pill("pp", "📋 Paste AI reply code", { color: "#22c55e", wide: true, onClick: () => fire("pasteAiPicks") }),
                ])}
                {group("One-click passes", [
                  pill("f", "✨ Fade filler", { color: "#f59e0b", active: !!fillerDone, onClick: () => fire("filler") }),
                  pill("v", "📈 Score virality", { color: "#ec4899", active: !!viralityDone, onClick: () => fire("virality") }),
                ])}
                {group("Switch view", AI_VIEWS.map(([v, label]) =>
                  pill(v, label, { color: "#4db6ac", active: currentView === v, onClick: () => act(() => onViewChange(v)) })))}
                {group("What the AI has cost you", [
                  pill("us", "💳 AI usage report", { color: "#9aa0a6", onClick: () => fire("aiUsage") }),
                ])}
              </Box>
            )}
          </Box>,
        ]},

        settings: { title: "Settings", groups: [
          // The switches live in their own panel: seven of them inline made Settings
          // unreadable (Isaac, 2026-09-24). This is the door to them, not the room.
          band("What’s new"),
          group("", [
            pill("open-labs", `✨ Try the new stuff${labsOnCount() ? ` · ${labsOnCount()} on` : ""}`, {
              color: "#E61D50", wide: true, active: labsOnCount() > 0,
              onClick: () => setOpenBubble("labs"),
            }),
          ], "Opens its own panel. Everything in there is off until you turn it on."),

          // LAYOUT — must be reachable from BOTH layouts. These pills used to live inside the
          // isXSmallScreen block, so once you were in Full the only way back to Phone was the
          // floating toggle; if that was hidden too (or you were on a remote-driver share link,
          // where the bubbles cover the corner it sits in) there was NO way back at all.
          group("Layout", [
            pill("vm-phone", "\ud83d\udcf1 Phone", { color: "#e0466b", active: etViewMode === "phone", onClick: () => setEtViewMode("phone") }),
            pill("vm-full", "\ud83d\udda5 Full", { color: "#4db6ac", active: etViewMode === "full", onClick: () => setEtViewMode("full") }),
            pill("vm-auto", "Auto", { active: etViewMode === "auto", onClick: () => setEtViewMode("auto") }),
          ], "Phone folds the columns away so the transcript stays readable. Full shows the whole spreadsheet \u2014 you scroll sideways, but nothing is hidden. Auto picks by device."),
          group("The floating Phone/Full control", [
            pill("vt-off", "Hidden", { active: (viewToggleShown || "off") !== "on", onClick: () => setViewToggleShown("off") }),
            pill("vt-on", "Show it", { active: viewToggleShown === "on", onClick: () => setViewToggleShown("on") }),
          ], "The small control at the bottom-right. On a phone it has nowhere to sit without covering something, and everything it does is on this panel anyway."),

          // Phone only: the compact layout's row presets.
          ...(isXSmallScreen ? [
            <Box key="phoneview" sx={{
              mb: 1.8, px: 1.3, py: 1, borderRadius: "8px",
              bgcolor: "rgba(41,182,246,0.10)", border: "1px solid rgba(41,182,246,0.4)",
            }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#7fd3ff", mb: 0.3 }}>
                \ud83d\udcf1 You're on the phone view
              </Typography>
              <Typography sx={{ fontSize: 11, color: "#9fb6c4", lineHeight: 1.4 }}>
                Columns are folded away so the transcript stays readable. Currently <b>{etMode?.name}</b>.
              </Typography>
            </Box>,
            group("Row style", ET_MODES.map((m, i) =>
              pill(m.key, m.name, { color: "#29b6f6", active: etModeIdx === i, title: m.hint, onClick: () => setEtModeIndex(i) })),
              etMode?.hint),
          ] : []),

          group("Break clips up differently", [pill("sg", "⚙ Sentences · words · seconds", { color: "#f59e0b", wide: true, onClick: () => fire("segment") })],
            "One row per sentence by default. Switch to single words, or fixed chunks like every 10 seconds."),
          group("Size of things", [
            pill("cs", "⛶ Video size", { color: "#29b6f6", onClick: () => fire("clipSize") }),
            pill("ts", "🔠 Text size", { color: "#8b5cf6", onClick: () => fire("textStyle") }),
          ], "How big the thumbnails and transcript text are."),
          group("Zoom the whole sheet", [
            pill("z-", "−", { color: "#29b6f6", disabled: etZoom <= 70, onClick: () => setEtZoom((z) => Math.max(70, z - 10)) }),
            pill("z0", `${etZoom}%`, { color: "#29b6f6", active: etZoom !== 100, onClick: () => setEtZoom(100) }),
            pill("z+", "+", { color: "#29b6f6", disabled: etZoom >= 160, onClick: () => setEtZoom((z) => Math.min(160, z + 10)) }),
          ], "Everything at once, like pinching the trackpad. Click the percentage to go back to 100%."),
          group("Which columns show", [
            pill("co", "🗂 Next column set", { color: "#4db6ac", wide: true, onClick: () => act(() => openTool("columns")) }),
          ], "Cycles the last three columns — verbs and nouns, entities, or media details."),
          group("Marking up clips", [
            pill("pen", "🖍 Highlight words", { color: "#f59e0b", active: !!penMode, onClick: () => act(() => setPenMode((v) => !v)) }),
            pill("ed", "✏ Fix the transcript", { color: "#8b5cf6", active: !!editMode, onClick: () => act(() => setEditMode((v) => !v)) }),
          ], "Highlight: drag across words to mark them. Fix: retype a line the AI misheard."),
          group("Undo a timing change", [
            pill("u", "↶ Undo", { disabled: !canUndo, onClick: () => act(onUndo) }),
            pill("rd", "↷ Redo", { disabled: !canRedo, onClick: () => act(onRedo) }),
          ], "Steps back through the +/− nudges you made to clip start and end times. Ctrl+Z works too."),
          group("Translate the transcript", [
            pill("tl", `🌐 Language${lastTranslationLang ? ` · ${lastTranslationLang}` : ""}`, { color: "#29b6f6", onClick: () => fire("translate") }),
            pill("ta", translating ? "Translating…" : "Translate all", { color: "#4db6ac", disabled: !!translating, onClick: () => fire("translateAll") }),
            ...(canToggleTranslation ? [pill("tt", "Show original / translated", { color: "#8b5cf6", onClick: () => act(toggleTranslation) })] : []),
          ], "Pick a language, translate every clip, then flip between the two."),
          group("Help", [pill("h", "📖 How it works", { color: "#29b6f6", wide: true, onClick: () => fire("help") })]),
          group(archive ? "The original videos" : "The original video", archive
            ? archive.sources.map((src) => pill(`raw-${src.transcriptId}`, `▶ ${src.short} · ${src.label}`, { color: src.color, wide: true, disabled: !src.video, onClick: () => act(() => { setRawVideoSrc(src.video); setRawVideoOpen(true); }) }))
            : [pill("raw", "▶ Watch the whole upload", { color: "#29b6f6", wide: true, disabled: !videoFile, onClick: () => act(() => { setRawVideoSrc(""); setRawVideoOpen(true); }) })],
          archive ? "Each upload in this archive, uncut." : "The untouched file you uploaded, before it was cut into clips."),
          group("Export", [pill("e", "🎬 Export settings", { color: "#a855f7", wide: true, onClick: () => fire("exportModal") })],
            "Format, orientation and burned-in captions for the clips you picked."),
        ]},
      };

      const box = openBubble ? BOX[openBubble] : null;

      // Hover name: left of the bubble on the rail, above it when the row sits by Play.
      const capSx = rail.byPlay ? {
        position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", mb: "8px",
      } : {
        position: "absolute", right: "100%", top: "50%", transform: "translateY(-50%)", mr: "10px",
      };

      const resetBtn = (
          <Box sx={{ position: "relative", "&:hover .ccbub-cap": { opacity: 1, visibility: "visible" } }}>
          <Box className="ccbub-cap" sx={{
            ...capSx, zIndex: 1, pointerEvents: "none",
            opacity: 0, visibility: "hidden", transition: "opacity .15s ease",
            px: "9px", py: "4px", borderRadius: "6px", whiteSpace: "nowrap",
            bgcolor: "#0f0d14", border: "1px solid #5c2233", color: "#ff9fb4",
            fontSize: 11.5, fontWeight: 700, boxShadow: "0 6px 18px rgba(0,0,0,0.6)",
          }}>
            Reset filters
          </Box>
          <Box
            component="button"
            aria-label="Reset filters"
            onClick={() => { setOpenBubble(null); openTool("clearFilters"); }}
            title="Clear every filter, search and sort"
            className="ccbub"
            sx={{
              // Same shell as the four above — only the accent says "this one undoes things".
              width: S, height: S, borderRadius: "999px", cursor: "pointer", p: 0, font: "inherit",
              display: "grid", placeItems: "center", fontSize: rail.embedded ? 17 : rail.compact ? 22 : 26, fontWeight: 700,
              color: "#e0466b", alignSelf: "center",
              bgcolor: "#1a1420", border: "1px solid #5c2233",
              boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
              transition: "transform .16s ease, border-color .13s, color .13s",
              "&:hover": { transform: "scale(1.08)", borderColor: "#e0466b", color: "#ff9fb4" },
              "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
            }}>
            <Box component="span" sx={{
              display: "grid", placeItems: "center", lineHeight: 1,
              transition: "transform .45s cubic-bezier(.4,0,.2,1)",
              ".ccbub:hover &": { transform: "rotate(-320deg)" },
              "@media (prefers-reduced-motion: reduce)": { transform: "none !important" },
            }}>
              ↺
            </Box>
          </Box>
          </Box>
      );

      const bubbleBtn = (b) => (
        <Box key={b.key} sx={{
          position: "relative",
          "&:hover .ccbub-cap": { opacity: 1, visibility: "visible" },
        }}>
          <Box className="ccbub-cap" sx={{
            ...capSx, zIndex: 1, pointerEvents: "none",
            opacity: 0, visibility: "hidden", transition: "opacity .15s ease",
            px: "9px", py: "4px", borderRadius: "6px", whiteSpace: "nowrap",
            bgcolor: "#0f0d14", border: "1px solid #453d52", color: "#f2eef6",
            fontSize: 11.5, fontWeight: 700, boxShadow: "0 6px 18px rgba(0,0,0,0.6)",
          }}>
            {b.label}
          </Box>
          <Box
            component="button"
            className="ccbub"
            aria-label={b.label}
            aria-expanded={openBubble === b.key}
            onClick={() => {
              if (rail.parked) setRailOut(true);
              // The Search modal lives in the toolbar and does not know about the rail, so
              // switching bubbles left it hanging open behind the new box.
              if (closeSearchModalRef) closeSearchModalRef();
              if (b.direct) { setOpenBubble(null); b.direct(); return; }
              setOpenBubble((cur) => (cur === b.key ? null : b.key));
            }}
            sx={{
              width: S, height: S, borderRadius: "999px", cursor: "pointer", p: 0, font: "inherit",
              display: "grid", placeItems: "center", fontSize: rail.compact ? 20 : 24,
              bgcolor: openBubble === b.key ? "#2a1c28" : "#1a1420",
              border: "1px solid", borderColor: openBubble === b.key ? "#e0466b" : "#3a2b46",
              boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
              transition: "transform .16s ease, border-color .13s, background-color .13s",
              "&:hover": { transform: "scale(1.08)", borderColor: "#e0466b" },
              "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
            }}>
            <Box component="span" sx={{
              display: "grid", placeItems: "center", lineHeight: 1,
              ...(ANIM[b.key] || null),
              "@media (prefers-reduced-motion: reduce)": { animation: "none !important", transform: "none !important" },
            }}>
              {b.icon}
            </Box>
          </Box>
          {!!b.badge && (
            <Box sx={{
              position: "absolute", top: -3, right: -3, minWidth: 18, height: 18, px: "4px",
              borderRadius: "999px", bgcolor: "#ef5350", color: "#fff", border: "2px solid #1b1b1d",
              fontSize: 10, fontWeight: 800, display: "grid", placeItems: "center",
            }}>
              {b.badge}
            </Box>
          )}
        </Box>
      );

      return (
        <>
          {box && <Box onClick={() => setOpenBubble(null)} sx={{ position: "fixed", inset: 0, zIndex: 10040 }} />}

          {/* edge tab — only when the rail parks on the right */}
          {!rail.byPlay && rail.show === "tab" && (
            <Box
              component="button"
              onClick={() => { setRailOut((v) => !v); setOpenBubble(null); }}
              aria-label={railOut ? "Hide tools" : "Show tools"}
              sx={{
                position: "fixed", right: 0, top: "50%", transform: "translateY(-50%)",
                zIndex: 10041, cursor: "pointer", p: 0, font: "inherit",
                width: rail.hint === "label" ? 28 : 22, height: rail.hint === "label" ? 96 : 78,
                borderRadius: "8px 0 0 8px", bgcolor: "#1a1420", borderRight: "none", border: "1px solid",
                color: rail.hint === "peek" && !railOut ? "#e0466b" : "#8b8592",
                borderColor: rail.hint === "peek" && !railOut ? "#e0466b" : "#3a2b46",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px",
                animation: rail.hint === "peek" && !railOut ? "ccRailNudge 2.4s ease-in-out infinite" : "none",
                "@keyframes ccRailNudge": {
                  "0%,72%,100%": { boxShadow: "0 0 0 0 rgba(224,70,107,0)" },
                  "82%": { boxShadow: "0 0 0 7px rgba(224,70,107,0.22)" },
                },
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}>
              <Box component="span" sx={{ fontSize: 12 }}>{railOut ? "›" : "‹"}</Box>
              {rail.hint === "label" && (
                <Box component="span" sx={{ writingMode: "vertical-rl", fontSize: 9, fontWeight: 800, letterSpacing: ".12em" }}>
                  TOOLS
                </Box>
              )}
            </Box>
          )}

          {/* Free mode: the cluster lives wherever you dragged it. Every fixed position is in
              the way of something — the right rail covers columns, above-Play covers the bottom.
              Parking it in the dead space beside the transcript is the only arrangement that can
              get out of the way of whatever YOU happen to be reading. */}
          {rail.free && (() => {
            const R = rail.compact ? 62 : 76;
            // Your ordering, clockwise from the top: AI up top, Filters upper-right (it was
            // already in a good spot), Settings the right corner, Reset the left corner,
            // Search on the left.
            const ORDER = ["ai", "filters", "settings", "reset", "search"];
            const byKey = {};
            BUBBLES.forEach((b2) => { byKey[b2.key] = { node: bubbleBtn(b2), label: b2.label }; });
            byKey.reset = { node: resetBtn, label: "Reset" };
            const petals = ORDER.map((k) => ({ key: k, ...byKey[k] })).filter((x) => x.node);

            const at = (i) => {
              const n = petals.length;
              if (rail.shape === "row") return { x: (i - (n - 1) / 2) * (S + 14), y: 0 };
              if (rail.shape === "arc") {
                const ang = (-165 + i * (150 / (n - 1))) * Math.PI / 180;
                return { x: Math.cos(ang) * R, y: Math.sin(ang) * R };
              }
              const ang = (-90 + i * (360 / n)) * Math.PI / 180;   // flower
              return { x: Math.cos(ang) * R, y: Math.sin(ang) * R };
            };
            const startDrag = (e) => {
              e.preventDefault();
              const move = (ev) => setRailXY({
                x: Math.min(0.95, Math.max(0.05, ev.clientX / window.innerWidth)),
                y: Math.min(0.90, Math.max(0.12, ev.clientY / window.innerHeight)),
              });
              const up = () => {
                window.removeEventListener("pointermove", move);
                window.removeEventListener("pointerup", up);
              };
              window.addEventListener("pointermove", move);
              window.addEventListener("pointerup", up);
            };
            return (
              <Box sx={{
                position: "fixed",
                left: `${(railXY ? railXY.x : (rail.compact ? 0.5 : 0.72)) * 100}vw`,
                top: `${(railXY ? railXY.y : (rail.compact ? 0.42 : 0.55)) * 100}vh`,
                width: 0, height: 0, zIndex: 11050,
              }}>
                {petals.map((p2, i) => {
                  const { x, y } = at(i);
                  return (
                    <Box key={p2.key} sx={{
                      position: "absolute", left: 0, top: 0,
                      transform: `translate(calc(${x}px - 50%), calc(${y}px - 50%))`,
                      transition: "transform .3s cubic-bezier(.34,1.4,.64,1)",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: "3px",
                      "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                    }}>
                      {p2.node}
                      {/* Named without hovering — a ring of unlabelled icons is a guessing game. */}
                      <Box sx={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: ".05em", color: "#c9b8d6",
                        whiteSpace: "nowrap", pointerEvents: "none", textShadow: "0 1px 4px #000",
                      }}>
                        {p2.label}
                      </Box>
                    </Box>
                  );
                })}
                <Box
                  component="button"
                  onPointerDown={startDrag}
                  aria-label="Drag to move the tools"
                  title="Drag me anywhere"
                  sx={{
                    position: "absolute", left: 0, top: 0, transform: "translate(-50%, -50%)",
                    width: S - 2, height: S - 2, borderRadius: "999px", cursor: "grab",
                    display: "grid", placeItems: "center",
                    p: 0, font: "inherit", touchAction: "none", zIndex: 3,
                    bgcolor: "#1a1420", border: "2px solid #e0466b",
                    boxShadow: "0 8px 26px rgba(224,70,107,0.45)",
                    "&:active": { cursor: "grabbing" },
                  }}>
                  <Box component="img" src={ccLogoRed} alt="" sx={{ height: S * 0.52, width: "auto", pointerEvents: "none" }} />
                </Box>
              </Box>
            );
          })()}

          {/* the bubbles — a column on the right edge, or a row above Play selected */}
          {!rail.free && <Box className="cc-rail" sx={rail.byPlay ? {
            position: "fixed", left: "50%", bottom: rail.compact ? 128 : 152, zIndex: 11050,
            display: "flex", flexDirection: "row", gap: `${rail.compact ? 10 : 14}px`,
            transform: "translateX(-50%)",
          } : {
            position: "fixed", right: rail.right, top: "50%", zIndex: 11050,
            display: "flex", flexDirection: "column", alignItems: "center", gap: `${rail.embedded ? 7 : rail.compact ? 10 : 14}px`,
            transform: `translateY(-50%) translateX(${rail.parked ? (rail.hint === "peek" ? S * 0.62 : S + 34) : 0}px)`,
            transition: "transform .26s ease",
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}>
            {BUBBLES.map(bubbleBtn)}
            {resetBtn}
          </Box>}

          {/* the open bubble's box */}
          {box && (
            <Box sx={{
              position: "fixed", zIndex: 10043, overflowY: "auto",
              // min() keeps it inside a narrow screen without needing a phone to test on.
              p: 2.25, borderRadius: "12px", bgcolor: "#26232c", border: "1px solid #453d52",
              boxShadow: "0 18px 52px rgba(0,0,0,0.7)",
              // Never let the panel depend on where the bubbles happen to be. It used to be
              // anchored off rail.right, which is not even where the cluster sits in drag
              // mode — so moving the bubbles pushed the box off the screen edge.
              ...(rail.compact ? {
                // Phone: a bottom sheet pinned to the frame. Cannot spill, cannot be pushed
                // off by a dragged cluster, and it sits clear of the view controls.
                left: 8, right: 8, bottom: 8, top: "auto", transform: "none",
                width: "auto", maxHeight: "62vh", borderRadius: "14px",
              } : rail.byPlay ? {
                // Desktop: sit inboard of the timecode column. `right: 20` parked the panel
                // straight on top of every row's in/out and download controls.
                right: 200, left: "auto",
                bottom: (rail.embedded ? 96 : 152) + rail.size + 18,
                top: "auto", transform: "none", maxHeight: "68vh",
                width: rail.embedded ? "min(88vw, 300px)" : "min(94vw, 560px)",
                maxWidth: "calc(100vw - 32px)",
              } : {
                // Right rail or dragged cluster: clear of the timecode column, clamped so a
                // long panel can never run past the window.
                right: 200, left: "auto", top: "50%", transform: "translateY(-50%)",
                maxHeight: "86vh", width: "min(94vw, 560px)", maxWidth: "calc(100vw - 32px)",
              }),
              // Dragged panels win over every docked position above, so you can shove the box
              // aside and read what it was covering.
              ...(boxXY && !rail.compact ? {
                left: boxXY.x, top: boxXY.y, right: "auto", bottom: "auto", transform: "none",
              } : null),
              ...(boxSize && !rail.compact ? {
                width: boxSize.w, maxWidth: "calc(100vw - 32px)",
                height: boxSize.h, maxHeight: "calc(100vh - 32px)",
              } : null),
            }}>
              <Box
                onPointerDown={(e) => {
                  if (rail.compact) return;               // phone: it's a bottom sheet, leave it
                  const el = e.currentTarget.parentElement;
                  const r = el.getBoundingClientRect();
                  boxDrag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, w: r.width, h: r.height };
                  e.currentTarget.setPointerCapture(e.pointerId);
                }}
                onPointerMove={(e) => {
                  const d = boxDrag.current;
                  if (!d) return;
                  // Clamp so the panel can never be dragged off-screen and stranded.
                  const x = Math.min(Math.max(8, e.clientX - d.dx), window.innerWidth - d.w - 8);
                  const y = Math.min(Math.max(8, e.clientY - d.dy), window.innerHeight - 60);
                  setBoxXY({ x, y });
                }}
                onPointerUp={() => { boxDrag.current = null; }}
                sx={{
                  display: "flex", alignItems: "center", gap: 1, mb: 1.6,
                  cursor: rail.compact ? "default" : "grab",
                  "&:active": { cursor: rail.compact ? "default" : "grabbing" },
                  touchAction: "none", userSelect: "none",
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#fff", flex: 1 }}>
                  {box.title}
                </Typography>
                {!rail.compact && (
                  <>
                    <Typography sx={{ fontSize: 15, color: "#6b6475", lineHeight: 1 }} title="Drag me anywhere">⠿</Typography>
                    {(boxXY || boxSize) && (
                      <Button
                        onClick={(e) => { e.stopPropagation(); setBoxXY(null); setBoxSize(null); }}
                        sx={{ minWidth: "unset", p: 0.3, fontSize: 10.5, color: "#8b8592", textTransform: "none", "&:hover": { color: "#fff" } }}
                      >
                        snap back
                      </Button>
                    )}
                  </>
                )}
              </Box>
              {openBubble === "filters" && (
                <Box sx={{
                  mb: 1.6, px: 1.2, py: 0.9, borderRadius: "6px",
                  bgcolor: "rgba(224,70,107,0.12)", border: "1px solid rgba(224,70,107,0.35)",
                  fontSize: 12.5, color: "#c9b8d6",
                }}>
                  <b style={{ color: "#fff" }}>{selectedCount[currentView] || 0} clips</b> selected
                  {" · "}
                  <b style={{ color: "#fff" }}>{userStore.getDuration()}</b> total
                </Box>
              )}
              {box.groups}

              {/* Bubble layout is housekeeping, not a real setting — keep it folded away
                  so Segment by / sizes / columns stay the first thing you see. */}
              {openBubble === "settings" && (
                <Box sx={{ mt: 0.5, pt: 1, borderTop: "1px solid #3a3542" }}>
                  <Box component="button" onClick={() => setShowRailPrefs((v) => !v)}
                    sx={{
                      width: "100%", textAlign: "left", cursor: "pointer", p: 0, font: "inherit",
                      bgcolor: "transparent", border: "none", color: "#6a6472",
                      fontSize: 9, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                      "&:hover": { color: "#c9b8d6" },
                    }}>
                    {showRailPrefs ? "▾" : "▸"} Where these bubbles sit
                  </Box>
                  {showRailPrefs && (
                    <Box sx={{ mt: 1 }}>
                      {group("The toolbar", [
                        pill("tbshow", "Show it", { active: !toolbarHidden, onClick: () => setToolbarHidden(false) }),
                        pill("tbhide", "Hide it", { active: toolbarHidden, onClick: () => act(() => setToolbarHidden(true)) }),
                      ], "Hidden leaves just headers, clips and data. A ▾ SHOW TOOLBAR tab at the top brings it straight back — nothing is lost.")}
                      {group("Speaker names", [
                        pill("spkcol", "On every row", { active: !speakerHeadings, onClick: () => setSpeakerHeadings(false) }),
                        pill("spkhead", "Heading when it changes", { active: speakerHeadings, onClick: () => setSpeakerHeadings(true) }),
                      ], "Headings read like a normal transcript. They switch themselves off while the list is sorted or filtered, because the grouping would no longer be true.")}
                      {group("Shape", [
                        pill("sh-row", "Row", { active: railShape === "row", disabled: railPos !== "free", onClick: () => setRailShape("row") }),
                        pill("sh-flower", "🌸 Flower", { active: railShape === "flower", disabled: railPos !== "free", onClick: () => setRailShape("flower") }),
                        pill("sh-arc", "◡ Arc", { active: railShape === "arc", disabled: railPos !== "free", onClick: () => setRailShape("arc") }),
                      ], railPos === "free" ? "Drag the 🍒 in the middle to park the whole cluster anywhere." : "Pick “Drag anywhere” below to use these.")}
                      {group("Position", [
                        pill("p1", "Right side", { active: railPos === "right", onClick: () => setRailPos("right") }),
                        pill("p2", "By Play button", { active: railPos === "play", onClick: () => setRailPos("play") }),
                        pill("p3", "🍒 Drag anywhere", { active: railPos === "free", onClick: () => setRailPos("free") }),
                      ])}
                      {group("Visibility", [
                        pill("v0", "Auto", { title: "Always on a big screen, tucked away on a small one", active: railShow === "auto", onClick: () => setRailShow("auto") }),
                        pill("v1", "Always", { active: railShow === "always", onClick: () => setRailShow("always") }),
                        pill("v2", "Hide behind tab", { active: railShow === "tab", onClick: () => setRailShow("tab") }),
                      ])}
                      {group("Spacing", [
                        pill("s1", "Float over sheet", { title: "Bubbles sit on top; the sheet keeps its full width", active: railSpace === "float", onClick: () => setRailSpace("float") }),
                        pill("s2", "Push sheet across", { title: "The sheet shifts left to make room", active: railSpace === "push", onClick: () => setRailSpace("push") }),
                      ])}
                      {group("Tab handle", ["auto", "label", "peek", "none"].map((h) =>
                        pill(h, h[0].toUpperCase() + h.slice(1), { active: railHint === h, disabled: railPos === "free", onClick: () => setRailHint(h) })))}
                      {group("Timeline", [
                        pill("tl-off", "Off", { active: !timelineEnabled, onClick: () => setTimelineEnabled(false) }),
                        pill("tl-on", "Show the tab", { active: timelineEnabled, onClick: () => setTimelineEnabled(true) }),
                      ], "Puts a TIMELINE tab at the bottom. Your ticked clips assemble on a real timeline with a preview player, in pick order.")}
                      {group("Changed your mind?", [
                        pill("resetlay", "↺ Put the layout back to normal", { color: "#ef5350", wide: true, onClick: () => act(resetLayout) }),
                      ], "Undoes every switch on this panel — toolbar, shape, position, speakers.")}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </>
      );
    })()}

    </>
  );
};

export default observer(CreateVideo);
